import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentSessionHistoryView } from "./discovery/AgentSessionHistoryView";
import {
  Upload,
  Database,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Info,
  Search,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  X,
  Send,
  User,
  Layers,
  Server,
  Snowflake as SnowflakeIcon,
  Cloud,
  BookOpen,
  GitBranch,
  Eye,
  FileCode2,
  Shield,
  ClipboardCheck,
  Rocket,
  BarChart3,
  Table2,
  Hash,
  Type,
  Calendar,
  Globe,
  Target,
  MessageSquare,
  KeyRound,
  Link,
  Plus,
  Trash2,
  Copy,
  ChevronLeft,
  Clock,
  Wand2,
  RefreshCw,
  Microscope,
  ArrowUpRight,
  PenSquare,
  LayoutGrid,
  Sigma,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Lightbulb,
  GitCompare,
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { MermaidConfig } from "mermaid";
import { renderMermaid } from "../lib/mermaidRender";
import { api } from "../api";
import type {
  DatasetProfile,
  UploadResponse,
  UploadResult,
  SheetInfo,
  SemanticViewResponse,
  TargetModelResponse,
  ChatEntry,
  ColumnProfile,
  DatabaseConnectionParams,
  DeployModelRequest,
  ReconcileDeploymentRequest,
  ReconcileDeploymentResponse,
  ValidationResult,
  ValidationIssue,
  NamingStandardsReport,
  KeySuggestionsResponse,
  ProfileQuestion,
  SemanticRelationship,
  SnapshotSummary,
  ApprovalState,
  MaskingPlanItem,
  DdlValidationResponse,
  CostEstimateResponse,
  CaptureLineageResponse,
  CatalogExportResponse,
  ModelingInsightsResponse,
  ImpactAnalysisResponse,
  SnapshotDiffResponse,
  SemanticLayerExportFormat,
} from "../types";
import type {
  ProjectKnowledgeDocType,
  ProjectKnowledgeDocInfo,
} from "../apiv2";
import { isSheetSelection } from "../types";
import GreenTendrils from "./GreenTendrils";
import { SemanticERCanvas } from "./SemanticERCanvas";
import { openAgentInNewTab } from "@/lib/utils";
import { SAMPLE_PRESETS } from "../features/data-profiler/components/TableSelectStep";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "./data-source/ConnectDatabaseDialog";

// Column metadata / data dictionary type (from user-uploaded data dictionary file)
interface ParsedColumnMeta {
  column_name: string;
  description?: string;
  business_name?: string;
  sensitivity?: string;
  domain?: string;
  notes?: string;
}

interface QAFormState {
  [questionId: string]: string;
}

// This view's mermaid config (dark theme), applied on each render
// (lib/mermaidRender.ts) so another component's config can't replace it.
const SEMANTIC_MERMAID_CONFIG: MermaidConfig = {
  theme: "dark",
  themeVariables: {
    primaryColor: "#86bc25",
    primaryTextColor: "#e0e0e0",
    primaryBorderColor: "#86bc25",
    lineColor: "#555",
    secondaryColor: "#2a2a3e",
    tertiaryColor: "#1c1c2a",
    fontSize: "12px",
  },
  er: { useMaxWidth: true, layoutDirection: "TB" },
};

/** Mermaid diagram renderer component — zoomable (wheel/buttons) and
 *  pannable (drag) so large ER diagrams can be viewed properly. */
function MermaidDiagram({
  chart,
  onSvgReady,
}: {
  chart: string;
  onSvgReady?: (svg: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const MIN_SCALE = 0.1;
  const MAX_SCALE = 4;
  const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const fitScaleRef = useRef(1);
  const draggingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Extract just the mermaid code (strip markdown fences)
        let code = chart;
        const fenceMatch = code.match(/```mermaid\s*([\s\S]*?)```/);
        if (fenceMatch) code = fenceMatch[1].trim();

        const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const { svg: rendered } = await renderMermaid(
          id,
          code,
          SEMANTIC_MERMAID_CONFIG,
        );
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
          onSvgReady?.(rendered);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || "Failed to render diagram");
          setSvg("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chart, onSvgReady]);

  // Auto-fit the diagram to the viewport whenever a new diagram is rendered.
  useLayoutEffect(() => {
    if (!svg || !viewportRef.current) return;
    const svgEl = viewportRef.current.querySelector("svg");
    if (!svgEl) return;
    const svgRect = svgEl.getBoundingClientRect();
    const viewportRect = viewportRef.current.getBoundingClientRect();
    if (svgRect.width > 0 && svgRect.height > 0) {
      const fit = clampScale(
        Math.min(
          viewportRect.width / svgRect.width,
          viewportRect.height / svgRect.height,
          1,
        ),
      );
      fitScaleRef.current = fit;
      setScale(fit);
      setPan({ x: 0, y: 0 });
    }
  }, [svg]);

  // Wheel-to-zoom — attached natively so preventDefault() works (React's
  // synthetic onWheel handler is passive by default).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      setScale((prev) => clampScale(prev * factor));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [svg]);

  const handleMouseDown = (e: React.MouseEvent) => {
    draggingRef.current = true;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };
  const stopDrag = () => {
    draggingRef.current = false;
  };
  const resetView = () => {
    setScale(fitScaleRef.current);
    setPan({ x: 0, y: 0 });
  };

  if (error) {
    return (
      <div className="text-xs text-red-400/60 p-3 bg-red-500/5 rounded-lg border border-red-500/10">
        <p className="mb-1 font-medium">Diagram render error</p>
        <pre className="overflow-x-auto text-[11px] text-red-400/40">
          {error}
        </pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={16} className="animate-spin text-brand-500" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-1.5">
      <div className="flex items-center justify-end gap-1">
        <span className="text-[11px] text-white/30 mr-1">
          Scroll to zoom · drag to pan
        </span>
        <button
          type="button"
          onClick={() => setScale((prev) => clampScale(prev * 0.85))}
          title="Zoom out"
          className="p-1 rounded-md text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
        >
          <ZoomOut size={13} />
        </button>
        <span className="text-[11px] text-white/40 w-10 text-center tabular-nums">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setScale((prev) => clampScale(prev * 1.15))}
          title="Zoom in"
          className="p-1 rounded-md text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
        >
          <ZoomIn size={13} />
        </button>
        <button
          type="button"
          onClick={resetView}
          title="Reset zoom"
          className="p-1 rounded-md text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
        >
          <RotateCcw size={13} />
        </button>
      </div>
      <div
        ref={viewportRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        onDoubleClick={resetView}
        className="relative overflow-hidden bg-surface-300/30 rounded-lg resize-y select-none cursor-grab active:cursor-grabbing"
        style={{
          height: 520,
          minHeight: 240,
        }}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: "0 0",
            width: "fit-content",
            padding: 16,
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Context Persistence — fingerprint by filename + columns
   ═══════════════════════════════════════════════════ */
const CONTEXT_CACHE_KEY = "rgx-dataset-context-cache";

interface SheetKeyDef {
  primaryKey: string;
  alternateKeys: {
    column: string;
    conditionColumn: string;
    conditionValue: string;
  }[];
  foreignKeys: {
    column: string;
    referencesSheet: string;
    referencesColumn: string;
  }[];
}

interface CachedDatasetCtx {
  datasetContext: {
    domain: string;
    domainCustom: string;
    rowMeaning: string;
    purpose: string;
    consumers: string;
    businessRules: string;
    relatedSystems: string;
    specialRequirements: string;
    knowledgeSkillSlug: string;
  };
  sheetContexts?: Record<string, string>;
  sheetKeys?: Record<string, SheetKeyDef>;
  savedAt: string;
}

/** Collapsible section wrapper */
function CollapsibleSection({
  icon,
  title,
  badge,
  defaultOpen = false,
  extra,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  badge?: string;
  defaultOpen?: boolean;
  extra?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        {icon}
        <span className="text-xs font-semibold text-white/80 flex-1">
          {title}
        </span>
        {badge && (
          <span className="text-[11px] text-white/25 bg-surface-200 px-2 py-0.5 rounded">
            {badge}
          </span>
        )}
        {extra && (
          <span className="mr-1" onClick={(e) => e.stopPropagation()}>
            {extra}
          </span>
        )}
        {open ? (
          <ChevronUp size={12} className="text-white/30" />
        ) : (
          <ChevronDown size={12} className="text-white/30" />
        )}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

/** Entity card — collapsible with column table, entity type badge, and grain */
const SENSITIVITY_OPTIONS = [
  "Public",
  "Internal",
  "Confidential",
  "Highly Confidential / PII",
  "PII",
  "PHI",
];

const ENTITY_TYPE_OPTIONS = [
  "Fact",
  "Dimension",
  "Bridge",
  "Snapshot",
  "Aggregate",
];

type ColDraft = {
  business_name: string;
  sensitivity: string;
  description: string;
};

function EntityCard({
  entity,
  defaultOpen,
  autoEdit,
  sessionId,
  allEntities,
  onSaved,
}: {
  entity: any;
  defaultOpen: boolean;
  /** Opens the card directly in edit mode on mount (used by the ER canvas's
   * click-to-edit modal, which has no separate "Edit" button of its own). */
  autoEdit?: boolean;
  sessionId?: string;
  allEntities?: Array<{
    name: string;
    columns?: Array<{ technical_name: string }>;
  }>;
  onSaved?: (wrapper: unknown) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [descDraft, setDescDraft] = useState<string>(entity.description ?? "");
  const [entityTypeDraft, setEntityTypeDraft] = useState<string>(
    entity.entity_type ?? "",
  );
  const [colDraft, setColDraft] = useState<Record<string, ColDraft>>({});
  const [pkDraft, setPkDraft] = useState<Set<string>>(new Set());
  const [fkDraft, setFkDraft] = useState<
    Array<{
      column: string;
      references_table: string;
      references_column: string;
    }>
  >([]);
  const [addingColumn, setAddingColumn] = useState(false);
  const [newCol, setNewCol] = useState({
    technical_name: "",
    business_name: "",
    data_type: "string",
    nullable: true,
    description: "",
  });
  const [colActionBusy, setColActionBusy] = useState<string | null>(null);
  const [colActionError, setColActionError] = useState<string | null>(null);
  const [entityActionBusy, setEntityActionBusy] = useState(false);
  const [entityActionError, setEntityActionError] = useState<string | null>(
    null,
  );
  const cols: any[] = entity.columns ?? [];
  const visible = showAll ? cols : cols.slice(0, 12);
  const fks: any[] = entity.foreign_keys ?? [];

  const startEdit = () => {
    setDescDraft(entity.description ?? "");
    setEntityTypeDraft(entity.entity_type ?? "");
    const cd: Record<string, ColDraft> = {};
    for (const c of cols) {
      cd[c.technical_name] = {
        business_name: c.business_name ?? "",
        sensitivity: c.sensitivity ?? "",
        description: c.description ?? "",
      };
    }
    setColDraft(cd);
    setPkDraft(new Set(entity.primary_keys ?? []));
    setFkDraft(
      fks
        .filter((fk: any) => typeof fk === "object" && fk !== null)
        .map((fk: any) => ({
          column: fk.column || fk.source_column || "",
          references_table:
            fk.references_table ||
            fk.ref_table ||
            fk.references_entity ||
            fk.target_entity ||
            fk.target_table ||
            "",
          references_column:
            fk.references_column || fk.ref_column || fk.target_column || "",
        })),
    );
    setSaveError(null);
    setShowAll(true);
    setEditing(true);
    setOpen(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  useEffect(() => {
    if (autoEdit) startEdit();
  }, []);

  const togglePk = (technicalName: string) => {
    setPkDraft((prev) => {
      const next = new Set(prev);
      if (next.has(technicalName)) next.delete(technicalName);
      else next.add(technicalName);
      return next;
    });
  };

  const addFkRow = () =>
    setFkDraft((prev) => [
      ...prev,
      { column: "", references_table: "", references_column: "" },
    ]);

  const updateFkRow = (
    i: number,
    field: "column" | "references_table" | "references_column",
    value: string,
  ) =>
    setFkDraft((prev) =>
      prev.map((fk, idx) =>
        idx === i
          ? field === "references_table"
            ? { ...fk, references_table: value, references_column: "" }
            : { ...fk, [field]: value }
          : fk,
      ),
    );

  const removeFkRow = (i: number) =>
    setFkDraft((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!sessionId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await api.updateSemanticEntity(sessionId, entity.name, {
        entity: {
          description: descDraft,
          entity_type: entityTypeDraft,
          primary_keys: Array.from(pkDraft),
          foreign_keys: fkDraft.filter(
            (fk) => fk.column && fk.references_table && fk.references_column,
          ),
        },
        columns: Object.entries(colDraft).map(([technical_name, c]) => ({
          technical_name,
          ...c,
        })),
      });
      setEditing(false);
      onSaved?.((res as { semantic_view?: unknown }).semantic_view);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addColumn = async () => {
    if (!sessionId || !newCol.technical_name.trim()) return;
    setColActionBusy("__add__");
    setColActionError(null);
    try {
      const res = await api.addSemanticColumn(sessionId, entity.name, newCol);
      onSaved?.((res as { semantic_view?: unknown }).semantic_view);
      setNewCol({
        technical_name: "",
        business_name: "",
        data_type: "string",
        nullable: true,
        description: "",
      });
      setAddingColumn(false);
    } catch (err: unknown) {
      setColActionError(
        err instanceof Error ? err.message : "Add column failed",
      );
    } finally {
      setColActionBusy(null);
    }
  };

  const deleteColumn = async (technicalName: string) => {
    if (!sessionId || colActionBusy === technicalName) return;
    setColActionBusy(technicalName);
    setColActionError(null);
    let confirmMessage = `Delete column "${technicalName}"? This cannot be undone.`;
    try {
      const impact = await api.impactAnalysis(
        sessionId,
        entity.name,
        technicalName,
      );
      if (!impact.summary.safe_to_remove) {
        confirmMessage =
          `Deleting "${technicalName}" affects ${impact.summary.relationship_count} relationship(s) ` +
          `and ${impact.summary.metric_count} metric(s)` +
          (impact.metrics.length
            ? `: ${impact.metrics.map((m) => m.name).join(", ")}`
            : "") +
          `.\n\nDelete anyway? This cannot be undone.`;
      }
    } catch {
      // Best-effort — fall back to the generic confirm message if the
      // impact check itself fails (e.g. offline); don't block the delete.
    }
    if (!window.confirm(confirmMessage)) {
      setColActionBusy(null);
      return;
    }
    try {
      const res = await api.deleteSemanticColumn(
        sessionId,
        entity.name,
        technicalName,
      );
      onSaved?.((res as { semantic_view?: unknown }).semantic_view);
    } catch (err: unknown) {
      setColActionError(
        err instanceof Error ? err.message : "Delete column failed",
      );
    } finally {
      setColActionBusy(null);
    }
  };

  const deleteEntity = async () => {
    if (!sessionId || entityActionBusy) return;
    setEntityActionBusy(true);
    setEntityActionError(null);
    let confirmMessage = `Delete entity "${entity.name}" and any relationships involving it? This cannot be undone.`;
    try {
      const impact = await api.impactAnalysis(sessionId, entity.name);
      if (!impact.summary.safe_to_remove) {
        confirmMessage =
          `Deleting "${entity.name}" affects ${impact.summary.relationship_count} relationship(s) ` +
          `and ${impact.summary.metric_count} metric(s)` +
          (impact.downstream_entities.length
            ? `, and touches downstream entities: ${impact.downstream_entities.join(", ")}`
            : "") +
          `.\n\nDelete anyway? This cannot be undone.`;
      }
    } catch {
      // Best-effort — fall back to the generic confirm message if the
      // impact check itself fails (e.g. offline); don't block the delete.
    }
    if (!window.confirm(confirmMessage)) {
      setEntityActionBusy(false);
      return;
    }
    try {
      const res = await api.deleteSemanticEntity(sessionId, entity.name);
      onSaved?.((res as { semantic_view?: unknown }).semantic_view);
    } catch (err: unknown) {
      setEntityActionError(
        err instanceof Error ? err.message : "Delete entity failed",
      );
    } finally {
      setEntityActionBusy(false);
    }
  };

  const entityTypeColor = (t: string) => {
    const m: Record<string, string> = {
      fact: "bg-orange-500/15 text-orange-400 border-orange-500/20",
      dimension: "bg-blue-500/15 text-blue-400 border-blue-500/20",
      bridge: "bg-purple-500/15 text-purple-400 border-purple-500/20",
      aggregate: "bg-green-500/15 text-green-400 border-green-500/20",
    };
    return (
      m[t?.toLowerCase()] || "bg-gray-500/15 text-gray-400 border-gray-500/20"
    );
  };

  return (
    <div
      className="glass rounded-xl overflow-hidden"
      data-testid="semantic-entity"
      data-entity={entity.name}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors cursor-pointer"
      >
        <Table2 size={14} className="text-brand-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-white/90 flex-1">
          {entity.name}
        </span>
        {editing ? (
          <select
            data-testid="semantic-entity-type"
            data-entity={entity.name}
            value={entityTypeDraft}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setEntityTypeDraft(e.target.value)}
            className="text-[11px] px-2 py-0.5 rounded-full border font-semibold uppercase bg-surface-300 border-white/10 text-white/80 focus:outline-none focus:border-brand-500"
          >
            <option value="">—</option>
            {ENTITY_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        ) : (
          entity.entity_type && (
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold uppercase ${entityTypeColor(entity.entity_type)}`}
            >
              {entity.entity_type}
            </span>
          )
        )}
        {entity.primary_keys?.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-amber-400/60">
            <KeyRound size={9} /> {entity.primary_keys.length} PK
          </span>
        )}
        <span className="text-[11px] text-white/25 bg-surface-200 px-2 py-0.5 rounded">
          {cols.length} cols
        </span>
        {entity._user_edited && (
          <span
            className="text-[11px] text-green-400/70"
            title="Manually curated"
          >
            ✎ edited
          </span>
        )}
        {open ? (
          <ChevronUp size={12} className="text-white/30" />
        ) : (
          <ChevronDown size={12} className="text-white/30" />
        )}
      </div>
      {sessionId && open && !editing && (
        <div className="px-4 -mt-1 pb-1 flex items-center justify-end gap-3">
          {entityActionError && (
            <span className="text-[11px] text-red-400">
              {entityActionError}
            </span>
          )}
          <button
            data-testid="semantic-entity-delete"
            data-entity={entity.name}
            onClick={() => void deleteEntity()}
            disabled={entityActionBusy}
            className="text-[11px] text-red-400/60 hover:text-red-400 transition-colors disabled:opacity-50"
          >
            {entityActionBusy ? "Deleting…" : "🗑 Delete entity"}
          </button>
          <button
            data-testid="semantic-entity-edit"
            data-entity={entity.name}
            onClick={startEdit}
            className="text-[11px] text-brand-400 hover:text-brand-300 transition-colors"
          >
            ✎ Edit
          </button>
        </div>
      )}

      {open && (
        <div className="px-4 pb-4 pt-1">
          {editing ? (
            <textarea
              data-testid="semantic-entity-description-input"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              rows={2}
              placeholder="Entity description…"
              className="w-full mb-2 px-2 py-1.5 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
            />
          ) : (
            entity.description && (
              <p className="text-[11px] text-white/40 mb-2">
                {entity.description}
              </p>
            )
          )}
          {entity.grain && (
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-[11px] text-white/30">Grain:</span>
              <span className="text-[11px] text-white/60 italic">
                {entity.grain}
              </span>
            </div>
          )}
          {!editing && entity.primary_keys?.length > 0 && (
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-[11px] text-white/30">Primary Keys:</span>
              {entity.primary_keys.map((k: string) => (
                <code
                  key={k}
                  className="text-[11px] bg-amber-500/10 text-amber-400/80 px-1.5 py-0.5 rounded border border-amber-500/20"
                >
                  {k}
                </code>
              ))}
            </div>
          )}
          {!editing && fks.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <span className="text-[11px] text-white/30">Foreign Keys:</span>
              {fks
                .filter((fk: any) => typeof fk === "object" && fk !== null)
                .map((fk: any, i: number) => {
                  const col = fk.column || fk.source_column || "";
                  const refEntity =
                    fk.ref_table ||
                    fk.references_entity ||
                    fk.target_entity ||
                    fk.target_table ||
                    fk.references_table ||
                    "";
                  const refCol =
                    fk.ref_column ||
                    fk.references_column ||
                    fk.target_column ||
                    "";
                  if (!refEntity && !refCol) return null;
                  return (
                    <code
                      key={i}
                      className="text-[11px] bg-blue-500/10 text-blue-400/70 px-1.5 py-0.5 rounded border border-blue-500/20"
                    >
                      {col} → {refEntity}.{refCol}
                    </code>
                  );
                })}
            </div>
          )}
          {editing && (
            <div className="mb-3 p-2 bg-surface-300/40 rounded-lg border border-white/10">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-white/30">Foreign Keys:</span>
                <button
                  onClick={addFkRow}
                  className="flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 transition-colors"
                >
                  <Plus size={10} /> Add FK
                </button>
              </div>
              {fkDraft.length === 0 && (
                <p className="text-[11px] text-white/25">
                  No foreign keys defined.
                </p>
              )}
              {fkDraft.map((fk, i) => {
                const targetCols =
                  allEntities?.find((e) => e.name === fk.references_table)
                    ?.columns ?? [];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 mb-1.5 last:mb-0"
                  >
                    <select
                      data-testid="semantic-fk-column"
                      value={fk.column}
                      onChange={(e) => updateFkRow(i, "column", e.target.value)}
                      className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                    >
                      <option value="">Column…</option>
                      {cols.map((c: any) => (
                        <option key={c.technical_name} value={c.technical_name}>
                          {c.technical_name}
                        </option>
                      ))}
                    </select>
                    <span className="text-white/20">→</span>
                    <select
                      data-testid="semantic-fk-target-entity"
                      value={fk.references_table}
                      onChange={(e) =>
                        updateFkRow(i, "references_table", e.target.value)
                      }
                      className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                    >
                      <option value="">Target entity…</option>
                      {(allEntities ?? [])
                        .filter((e) => e.name !== entity.name)
                        .map((e) => (
                          <option key={e.name} value={e.name}>
                            {e.name}
                          </option>
                        ))}
                    </select>
                    <select
                      data-testid="semantic-fk-target-column"
                      value={fk.references_column}
                      onChange={(e) =>
                        updateFkRow(i, "references_column", e.target.value)
                      }
                      disabled={!fk.references_table}
                      className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500 disabled:opacity-40"
                    >
                      <option value="">Target column…</option>
                      {targetCols.map((c: any) => (
                        <option key={c.technical_name} value={c.technical_name}>
                          {c.technical_name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeFkRow(i)}
                      title="Remove foreign key"
                      className="text-red-400/50 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-white/5">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-surface-200/50 text-white/40">
                  <th className="text-left px-3 py-1.5 font-medium">
                    Business Term
                  </th>
                  <th className="text-left px-3 py-1.5 font-medium">Column</th>
                  <th className="text-left px-3 py-1.5 font-medium">Domain</th>
                  <th className="text-left px-3 py-1.5 font-medium">Type</th>
                  <th className="text-left px-3 py-1.5 font-medium">
                    Sensitivity
                  </th>
                  {editing && (
                    <th className="text-left px-3 py-1.5 font-medium">PK</th>
                  )}
                  {editing && (
                    <th className="text-left px-3 py-1.5 font-medium" />
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((col: any) => (
                  <tr
                    key={col.technical_name}
                    data-testid="semantic-col-row"
                    data-column={col.technical_name}
                    className="border-t border-white/[0.03] hover:bg-white/[0.02]"
                  >
                    <td className="px-3 py-1.5 text-white/80 font-medium">
                      {editing ? (
                        <input
                          data-testid="semantic-col-business-name"
                          data-column={col.technical_name}
                          value={
                            colDraft[col.technical_name]?.business_name ?? ""
                          }
                          onChange={(e) =>
                            setColDraft((p) => ({
                              ...p,
                              [col.technical_name]: {
                                ...p[col.technical_name],
                                business_name: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-1.5 py-0.5 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                        />
                      ) : (
                        col.business_name
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-white/40">
                      {col.technical_name}
                    </td>
                    <td className="px-3 py-1.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${_domainColor(col.domain)}`}
                      >
                        {col.domain}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-white/40">
                      {col.semantic_type}
                      {col.aggregation ? ` (${col.aggregation})` : ""}
                    </td>
                    <td className="px-3 py-1.5">
                      {editing ? (
                        <select
                          data-testid="semantic-col-sensitivity"
                          data-column={col.technical_name}
                          value={
                            colDraft[col.technical_name]?.sensitivity ?? ""
                          }
                          onChange={(e) =>
                            setColDraft((p) => ({
                              ...p,
                              [col.technical_name]: {
                                ...p[col.technical_name],
                                sensitivity: e.target.value,
                              },
                            }))
                          }
                          className="px-1.5 py-0.5 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                        >
                          <option value="">—</option>
                          {SENSITIVITY_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${_sensitivityColor(col.sensitivity)}`}
                        >
                          {col.sensitivity}
                        </span>
                      )}
                    </td>
                    {editing && (
                      <td className="px-3 py-1.5">
                        <input
                          type="checkbox"
                          data-testid="semantic-col-pk"
                          data-column={col.technical_name}
                          checked={pkDraft.has(col.technical_name)}
                          onChange={() => togglePk(col.technical_name)}
                          title="Primary key"
                        />
                      </td>
                    )}
                    {editing && (
                      <td className="px-3 py-1.5">
                        <button
                          data-testid="semantic-col-delete"
                          data-column={col.technical_name}
                          onClick={() => void deleteColumn(col.technical_name)}
                          disabled={colActionBusy === col.technical_name}
                          title="Delete column"
                          className="text-red-400/50 hover:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={11} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cols.length > 12 && !editing && (
            <button
              onClick={() => setShowAll((s) => !s)}
              className="text-[11px] text-brand-500 hover:text-brand-400 mt-2 transition-colors"
            >
              {showAll ? `Show fewer ▲` : `Show all ${cols.length} columns ▼`}
            </button>
          )}
          {editing && !addingColumn && (
            <button
              onClick={() => setAddingColumn(true)}
              className="flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 mt-2 transition-colors"
            >
              <Plus size={11} /> Add column
            </button>
          )}
          {editing && addingColumn && (
            <div className="mt-2 p-2 bg-surface-300/40 rounded-lg border border-white/10 space-y-1.5">
              <div className="grid grid-cols-2 gap-1.5">
                <input
                  value={newCol.technical_name}
                  onChange={(e) =>
                    setNewCol((p) => ({
                      ...p,
                      technical_name: e.target.value,
                    }))
                  }
                  placeholder="Column name (technical)"
                  className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                />
                <input
                  value={newCol.business_name}
                  onChange={(e) =>
                    setNewCol((p) => ({
                      ...p,
                      business_name: e.target.value,
                    }))
                  }
                  placeholder="Business name"
                  className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                />
                <input
                  value={newCol.data_type}
                  onChange={(e) =>
                    setNewCol((p) => ({ ...p, data_type: e.target.value }))
                  }
                  placeholder="Data type (e.g. string, integer)"
                  className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                />
                <label className="flex items-center gap-1.5 px-1.5 text-[11px] text-white/50">
                  <input
                    type="checkbox"
                    checked={newCol.nullable}
                    onChange={(e) =>
                      setNewCol((p) => ({
                        ...p,
                        nullable: e.target.checked,
                      }))
                    }
                  />
                  Nullable
                </label>
              </div>
              <input
                value={newCol.description}
                onChange={(e) =>
                  setNewCol((p) => ({ ...p, description: e.target.value }))
                }
                placeholder="Description (optional)"
                className="w-full px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => void addColumn()}
                  disabled={
                    colActionBusy === "__add__" || !newCol.technical_name.trim()
                  }
                  className="px-2 py-1 rounded-md text-[11px] font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white transition-colors"
                >
                  {colActionBusy === "__add__" ? "Adding…" : "Add"}
                </button>
                <button
                  onClick={() => {
                    setAddingColumn(false);
                    setColActionError(null);
                  }}
                  className="text-[11px] text-white/40 hover:text-white/70 transition-colors"
                >
                  Cancel
                </button>
                {colActionError && (
                  <span className="text-[11px] text-red-400">
                    {colActionError}
                  </span>
                )}
              </div>
            </div>
          )}
          {editing && (
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="semantic-entity-save"
                data-entity={entity.name}
                onClick={() => void save()}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white transition-colors"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/80 transition-colors"
              >
                Cancel
              </button>
              {saveError && (
                <span className="text-[11px] text-red-400">{saveError}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Reference list of every deterministic (no-LLM) way to edit the semantic
 *  view, surfaced when no LLM provider is configured for a project. */
function DeterministicRefineGuide({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const actions = [
    {
      title: "Edit an entity",
      steps:
        'Expand an entity card on the left, click "✎ Edit", then change its Entity Type, description, or any column\'s Business Name / Sensitivity / Description — Save applies immediately.',
    },
    {
      title: "Add or delete an entity/column",
      steps:
        'Use "Add entity" above the entity list for a target-only table with no source counterpart, or "🗑 Delete entity" on any entity card. While editing an entity, use "Add column" / the trash icon on a column row to add or remove fields.',
    },
    {
      title: "Toggle primary keys / add foreign keys",
      steps:
        'While editing an entity, check/uncheck the "PK" box on any column row to toggle it in/out of the primary key, and use "Add FK" in the Foreign Keys panel to link a column to another entity\'s column (or the trash icon to remove one) — Save applies both.',
    },
    {
      title: "Edit relationships",
      steps:
        'In the Relationships section on the left, click "Edit" to change the From/To entity and column or cardinality (1:1, 1:M, M:1, M:M) of any relationship, add a new one, or delete one with the trash icon.',
    },
    {
      title: "Find an entity",
      steps:
        "Use the search box above the entity list to filter down to specific entities by name.",
    },
  ];
  return (
    <div className="text-left">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 transition-colors"
      >
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        {open ? "Hide" : "View"} all deterministic actions
      </button>
      {open && (
        <ul className="mt-2 space-y-2">
          {actions.map((a) => (
            <li key={a.title} className="text-xs text-white/50">
              <span className="text-white/70 font-medium">{a.title}:</span>{" "}
              {a.steps}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Inline quick-fix rows for a Validation Gate issue whose affected_items are
 *  entity names missing a single deterministic field (primary key / grain /
 *  business purpose) — lets the user fill it in and save without leaving
 *  this screen. Only renders for fixable categories; caps the row count so
 *  a large model doesn't dump dozens of inputs onto the page at once. */
const QUICK_FIX_ROW_LIMIT = 8;

function IssueQuickFixPanel({
  field,
  entityNames,
  columnsByEntity,
  values,
  onChangeValue,
  saving,
  saved,
  onSave,
}: {
  field: "primary_keys" | "grain" | "business_purpose";
  entityNames: string[];
  columnsByEntity: Record<string, string[]>;
  values: Record<string, string>;
  onChangeValue: (entityName: string, value: string) => void;
  saving: Set<string>;
  saved: Set<string>;
  onSave: (entityName: string) => void;
}) {
  const shown = entityNames.slice(0, QUICK_FIX_ROW_LIMIT);
  const remaining = entityNames.length - shown.length;
  return (
    <div className="mt-2 space-y-1.5 border-t border-white/10 pt-2">
      {shown.map((entityName) => {
        const key = `${field}::${entityName}`;
        const isSaving = saving.has(key);
        const isSaved = saved.has(key);
        const value = values[key] ?? "";
        return (
          <div key={entityName} className="flex items-center gap-1.5">
            <span className="text-[11px] text-white/50 w-28 truncate flex-shrink-0">
              {entityName}
            </span>
            {field === "primary_keys" ? (
              <select
                value={value}
                onChange={(e) => onChangeValue(entityName, e.target.value)}
                disabled={isSaving}
                className="flex-1 min-w-0 px-2 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
              >
                <option value="">Select column…</option>
                {(columnsByEntity[entityName] ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => onChangeValue(entityName, e.target.value)}
                disabled={isSaving}
                placeholder={
                  field === "grain"
                    ? "e.g. one row per order"
                    : "e.g. tracks customer purchase orders"
                }
                className="flex-1 min-w-0 px-2 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white placeholder-gray-600"
              />
            )}
            <button
              onClick={() => onSave(entityName)}
              disabled={isSaving || !value.trim()}
              className="px-2 py-1 rounded text-[11px] font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white flex items-center gap-1 flex-shrink-0"
            >
              {isSaving ? (
                <Loader2 size={10} className="animate-spin" />
              ) : isSaved ? (
                <CheckCircle2 size={10} />
              ) : (
                "Save"
              )}
            </button>
          </div>
        );
      })}
      {remaining > 0 && (
        <p className="text-[11px] text-white/30">
          +{remaining} more — use Refine or Re-validate after fixing these.
        </p>
      )}
    </div>
  );
}

/** Styled glossary table built from entity data */
function GlossaryTable({ entities }: { entities: any[] }) {
  const allCols = entities.flatMap((e: any) =>
    (e.columns ?? []).map((c: any) => ({ ...c, entity: e.name })),
  );
  const [filter, setFilter] = useState("");
  const filtered = filter
    ? allCols.filter(
        (c: any) =>
          c.business_name?.toLowerCase().includes(filter.toLowerCase()) ||
          c.technical_name?.toLowerCase().includes(filter.toLowerCase()) ||
          c.domain?.toLowerCase().includes(filter.toLowerCase()),
      )
    : allCols;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-white/20"
          />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter terms..."
            className="w-full pl-7 pr-3 py-1.5 bg-white/5 border border-white/8 rounded-lg text-[11px] text-white/70 outline-none focus:border-brand-500/30 placeholder:text-white/15"
          />
        </div>
        <span className="text-[11px] text-white/20">
          {filtered.length} / {allCols.length}
        </span>
        <TableDownloadButtons
          title="Business Glossary"
          headers={[
            "Business Term",
            "Column",
            "Entity",
            "Domain",
            "Type",
            "Sensitivity",
            "Nullable",
          ]}
          rows={filtered.map((c: any) => [
            c.business_name ?? "",
            c.technical_name ?? "",
            c.entity ?? "",
            c.domain ?? "",
            c.semantic_type ?? "",
            c.sensitivity ?? "",
            c.nullable ?? "",
          ])}
          filenameBase="business-glossary"
        />
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/5 max-h-[400px] overflow-y-auto custom-scrollbar">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-surface-200/80 text-white/40">
              <th className="text-left px-3 py-1.5 font-medium">
                Business Term
              </th>
              <th className="text-left px-3 py-1.5 font-medium">Column</th>
              <th className="text-left px-3 py-1.5 font-medium">Entity</th>
              <th className="text-left px-3 py-1.5 font-medium">Domain</th>
              <th className="text-left px-3 py-1.5 font-medium">Type</th>
              <th className="text-left px-3 py-1.5 font-medium">Sensitivity</th>
              <th className="text-left px-3 py-1.5 font-medium">Nullable</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((col: any, i: number) => (
              <tr
                key={`${col.entity}-${col.technical_name}-${i}`}
                className="border-t border-white/[0.03] hover:bg-white/[0.02]"
              >
                <td className="px-3 py-1 text-white/80 font-medium whitespace-nowrap">
                  {col.business_name}
                </td>
                <td className="px-3 py-1 font-mono text-white/40 whitespace-nowrap">
                  {col.technical_name}
                </td>
                <td className="px-3 py-1 text-white/30 whitespace-nowrap">
                  {col.entity}
                </td>
                <td className="px-3 py-1">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${_domainColor(col.domain)}`}
                  >
                    {col.domain}
                  </span>
                </td>
                <td className="px-3 py-1 text-white/40 whitespace-nowrap">
                  {col.semantic_type}
                </td>
                <td className="px-3 py-1">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${_sensitivityColor(col.sensitivity)}`}
                  >
                    {col.sensitivity}
                  </span>
                </td>
                <td className="px-3 py-1 text-white/30">
                  {col.nullable ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Standalone color helpers for sub-components (outside main component)
function _domainColor(domain: string): string {
  const map: Record<string, string> = {
    Identity: "bg-blue-500/10 text-blue-400",
    Temporal: "bg-purple-500/10 text-purple-400",
    Financial: "bg-green-500/10 text-green-400",
    Geographic: "bg-teal-500/10 text-teal-400",
    Metric: "bg-amber-500/10 text-amber-400",
    Descriptive: "bg-indigo-500/10 text-indigo-400",
    Categorical: "bg-pink-500/10 text-pink-400",
  };
  return map[domain] || "bg-gray-500/10 text-gray-400";
}

function _sensitivityColor(sensitivity: string): string {
  const map: Record<string, string> = {
    Public: "bg-green-500/10 text-green-400",
    Internal: "bg-blue-500/10 text-blue-400",
    Confidential: "bg-amber-500/10 text-amber-400",
    PII: "bg-red-500/10 text-red-400",
  };
  return map[sensitivity] || "bg-gray-500/10 text-gray-400";
}

function buildFingerprint(fileName: string, columns: string[]): string {
  const sorted = [...columns].sort().join("|");
  return `${fileName}::${sorted}`;
}

function loadCachedContext(fingerprint: string): CachedDatasetCtx | null {
  try {
    const raw = localStorage.getItem(CONTEXT_CACHE_KEY);
    if (!raw) return null;
    const cache: Record<string, CachedDatasetCtx> = JSON.parse(raw);
    return cache[fingerprint] ?? null;
  } catch {
    return null;
  }
}

function saveCachedContext(fingerprint: string, ctx: CachedDatasetCtx) {
  try {
    const raw = localStorage.getItem(CONTEXT_CACHE_KEY);
    const cache: Record<string, CachedDatasetCtx> = raw ? JSON.parse(raw) : {};
    cache[fingerprint] = ctx;
    // Keep only the 20 most recent entries
    const entries = Object.entries(cache).sort(
      (a, b) =>
        new Date(b[1].savedAt).getTime() - new Date(a[1].savedAt).getTime(),
    );
    const trimmed = Object.fromEntries(entries.slice(0, 20));
    localStorage.setItem(CONTEXT_CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore storage errors */
  }
}

/* ═══════════════════════════════════════════════════
   Step Indicator — matching other agents
   ═══════════════════════════════════════════════════ */
const STEPS = [
  { label: "Upload", icon: Upload },
  { label: "Discover", icon: BarChart3 },
  { label: "Q&A", icon: MessageSquare },
  { label: "Semantic", icon: Sparkles },
  { label: "Refine", icon: Eye },
  { label: "Validate", icon: ClipboardCheck },
  { label: "Deploy", icon: Rocket },
  { label: "Docs", icon: BookOpen },
];

function stepToDisplay(current: number): number {
  if (current <= 1.8) return 1;
  if (current <= 2) return 2;
  if (current <= 3) return 3;
  if (current <= 4) return 4;
  if (current <= 4.5) return 5;
  if (current <= 4.7) return 6;
  if (current <= 5) return 7;
  return 8;
}

function StepIndicator({ current }: { current: number }) {
  const displayStep = stepToDisplay(current);
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const stepNum = i + 1;
        const isActive = stepNum === displayStep;
        const isDone = stepNum < displayStep;
        return (
          <div key={i} className="flex items-center">
            <div
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                isActive
                  ? "bg-brand-500/20 text-brand-500 border border-brand-500/30"
                  : isDone
                    ? "bg-green-500/10 text-green-400"
                    : "bg-surface-200 text-white/25"
              }`}
            >
              {isDone ? <CheckCircle2 size={11} /> : <Icon size={11} />}
              <span className="hidden md:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight size={10} className="text-white/10 mx-0.5" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Dataset Context Step — collects context before profiling
   ═══════════════════════════════════════════════════ */
const DOMAIN_OPTIONS = [
  "Healthcare",
  "Finance / Banking",
  "Insurance",
  "Retail / E-Commerce",
  "Manufacturing",
  "Government",
  "Technology",
  "Telecommunications",
  "Energy / Utilities",
  "Real Estate",
  "Education",
  "Transportation / Logistics",
  "Media / Entertainment",
  "Life Sciences / Pharma",
  "Child Support",
  "Child Welfare",
  "Government Funding / Grants",
];

/** Normalises a domain/skill label to a slug for comparison, e.g.
    "Child Support" -> "child-support" — lets a domain label be matched
    against a knowledge-pack slug without a hand-maintained lookup table. */
function slugify(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Domain knowledge base — auto-fills the Dataset Context fields when a
    business/industry domain is picked. Mirrors the backend's
    qa_domain_templates.py used for the later profiling Q&A step. Purely a
    starting point: every field stays freely editable, and edited values are
    never overwritten by a later domain switch. */
const DOMAIN_CONTEXT_TEMPLATES: Record<
  string,
  Partial<{
    rowMeaning: string;
    purpose: string;
    consumers: string;
    businessRules: string;
    specialRequirements: string;
  }>
> = {
  Healthcare: {
    rowMeaning:
      "One row per clinical or administrative event — e.g., an encounter, claim line, lab result, or patient record.",
    purpose:
      "Used for clinical quality reporting, compliance audits, and operational dashboards.",
    consumers:
      "Clinical quality teams, compliance/HIM, finance/billing, population health analysts.",
    businessRules:
      "Watch for duplicate patient records (MPI issues) and null diagnosis/procedure codes.",
    specialRequirements:
      "Contains PHI — requires masking/de-identification and HIPAA-compliant handling.",
  },
  "Finance / Banking": {
    rowMeaning:
      "One row per financial transaction, account, or statement line.",
    purpose:
      "Used for regulatory reporting, risk analytics, and financial dashboards.",
    consumers:
      "Risk & compliance, finance/treasury, fraud analytics, regulatory reporting teams.",
    businessRules:
      "Watch for reversed/duplicate transactions and currency mismatches.",
    specialRequirements:
      "Contains PII (SSNs, account numbers) — requires masking and AML/KYC compliance handling.",
  },
  Insurance: {
    rowMeaning: "One row per policy, claim, or premium transaction.",
    purpose:
      "Used for actuarial analysis, claims operations, and regulatory reporting.",
    consumers:
      "Actuarial, claims operations, underwriting, compliance/regulatory reporting.",
    businessRules:
      "Watch for claims re-opened/adjusted multiple times and missing loss-date fields.",
    specialRequirements:
      "Contains PII on claimants/insured parties — requires masking before wider distribution.",
  },
  "Retail / E-Commerce": {
    rowMeaning: "One row per order, order line item, or transaction.",
    purpose:
      "Used for sales reporting, merchandising analytics, and executive dashboards.",
    consumers: "Merchandising, marketing, finance, executive dashboards.",
    businessRules:
      "Exclude cancelled/returned orders where relevant; watch for test/internal accounts.",
    specialRequirements: "",
  },
  Manufacturing: {
    rowMeaning:
      "One row per production run, work order, or inspected unit/part.",
    purpose:
      "Used for plant operations reporting, quality assurance, and supply chain planning.",
    consumers:
      "Plant operations, supply chain, quality assurance, engineering.",
    businessRules:
      "Watch for sensor data gaps and unit-of-measure inconsistencies across plants.",
    specialRequirements: "",
  },
  Government: {
    rowMeaning:
      "One row per case, application, permit, or constituent transaction.",
    purpose:
      "Used for program reporting, compliance audits, and constituent services analytics.",
    consumers:
      "Program staff, compliance/audit, agency leadership, federal reporting.",
    businessRules: "Watch for legacy system data-entry inconsistencies.",
    specialRequirements:
      "Contains PII requiring redaction before external sharing.",
  },
  Technology: {
    rowMeaning: "One row per user event, session, ticket, or system log entry.",
    purpose:
      "Used for product analytics, operational monitoring, and customer success reporting.",
    consumers: "Product, engineering, customer success, executive dashboards.",
    businessRules: "Watch for bot/test traffic and duplicate event firing.",
    specialRequirements: "",
  },
  Telecommunications: {
    rowMeaning:
      "One row per subscriber, call detail record (CDR), or billing transaction.",
    purpose:
      "Used for churn analysis, network operations, and billing/revenue reporting.",
    consumers:
      "Network operations, customer retention/marketing, finance/billing.",
    businessRules: "Watch for duplicate CDRs and roaming edge cases.",
    specialRequirements:
      "Contains PII (phone numbers/addresses) — requires masking.",
  },
  "Energy / Utilities": {
    rowMeaning: "One row per meter read, outage event, or usage interval.",
    purpose:
      "Used for grid operations, billing accuracy, and demand forecasting.",
    consumers:
      "Grid operations, billing, regulatory reporting, demand forecasting teams.",
    businessRules:
      "Watch for meter-read gaps/estimates and daylight-saving-time interval boundaries.",
    specialRequirements: "",
  },
  "Real Estate": {
    rowMeaning: "One row per property, listing, or lease/transaction.",
    purpose:
      "Used for portfolio reporting, valuation analysis, and leasing dashboards.",
    consumers: "Portfolio managers, brokers, finance/asset management.",
    businessRules: "Watch for duplicate listings across MLS feeds.",
    specialRequirements: "",
  },
  Education: {
    rowMeaning:
      "One row per student enrollment, course section, or assessment record.",
    purpose:
      "Used for institutional research, accreditation reporting, and academic dashboards.",
    consumers:
      "Academic affairs, institutional research, compliance/state reporting.",
    businessRules: "Watch for duplicate student IDs across systems.",
    specialRequirements:
      "Contains FERPA-protected fields — requires masking before wider distribution.",
  },
  "Transportation / Logistics": {
    rowMeaning: "One row per shipment, route, or delivery event.",
    purpose:
      "Used for logistics operations reporting, fleet planning, and cost analysis.",
    consumers:
      "Logistics operations, dispatch, finance, supply chain planning.",
    businessRules:
      "Watch for GPS/telematics gaps and duplicate shipment records across carrier systems.",
    specialRequirements: "",
  },
  "Government Funding / Grants": {
    rowMeaning:
      "One row per fiscal-year funding allocation, apportionment, or obligation record.",
    purpose:
      "Used for federal/state funding allocation tracking, budget planning, and obligation/apportionment reporting.",
    consumers:
      "Program finance/budget office, grantor-agency reporting, program management, auditors.",
    businessRules:
      "Confirm obligated amounts never exceed apportioned/allocated amounts, and that unobligated/available balances reconcile year over year.",
    specialRequirements:
      "Budget and funding figures may be subject to public-records (FOIA) requests — confirm before external sharing.",
  },
  "Media / Entertainment": {
    rowMeaning:
      "One row per content asset, viewing/session event, or subscription transaction.",
    purpose:
      "Used for subscriber analytics, content engagement reporting, and revenue dashboards.",
    consumers: "Content/programming, marketing, subscriber analytics, finance.",
    businessRules:
      "Watch for bot/automated viewing events and duplicate session records.",
    specialRequirements: "",
  },
  "Life Sciences / Pharma": {
    rowMeaning:
      "One row per clinical trial subject visit, adverse event, or lot/batch record.",
    purpose:
      "Used for regulatory submissions, clinical operations reporting, and quality assurance.",
    consumers:
      "Clinical operations, regulatory affairs, quality assurance, R&D.",
    businessRules:
      "Watch for protocol deviations and batch/lot traceability requirements.",
    specialRequirements:
      "Contains PHI on trial subjects — requires GxP/HIPAA-compliant handling.",
  },
  "Child Support": {
    rowMeaning:
      "One row per IV-D case, support obligation, or payment/disbursement transaction.",
    purpose:
      "Used for federal IV-D performance reporting, collections tracking, and caseworker dashboards.",
    consumers:
      "Caseworkers, state IV-D program office, federal OCSE reporting, finance/disbursement unit.",
    businessRules:
      "Watch for duplicate non-custodial parent (NCP) records across counties.",
    specialRequirements:
      "Contains PII (SSNs) — requires masking and IV-D confidentiality compliance.",
  },
  "Child Welfare": {
    rowMeaning:
      "One row per case, allegation, contact/visit, or placement event tied to a child welfare case.",
    purpose:
      "Used for AFCARS/NCANDS compliance reporting, caseworker dashboards, and program oversight.",
    consumers:
      "Caseworkers/supervisors, DFPS/CCWIS program office, federal AFCARS/NCANDS reporting, compliance/audit.",
    businessRules:
      "Watch for polymorphic audit/change-log tables with no hard foreign keys.",
    specialRequirements:
      "Contains PII on children/caregivers — requires masking and strict confidentiality handling.",
  },
};

interface DatasetContextProps {
  datasetContext: {
    domain: string;
    domainCustom: string;
    rowMeaning: string;
    purpose: string;
    consumers: string;
    businessRules: string;
    relatedSystems: string;
    specialRequirements: string;
    knowledgeSkillSlug: string;
  };
  setDatasetContext: React.Dispatch<
    React.SetStateAction<DatasetContextProps["datasetContext"]>
  >;
  onSubmit: () => void;
  onSkip: () => void;
  onBack: () => void;
  sourceName: string;
  /** Column names from all sheets / the profile — used for smart placeholders */
  allColumns?: string[];
  /** Sheet names when multi-sheet */
  sheetNames?: string[];
}

/** Build dataset-aware placeholder text using actual column names / sheet names */
function smartPlaceholder(
  field:
    | "rowMeaning"
    | "purpose"
    | "consumers"
    | "businessRules"
    | "relatedSystems"
    | "specialRequirements",
  cols: string[],
  sheets: string[],
): string {
  // Pick a few representative columns for contextual hints
  const sample = cols.slice(0, 5).join(", ");
  const sheetList = sheets.length > 0 ? sheets.join(", ") : "";

  switch (field) {
    case "rowMeaning":
      if (cols.length > 0)
        return `Looking at columns like ${sample} — what does each row represent? (e.g., a customer order, a claim, a device reading…)`;
      return "e.g., Each row represents a customer transaction, a patient visit, an insurance claim...";
    case "purpose":
      if (cols.length > 0)
        return `What is the business purpose of data containing ${sample}? (e.g., monthly sales reporting, operational dashboards, compliance audit…)`;
      return "e.g., Used for monthly sales analytics, tracking revenue trends and customer segmentation";
    case "consumers":
      return "e.g., Business analysts, Data science team, Finance department, External auditors";
    case "businessRules":
      if (cols.length > 0) {
        const idCols = cols.filter((c) => /id|key|code/i.test(c)).slice(0, 2);
        const numCols = cols
          .filter((c) => /amount|price|qty|quantity|total|count|rate/i.test(c))
          .slice(0, 2);
        const hints: string[] = [];
        if (idCols.length > 0)
          hints.push(`Should ${idCols[0]} be unique per row?`);
        if (numCols.length > 0)
          hints.push(`Can ${numCols[0]} be negative (e.g., refunds)?`);
        if (hints.length > 0)
          return hints.join(" ") + " List any known constraints or rules.";
      }
      return 'e.g., Negative amounts indicate refunds; Each customer_id should be unique; Status "cancelled" excluded from revenue';
    case "relatedSystems":
      if (sheets.length > 1)
        return `How do ${sheetList} relate to one another? Which external systems feed these tables?`;
      if (cols.length > 0) {
        const fkCandidates = cols
          .filter((c) => /(_id|_key|_code|_ref)$/i.test(c))
          .slice(0, 3);
        if (fkCandidates.length > 0)
          return `Columns like ${fkCandidates.join(", ")} look like references — what tables/systems do they point to?`;
      }
      return "e.g., Connects to Products table via product_id, feeds into the data warehouse star schema";
    case "specialRequirements":
      if (cols.length > 0) {
        const piiHints = cols
          .filter((c) => /name|email|phone|ssn|address|dob|birth/i.test(c))
          .slice(0, 3);
        if (piiHints.length > 0)
          return `Columns like ${piiHints.join(", ")} may contain PII — any masking or compliance requirements (HIPAA, GDPR)?`;
      }
      return "e.g., Need to identify PII columns, want fact/dimension models, must comply with HIPAA, need data lineage tracking";
  }
}

type DatasetAwareContext = Partial<{
  rowMeaning: string;
  purpose: string;
  consumers: string;
  businessRules: string;
  specialRequirements: string;
}>;

/** Overlays the static per-domain template with facts actually observed in
    the profiled dataset (real column/sheet names) — the same
    dataset-grounding principle as the backend's ``qa_domain_templates``
    dataset-aware suggestion functions, applied to this step. Never invents
    a suggestion when no domain is selected (mirrors the "" -> undefined
    template lookup) — dataset facts only ever refine an existing
    domain-based suggestion, never replace the requirement to pick a
    domain first. */
function buildDatasetAwareContext(
  domain: string,
  cols: string[],
  sheets: string[],
): DatasetAwareContext {
  const template = DOMAIN_CONTEXT_TEMPLATES[domain];
  if (!template) return {};
  const result: DatasetAwareContext = { ...template };

  if (sheets.length > 1) {
    const note = `This dataset spans ${sheets.length} tables/sheets (${sheets.join(", ")}) — grain may differ per table.`;
    result.rowMeaning = result.rowMeaning
      ? `${result.rowMeaning} ${note}`
      : note;
  }

  const measureCols = cols.filter((c) =>
    /amount|price|qty|quantity|total|count|rate|revenue|balance|fee/i.test(c),
  );
  if (measureCols.length > 0) {
    const note = `Numeric columns detected: ${measureCols.slice(0, 5).join(", ")} — confirm expected ranges/signs (e.g., can they be negative?).`;
    result.businessRules = result.businessRules
      ? `${result.businessRules} ${note}`
      : note;
  }

  const piiCols = cols.filter((c) =>
    /name|email|phone|ssn|address|dob|birth/i.test(c),
  );
  if (piiCols.length > 0) {
    const note = `Columns like ${piiCols.slice(0, 5).join(", ")} look like PII — confirm masking/compliance requirements.`;
    result.specialRequirements = result.specialRequirements
      ? `${result.specialRequirements} ${note}`
      : note;
  }

  return result;
}

const KNOWLEDGE_DOC_TYPE_LABELS: Record<ProjectKnowledgeDocType, string> = {
  source_data_dictionary: "Source Data Dictionary",
  target_data_dictionary: "Target Data Dictionary",
  source_data_model: "Source Data Model",
  target_data_model: "Target Data Model",
  source_functional_spec: "Functional / Technical Spec",
  target_functional_spec: "Functional / Technical Spec",
  other: "Other",
};

/** Upload functional/technical documents (specs, data models, or any other
 * supporting material) for a project. Stored at the project level (not
 * scoped to Semantic Intelligence — see ``ztd_app.api.modules.
 * project_knowledge``), so any agent working on this project can reuse
 * them. Content is matched deterministically via plain-text search
 * (``qa_domain_templates.suggest_column_metadata_from_knowledge_docs``) to
 * pre-fill column descriptions — it is never sent to an LLM. Purely
 * additive: works exactly as before when nothing is uploaded. */
function ProjectKnowledgeUploadCard({ projectId }: { projectId?: string }) {
  const [docs, setDocs] = useState<ProjectKnowledgeDocInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<ProjectKnowledgeDocType>(
    "source_functional_spec",
  );
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(() => {
    if (!projectId) {
      setDocs([]);
      return;
    }
    setLoading(true);
    import("../apiv2")
      .then(({ apiv2 }) => apiv2.projectListKnowledgeDocs(projectId))
      .then((res) => setDocs(res))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!projectId) return null;

  const handleFileChosen = async (file: File | undefined) => {
    if (!file || !projectId) return;
    setError(null);
    setUploading(true);
    try {
      const { apiv2 } = await import("../apiv2");
      await apiv2.projectUploadKnowledgeDoc(projectId, file, docType);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (docId: string) => {
    if (!projectId) return;
    try {
      const { apiv2 } = await import("../apiv2");
      await apiv2.projectDeleteKnowledgeDoc(projectId, docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div className="glass rounded-xl p-5 space-y-3 text-left h-full flex flex-col">
      <div className="flex items-center gap-2">
        <FileSpreadsheet size={14} className="text-white/50" />
        <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
          Functional / Technical Documents
        </span>
        {(loading || uploading) && (
          <Loader2 size={12} className="animate-spin text-white/40" />
        )}
      </div>
      <p className="text-[11px] text-white/40">
        Upload a functional/technical spec, data model, or other reference
        material — matched deterministically (plain-text search, no AI/LLM) to
        pre-fill column descriptions. Stored at the project level, so other
        agents can use it too.
      </p>
      <select
        value={docType}
        onChange={(e) => setDocType(e.target.value as ProjectKnowledgeDocType)}
        disabled={uploading}
        title="Document type"
        className="w-full px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white focus:outline-none focus:border-brand-500/50"
      >
        {(
          Object.keys(KNOWLEDGE_DOC_TYPE_LABELS) as ProjectKnowledgeDocType[]
        ).map((t) => (
          <option key={t} value={t}>
            {KNOWLEDGE_DOC_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md"
        className="hidden"
        onChange={(e) => void handleFileChosen(e.target.files?.[0])}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full justify-center px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-200 border border-white/10 text-white/70 hover:text-white hover:border-white/25 flex items-center gap-1.5"
      >
        {uploading ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <Upload size={12} />
        )}
        Upload Document
      </button>

      {error && (
        <p className="text-[11px] text-red-400 flex items-center gap-1">
          <AlertTriangle size={11} /> {error}
        </p>
      )}

      {docs.length > 0 && (
        <div className="space-y-1.5 overflow-auto max-h-32 custom-scrollbar">
          {docs.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-surface-400/30 border border-white/8 rounded-lg"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet
                  size={12}
                  className="text-white/40 flex-shrink-0"
                />
                <span className="text-xs text-white/80 truncate">
                  {d.filename}
                </span>
              </div>
              <button
                onClick={() => void handleDelete(d.id)}
                title="Remove document"
                className="text-white/30 hover:text-red-400 flex-shrink-0"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Human-readable question text per dataset-context field — used both as the
 * "Question" column in the downloadable workbook and to recognize a field by
 * name (via the hidden "Field" column) when importing one back. */
const DATASET_CONTEXT_FIELD_LABELS: Record<
  keyof DatasetContextProps["datasetContext"],
  string
> = {
  domain: "Business Domain",
  domainCustom: "Business Domain (custom)",
  rowMeaning: "What does each row represent?",
  purpose: "Purpose of this dataset",
  consumers: "Who uses this data?",
  businessRules: "Key business rules or constraints",
  relatedSystems: "Related systems or datasets",
  specialRequirements: "Any specific requirements for the semantic model?",
  knowledgeSkillSlug: "Knowledge skill",
};

const DATASET_CONTEXT_FIELDS = Object.keys(
  DATASET_CONTEXT_FIELD_LABELS,
) as Array<keyof DatasetContextProps["datasetContext"]>;

/** Builds a "Field | Question | Answer" workbook for the current dataset
 * context (Field is hidden — it's only there so the file round-trips back to
 * the right form fields on import, even if a reviewer edits the Question
 * text). */
async function buildDatasetContextWorkbook(
  datasetContext: DatasetContextProps["datasetContext"],
): Promise<Blob> {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet("Dataset Context");
  sheet.columns = [
    { header: "Field", key: "field", width: 22 },
    { header: "Question", key: "question", width: 45 },
    { header: "Answer", key: "answer", width: 70 },
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.getColumn("field").hidden = true;
  for (const field of DATASET_CONTEXT_FIELDS) {
    if (field === "knowledgeSkillSlug") continue;
    const row = sheet.addRow({
      field,
      question: DATASET_CONTEXT_FIELD_LABELS[field],
      answer: datasetContext[field] || "",
    });
    row.getCell("question").alignment = { wrapText: true, vertical: "top" };
    row.getCell("answer").alignment = { wrapText: true, vertical: "top" };
  }
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Reads a "Field | Question | Answer" workbook (the shape produced by
 * buildDatasetContextWorkbook) and pulls out only known, non-empty answers by
 * their Field key — a reviewer can freely reorder/reword the Question column
 * without breaking the import. */
async function parseDatasetContextWorkbook(
  file: File,
): Promise<Partial<DatasetContextProps["datasetContext"]>> {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  const result: Partial<DatasetContextProps["datasetContext"]> = {};
  if (!sheet) return result;
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header
    const fieldName = String(row.getCell(1).value ?? "").trim();
    const validField = DATASET_CONTEXT_FIELDS.find((f) => f === fieldName);
    if (!validField) return;
    const answer = cellText(row.getCell(3).value);
    if (answer.trim()) {
      result[validField] = answer;
    }
  });
  return result;
}

/** Reads a workbook cell's value as plain text — exceljs returns rich-text
 * cells as `{ richText: [...] }`/`{ text }` objects rather than plain
 * strings, so this normalizes either shape to a string. */
function cellText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object" && "text" in value) {
    return String((value as { text: unknown }).text ?? "");
  }
  return String(value);
}

/** Normalises a raw relationship record for display/editing. The
 * deterministic view-generation engine emits source_entity/target_entity,
 * while the relationship-editor's save path writes back from_entity/
 * to_entity — so any code reading `sv.relationships` must accept either. */
/** Backend cardinality estimates may arrive as short "1:1"/"M:1"/"1:M"/"M:M"
 * form (ztd_app/tools/semantic/relationship_detector.py) or the underscore
 * enum form used by the relationship editor — canonicalize to the latter. */
const CARDINALITY_ALIASES: Record<string, string> = {
  "1:1": "one_to_one",
  "m:1": "many_to_one",
  "1:m": "one_to_many",
  "m:m": "many_to_many",
};

function canonicalizeCardinality(raw: string): string {
  if (!raw) return "unknown";
  return CARDINALITY_ALIASES[raw.toLowerCase()] ?? raw.toLowerCase();
}

function normalizeRelationship(r: Record<string, unknown>): {
  fromEntity: string;
  fromColumn: string;
  toEntity: string;
  toColumn: string;
  cardinality: string;
} {
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  return {
    fromEntity: str(r.from_entity) || str(r.source_entity) || str(r.from),
    fromColumn: str(r.from_column) || str(r.source_column),
    toEntity: str(r.to_entity) || str(r.target_entity) || str(r.to),
    toColumn: str(r.to_column) || str(r.target_column),
    cardinality: canonicalizeCardinality(str(r.cardinality)),
  };
}

/** Short display label for a relationship cardinality; "unknown" is an
 * internal sentinel (see normalizeRelationship) and is never shown verbatim. */
function cardinalityLabel(cardinality: string): string {
  const m: Record<string, string> = {
    one_to_one: "1:1",
    one_to_many: "1:N",
    many_to_one: "N:1",
    many_to_many: "N:N",
  };
  return m[cardinality] ?? (cardinality === "unknown" ? "—" : cardinality);
}

/** Converts a raw (possibly source_entity/target_entity-shaped) relationship
 * record into the payload shape `updateRelationships` expects. Shared by the
 * bulk relationship editor and the ER canvas's single-relationship actions. */
function toRelationshipPayload(
  r: Record<string, unknown>,
): SemanticRelationship {
  const n = normalizeRelationship(r);
  return {
    from_entity: n.fromEntity,
    to_entity: n.toEntity,
    from_column: n.fromColumn,
    to_column: n.toColumn,
    cardinality: n.cardinality === "unknown" ? "many_to_one" : n.cardinality,
    confidence: typeof r.confidence === "number" ? r.confidence : 1,
  };
}

/** Client-side metric lineage — matches a metric's expression against its
 * owning entity's column names (whole-word match) so the Metrics tab can
 * show "→ references: col_a, col_b" without a round-trip to the backend.
 * Deterministic string matching only; never inspects row data. */
function metricLineageColumns(
  metric: { entity?: string; expression?: string },
  entities: Array<{
    name: string;
    columns?: Array<{ technical_name: string }>;
  }>,
): string[] {
  const expr = metric.expression ?? "";
  const owningEntity = entities.find((e) => e.name === metric.entity);
  if (!owningEntity || !expr) return [];
  const referenced: string[] = [];
  for (const col of owningEntity.columns ?? []) {
    const name = col.technical_name;
    if (!name) continue;
    const pattern = new RegExp(
      `\\b${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "i",
    );
    if (pattern.test(expr)) referenced.push(name);
  }
  return referenced;
}

/** Builds a single-sheet workbook for the small ad-hoc "download this table"
 * buttons scattered across the semantic view (facts, dimensions, columns,
 * measures, relationships, entity details, glossary). */
async function buildRowsWorkbook(
  sheetName: string,
  headers: string[],
  rows: (string | number)[][],
): Promise<Blob> {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet(sheetName.slice(0, 31) || "Sheet1");
  sheet.addRow(headers).font = { bold: true };
  rows.forEach((row) => sheet.addRow(row));
  sheet.columns.forEach((col) => {
    col.width = 24;
  });
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Renders the same headers+rows shape as a landscape PDF via jsPDF-autotable. */
function downloadRowsAsPdf(
  title: string,
  headers: string[],
  rows: (string | number)[][],
  filename: string,
): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt" });
  doc.setFontSize(13);
  doc.setTextColor(20);
  doc.text(title, 40, 36);
  autoTable(doc, {
    startY: 50,
    head: [headers],
    body: rows,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [55, 65, 81] },
  });
  doc.save(filename);
}

/** Small Excel/PDF download icon pair reused next to every table-shaped
 * section of the semantic view (facts, dimensions, columns, measures,
 * relationships, entity details, glossary). */
function TableDownloadButtons({
  title,
  headers,
  rows,
  filenameBase,
}: {
  title: string;
  headers: string[];
  rows: (string | number)[][];
  filenameBase: string;
}) {
  const [busy, setBusy] = useState<"xlsx" | "pdf" | null>(null);
  const disabled = busy !== null || rows.length === 0;

  const handleExcel = async () => {
    setBusy("xlsx");
    try {
      const blob = await buildRowsWorkbook(title, headers, rows);
      await saveBlobFile(blob, `${filenameBase}.xlsx`);
    } catch (err) {
      if (!isAbortError(err)) {
        console.error("Failed to download Excel:", err);
      }
    } finally {
      setBusy(null);
    }
  };

  const handlePdf = () => {
    setBusy("pdf");
    try {
      downloadRowsAsPdf(title, headers, rows, `${filenameBase}.pdf`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => void handleExcel()}
        disabled={disabled}
        title="Download as Excel"
        className="p-1 rounded text-white/25 hover:text-white/70 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        {busy === "xlsx" ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <FileSpreadsheet size={12} />
        )}
      </button>
      <button
        type="button"
        onClick={handlePdf}
        disabled={disabled}
        title="Download as PDF"
        className="p-1 rounded text-white/25 hover:text-white/70 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        {busy === "pdf" ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <FileText size={12} />
        )}
      </button>
    </div>
  );
}

/** Builds a "ID (hidden) | Category | Question | Answer" workbook for the
 * Smart Q&A / Analytical Requirements step — same shape/rationale as
 * buildDatasetContextWorkbook, keyed by question id so re-import still works
 * even if a reviewer rewords the Question column. */
async function buildQaAnswersWorkbook(
  questions: ProfileQuestion[],
  answers: QAFormState,
): Promise<Blob> {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet("Analytical Requirements");
  sheet.columns = [
    { header: "ID", key: "id", width: 22 },
    { header: "Category", key: "category", width: 20 },
    { header: "Question", key: "question", width: 50 },
    { header: "Answer", key: "answer", width: 70 },
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.getColumn("id").hidden = true;
  for (const q of questions) {
    const row = sheet.addRow({
      id: q.id,
      category: q.category,
      question: q.required ? `${q.question} *` : q.question,
      answer: answers[q.id] || "",
    });
    row.getCell("question").alignment = { wrapText: true, vertical: "top" };
    row.getCell("answer").alignment = { wrapText: true, vertical: "top" };
  }
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Reads a workbook produced by buildQaAnswersWorkbook (or hand-edited) and
 * pulls out non-empty answers keyed by question id — unknown ids (e.g. from
 * a stale download whose questions changed) are ignored rather than
 * rejected. */
async function parseQaAnswersWorkbook(
  file: File,
  questions: ProfileQuestion[],
): Promise<QAFormState> {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  const result: QAFormState = {};
  if (!sheet) return result;
  const validIds = new Set(questions.map((q) => q.id));
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header
    const id = cellText(row.getCell(1).value).trim();
    if (!validIds.has(id)) return;
    const answer = cellText(row.getCell(4).value);
    if (answer.trim()) {
      result[id] = answer;
    }
  });
  return result;
}

function DatasetContextStep({
  datasetContext,
  setDatasetContext,
  onSubmit,
  onSkip,
  onBack,
  sourceName,
  allColumns = [],
  sheetNames = [],
}: DatasetContextProps) {
  const update = (field: string, value: string) =>
    setDatasetContext((prev) => ({ ...prev, [field]: value }));

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [downloadingContext, setDownloadingContext] = useState(false);

  const handleDownloadContext = async () => {
    const safeName = (sourceName || "dataset").replace(/[^a-z0-9-_]+/gi, "_");
    setDownloadingContext(true);
    try {
      const blob = await buildDatasetContextWorkbook(datasetContext);
      await saveBlobFile(blob, `dataset-context-${safeName}.xlsx`);
    } catch (err) {
      if (!isAbortError(err)) {
        setImportError(
          err instanceof Error ? err.message : "Failed to download context.",
        );
      }
    } finally {
      setDownloadingContext(false);
    }
  };

  const handleImportClick = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;
    try {
      const imported = await parseDatasetContextWorkbook(file);
      if (Object.keys(imported).length === 0) {
        throw new Error(
          "No answers found — use the downloaded workbook's own Field/Question columns.",
        );
      }
      setDatasetContext((prev) => ({ ...prev, ...imported }));
      setImportError(null);
    } catch (err) {
      setImportError(
        err instanceof Error
          ? `Import failed: ${err.message}`
          : "Import failed: invalid file.",
      );
    }
  };

  const isSuggested = (field: keyof typeof datasetContext) => {
    const suggestion = buildDatasetAwareContext(
      datasetContext.domain,
      allColumns,
      sheetNames,
    );
    const value = datasetContext[field];
    return (
      Boolean(value) && suggestion[field as keyof typeof suggestion] === value
    );
  };

  const hasAnyContext =
    datasetContext.domain ||
    datasetContext.domainCustom ||
    datasetContext.rowMeaning ||
    datasetContext.purpose ||
    datasetContext.consumers ||
    datasetContext.businessRules ||
    datasetContext.relatedSystems ||
    datasetContext.specialRequirements;

  return (
    <div
      className="flex flex-col min-h-0 animate-fade-in"
      style={{ maxHeight: "calc(100vh - 160px)" }}
    >
      {/* Header — compact so the scrollable form below never needs a page-level scrollbar */}
      <div className="flex-shrink-0 text-center space-y-1">
        <div
          className="inline-flex items-center gap-2 px-3 py-0.5 rounded-full border text-xs font-medium"
          style={{
            background: "rgba(134,188,37,0.08)",
            borderColor: "rgba(134,188,37,0.2)",
            color: "#86BC25",
          }}
        >
          <Target size={12} />
          Dataset Context
        </div>
        <h2 className="text-lg font-bold text-white">
          Tell us about your data
        </h2>
        <p className="text-white/35 max-w-2xl mx-auto text-xs">
          Providing context about{" "}
          <strong className="text-white/60">{sourceName}</strong> helps the AI
          build a more accurate and relevant semantic data model tailored to
          your needs.
        </p>
      </div>

      {/* Download / import these questions & answers as Excel — work offline or share with someone else */}
      <div className="flex-shrink-0 max-w-3xl w-full mx-auto flex items-center justify-end gap-2 pt-2">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => void handleImportFile(e)}
          className="hidden"
        />
        <button
          onClick={handleImportClick}
          title="Import a completed Excel workbook of these questions & answers"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/50 hover:text-white/80 border border-white/10 rounded-lg transition-colors"
        >
          <Upload size={12} /> Import
        </button>
        <button
          onClick={() => void handleDownloadContext()}
          disabled={downloadingContext}
          title="Download these questions & answers as an Excel workbook to fill in offline or share"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/50 hover:text-white/80 border border-white/10 rounded-lg transition-colors disabled:opacity-50"
        >
          <Download size={12} />{" "}
          {downloadingContext ? "Preparing…" : "Download Excel"}
        </button>
      </div>
      {importError && (
        <div className="flex-shrink-0 max-w-3xl w-full mx-auto text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mt-2">
          {importError}
        </div>
      )}

      {/* Context form — takes up all remaining height and scrolls internally;
          Back/Skip/Continue stay pinned as a footer below the scroll area. */}
      <div className="flex-1 min-h-0 max-w-3xl w-full mx-auto overflow-y-auto custom-scrollbar space-y-4 px-1 py-3">
        {/* Business Domain summary — chosen on the previous (Knowledge Base) step */}
        <div className="glass rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Globe size={14} className="text-brand-500" />
            <span className="text-white/50">Business Domain:</span>
            <strong className="text-white">
              {datasetContext.domainCustom ||
                datasetContext.domain ||
                "Not set"}
            </strong>
          </div>
          <button
            onClick={onBack}
            className="text-xs text-white/40 hover:text-white/70"
          >
            Change
          </button>
        </div>

        {/* Row meaning */}
        <ContextField
          icon={<Table2 size={14} className="text-accent-cyan" />}
          label="What does each row represent?"
          value={datasetContext.rowMeaning}
          onChange={(v) => update("rowMeaning", v)}
          placeholder={smartPlaceholder("rowMeaning", allColumns, sheetNames)}
          suggested={isSuggested("rowMeaning")}
        />

        {/* Purpose */}
        <ContextField
          icon={<Target size={14} className="text-purple-400" />}
          label="Purpose of this dataset"
          value={datasetContext.purpose}
          onChange={(v) => update("purpose", v)}
          placeholder={smartPlaceholder("purpose", allColumns, sheetNames)}
          multiline
          suggested={isSuggested("purpose")}
        />

        {/* Consumers */}
        <ContextField
          icon={<User size={14} className="text-orange-400" />}
          label="Who uses this data?"
          value={datasetContext.consumers}
          onChange={(v) => update("consumers", v)}
          placeholder={smartPlaceholder("consumers", allColumns, sheetNames)}
          suggested={isSuggested("consumers")}
        />

        {/* Business rules */}
        <ContextField
          icon={<ClipboardCheck size={14} className="text-yellow-400" />}
          label="Key business rules or constraints"
          value={datasetContext.businessRules}
          onChange={(v) => update("businessRules", v)}
          placeholder={smartPlaceholder(
            "businessRules",
            allColumns,
            sheetNames,
          )}
          multiline
          suggested={isSuggested("businessRules")}
        />

        {/* Related systems */}
        <ContextField
          icon={<GitBranch size={14} className="text-teal-400" />}
          label="Related systems or datasets"
          value={datasetContext.relatedSystems}
          onChange={(v) => update("relatedSystems", v)}
          placeholder={smartPlaceholder(
            "relatedSystems",
            allColumns,
            sheetNames,
          )}
          multiline
        />

        {/* Special requirements */}
        <ContextField
          icon={<Shield size={14} className="text-red-400" />}
          label="Any specific requirements for the semantic model?"
          value={datasetContext.specialRequirements}
          onChange={(v) => update("specialRequirements", v)}
          placeholder={smartPlaceholder(
            "specialRequirements",
            allColumns,
            sheetNames,
          )}
          multiline
          suggested={isSuggested("specialRequirements")}
        />

        {/* Pro tip */}
        <div className="glass rounded-xl p-4 flex items-start gap-3">
          <Sparkles size={16} className="text-brand-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-white/40 space-y-1">
            <p className="text-white/60 font-medium">Why does this matter?</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>
                The AI uses your context to classify columns accurately (PII,
                financial, etc.)
              </li>
              <li>
                Business rules shape data quality checks and semantic
                relationships
              </li>
              <li>
                Domain knowledge helps generate industry-specific glossary terms
              </li>
              <li>You can always refine during the Smart Q&A step</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions — pinned below the scrollable field list */}
      <div className="flex-shrink-0 flex items-center justify-center gap-4 pt-4">
        <button
          onClick={onBack}
          className="px-5 py-2.5 text-sm text-white/35 hover:text-white transition-colors"
        >
          Back
        </button>
        <button
          onClick={onSkip}
          className="px-5 py-2.5 text-sm text-white/25 hover:text-white/50 transition-colors"
        >
          Skip for now
        </button>
        <button
          onClick={onSubmit}
          className="px-6 py-2.5 text-white rounded-xl font-medium text-sm transition-all flex items-center gap-2"
          style={{
            background: hasAnyContext
              ? "linear-gradient(135deg, #86BC25, #6B9B1E)"
              : "rgba(134,188,37,0.3)",
            boxShadow: hasAnyContext ? "0 0 20px rgba(134,188,37,0.2)" : "none",
          }}
        >
          <ArrowRight size={16} />
          Continue to Analysis
        </button>
      </div>
    </div>
  );
}

function ContextField({
  icon,
  label,
  value,
  onChange,
  placeholder,
  multiline,
  suggested,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  multiline?: boolean;
  suggested?: boolean;
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const autoResize = React.useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useEffect(() => {
    autoResize(textareaRef.current);
  }, [value, autoResize]);

  return (
    <div className="glass rounded-xl p-5 space-y-2">
      <label className="flex items-center gap-2 text-sm font-semibold text-white/70">
        {icon}
        {label}
      </label>
      {suggested && (
        <span className="inline-flex items-center gap-1 text-[11px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
          <Wand2 size={8} /> AI suggested — review &amp; edit
        </span>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          autoResize(e.target);
        }}
        placeholder={placeholder}
        rows={multiline ? 2 : 1}
        className="w-full px-3 py-2 bg-surface-300 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 resize-none transition-all overflow-hidden"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   Profile helpers (from ProfileView)
   ═══════════════════════════════════════════════════ */
function qualityColor(score: number): string {
  if (score >= 95) return "text-green-400";
  if (score >= 80) return "text-lime-400";
  if (score >= 60) return "text-yellow-400";
  return "text-red-400";
}
function qualityBg(score: number): string {
  if (score >= 95) return "bg-green-500";
  if (score >= 80) return "bg-lime-500";
  if (score >= 60) return "bg-yellow-500";
  return "bg-red-500";
}

/* ═══════════════════════════════════════════════════
   Target Platform Config
   ═══════════════════════════════════════════════════ */
const TARGETS = [
  {
    id: "oracle",
    name: "Oracle Database",
    icon: Database,
    color: "text-red-400 bg-red-500/10 border-red-500/30",
    features: ["DDL", "Views", "Quality Checks", "Data Redaction"],
  },
  {
    id: "sqlserver",
    name: "SQL Server",
    icon: Server,
    color: "text-accent-blue bg-accent-blue/10 border-accent-blue/30",
    features: ["DDL", "Views", "Quality Checks", "Dynamic Masking"],
  },
  {
    id: "snowflake",
    name: "Snowflake",
    icon: SnowflakeIcon,
    color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    features: ["DDL", "Views", "Quality Checks", "Masking Policies"],
  },
  {
    id: "postgres",
    name: "PostgreSQL",
    icon: Database,
    color: "text-indigo-400 bg-indigo-500/10 border-indigo-500/30",
    features: ["DDL", "Views", "Quality Checks", "Comments"],
  },
  {
    id: "mysql",
    name: "MySQL",
    icon: Database,
    color: "text-orange-400 bg-orange-500/10 border-orange-500/30",
    features: ["DDL", "Views", "Quality Checks", "Comments"],
  },
  {
    id: "bigquery",
    name: "BigQuery",
    icon: Cloud,
    color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    features: ["DDL", "Views", "Quality Checks"],
  },
];

/* ═══════════════════════════════════════════════════
   Target model artifact file metadata (filenames/mime types mirror what
   the converter tool writes server-side — see generate_target_model() in
   ztd_app/tools/converter/target_model.py).
   ═══════════════════════════════════════════════════ */
const ARTIFACT_FILE_META: Record<string, { name: string; mime: string }> = {
  create_tables_ddl: { name: "01_create_tables.sql", mime: "text/x-sql" },
  semantic_views_ddl: { name: "02_semantic_views.sql", mime: "text/x-sql" },
  quality_checks: { name: "03_quality_checks.sql", mime: "text/x-sql" },
  sensitivity_policies: {
    name: "04_sensitivity_policies.sql",
    mime: "text/x-sql",
  },
  semantic_view_yaml: {
    name: "semantic-view.yaml",
    mime: "application/x-yaml",
  },
  deployment_guide: { name: "deployment-guide.md", mime: "text/markdown" },
  business_glossary: { name: "business-glossary.md", mime: "text/markdown" },
  er_diagram: { name: "er-diagram.md", mime: "text/markdown" },
};
const artifactFilename = (key: string): string =>
  ARTIFACT_FILE_META[key]?.name ?? `${key}.txt`;
const artifactMime = (key: string): string =>
  ARTIFACT_FILE_META[key]?.mime ?? "text/plain";
/** Rendered as prose (ReactMarkdown) in the artifacts accordion instead of a raw <pre> block. */
const MARKDOWN_ARTIFACT_KEYS = new Set([
  "deployment_guide",
  "business_glossary",
  "er_diagram",
]);

/** True for a cancelled native file/folder picker — not a real failure. */
const isAbortError = (err: unknown): boolean =>
  err instanceof Error &&
  (err.name === "AbortError" || /aborted a request/i.test(err.message));

/** Save one text artifact, letting the user pick the location when the
 * browser supports it (Chromium), falling back to a normal download. */
async function saveTextFile(
  content: string,
  filename: string,
  mimeType: string,
): Promise<void> {
  const blob = new Blob([content], { type: mimeType });
  if (window.showSaveFilePicker) {
    const handle = await window.showSaveFilePicker({ suggestedName: filename });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Save an arbitrary blob (e.g. a rasterized diagram), same picker-or-download
 * fallback behavior as saveTextFile. */
async function saveBlobFile(blob: Blob, filename: string): Promise<void> {
  if (window.showSaveFilePicker) {
    const handle = await window.showSaveFilePicker({ suggestedName: filename });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Rasterizes a rendered Mermaid SVG string to a PNG blob via an offscreen canvas. */
function svgStringToPngBlob(svgString: string, scale = 2): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth || img.width || 800;
      const height = img.naturalHeight || img.height || 600;
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Canvas not supported"));
        return;
      }
      ctx.fillStyle = "#1c1c2a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to create PNG"));
      }, "image/png");
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to rasterize diagram"));
    };
    img.src = url;
  });
}

/* ── Markdown -> structured blocks, shared by the PDF and Excel(CSV) artifact
 * exporters below — good enough for the deployment-guide/glossary generators'
 * output (headings, bold metadata lines, pipe tables, fenced code blocks). */
type MdBlock =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "code"; text: string }
  | { type: "hr" };

const stripInlineMarkdown = (text: string): string =>
  text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .trim();

function parseMarkdownBlocks(markdown: string): MdBlock[] {
  const lines = markdown.split(/\r?\n/);
  const blocks: MdBlock[] = [];
  const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
  const isTableSep = (line: string) =>
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
  const splitRow = (line: string): string[] =>
    line
      .trim()
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((cell) => stripInlineMarkdown(cell.trim()));

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (/^```/.test(line.trim())) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      blocks.push({ type: "code", text: codeLines.join("\n") });
      continue;
    }
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        text: stripInlineMarkdown(headingMatch[2]),
      });
      i++;
      continue;
    }
    if (/^\s*-{3,}\s*$/.test(line)) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: "table", head, rows });
      continue;
    }
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^```/.test(lines[i].trim()) &&
      !/^#{1,6}\s+/.test(lines[i]) &&
      !/^\s*-{3,}\s*$/.test(lines[i]) &&
      !(
        isTableRow(lines[i]) &&
        i + 1 < lines.length &&
        isTableSep(lines[i + 1])
      )
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      blocks.push({
        type: "paragraph",
        text: stripInlineMarkdown(paraLines.join(" ")),
      });
    }
  }
  return blocks;
}

/** Renders a markdown artifact (deployment guide, business glossary, ...) as
 * a formatted PDF — real headings and tables instead of a raw pipe-text dump. */
function downloadMarkdownAsPdf(
  markdown: string,
  docTitle: string,
  filename: string,
): void {
  const blocks = parseMarkdownBlocks(markdown);
  const doc = new jsPDF({ orientation: "portrait", unit: "pt" });
  const margin = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let cursorY = 50;

  const ensureRoom = (needed: number) => {
    if (cursorY + needed > pageHeight - 40) {
      doc.addPage();
      cursorY = 50;
    }
  };

  doc.setFontSize(16);
  doc.setTextColor(20);
  doc.text(docTitle, margin, cursorY);
  cursorY += 26;

  for (const block of blocks) {
    if (block.type === "heading") {
      ensureRoom(24);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(block.level <= 1 ? 14 : block.level === 2 ? 12.5 : 11);
      doc.setTextColor(20);
      doc.text(block.text, margin, cursorY);
      doc.setFont("helvetica", "normal");
      cursorY += block.level <= 2 ? 18 : 14;
    } else if (block.type === "paragraph") {
      doc.setFontSize(9.5);
      doc.setTextColor(60);
      const wrapped = doc.splitTextToSize(block.text, pageWidth - margin * 2);
      ensureRoom(wrapped.length * 12 + 6);
      doc.text(wrapped, margin, cursorY);
      cursorY += wrapped.length * 12 + 10;
    } else if (block.type === "code") {
      const codeLines = block.text.split("\n");
      ensureRoom(codeLines.length * 11 + 10);
      doc.setFont("courier", "normal");
      doc.setFontSize(8);
      doc.setTextColor(40);
      doc.text(codeLines, margin, cursorY);
      cursorY += codeLines.length * 11 + 12;
      doc.setFont("helvetica", "normal");
    } else if (block.type === "hr") {
      ensureRoom(10);
      doc.setDrawColor(200);
      doc.line(margin, cursorY, pageWidth - margin, cursorY);
      cursorY += 14;
    } else if (block.type === "table") {
      ensureRoom(30);
      autoTable(doc, {
        startY: cursorY,
        margin: { left: margin, right: margin },
        head: [block.head],
        body: block.rows,
        styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
        headStyles: { fillColor: [40, 40, 60] },
        theme: "grid",
      });
      cursorY = (doc as any).lastAutoTable.finalY + 16;
    }
  }

  doc.save(filename);
}

/** Flattens every table in a markdown artifact into one Excel-friendly CSV,
 * grouped under the nearest preceding heading (prose in between is dropped —
 * Excel wants rows/columns, not paragraphs). */
function markdownToSectionedCsv(markdown: string, docTitle: string): string {
  const blocks = parseMarkdownBlocks(markdown);
  const csvEscape = (value: string): string => {
    const escaped = value.replace(/"/g, '""');
    return /[",\n]/.test(value) ? `"${escaped}"` : escaped;
  };
  const outRows: string[] = [];
  let currentHeading = docTitle;
  for (const block of blocks) {
    if (block.type === "heading" && block.level <= 3) {
      currentHeading = block.text;
    } else if (block.type === "table") {
      outRows.push(csvEscape(currentHeading));
      outRows.push(block.head.map(csvEscape).join(","));
      for (const row of block.rows) {
        outRows.push(row.map(csvEscape).join(","));
      }
      outRows.push("");
    }
  }
  return outRows.join("\r\n");
}

/* ═══════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════ */
interface Props {
  onBack: () => void;
  projectId?: string;
  resumeSessionId?: string;
}

export default function SemanticModelCreatorView({
  onBack,
  projectId,
  resumeSessionId,
}: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ── Step & loading ── */
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ── Source selection ── */
  const [sourceType, setSourceType] = useState<"file" | "db">("file");
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── ER diagram export (holds the last rendered SVG for PNG/SVG download) ── */
  const erDiagramSvgRef = useRef<string>("");
  const [erDiagramDownloading, setErDiagramDownloading] = useState<
    "png" | "svg" | null
  >(null);

  /* ── Markdown artifact export (Deployment Guide / Business Glossary as PDF/Excel) ── */
  const [exportingArtifact, setExportingArtifact] = useState<string | null>(
    null,
  );

  /* ── Session & profile ── */
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [profile, setProfile] = useState<DatasetProfile | null>(null);
  const [preview, setPreview] = useState<Record<string, unknown>[]>([]);

  /* ── Multi-sheet state ── */
  const [isMultiSheet, setIsMultiSheet] = useState(false);
  const [pendingSheets, setPendingSheets] = useState<SheetInfo[]>([]);
  const [pendingFileName, setPendingFileName] = useState<string>("");
  const [selectedSheets, setSelectedSheets] = useState<Set<string>>(new Set());
  const [sheetContexts, setSheetContexts] = useState<Record<string, string>>(
    {},
  );

  /* ── PK / FK per sheet ── */
  const [sheetKeys, setSheetKeys] = useState<Record<string, SheetKeyDef>>({});

  /* ── Context fingerprint for caching ── */
  const [datasetFingerprint, setDatasetFingerprint] = useState<string>("");

  /* ── Column metadata / data dictionary (step 1.7) ── */
  const [metadataParsed, setMetadataParsed] = useState<
    ParsedColumnMeta[] | null
  >(null);
  const [metadataUploading, setMetadataUploading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  /* ── Existing Data Profiler run reuse offer ── */
  const [existingProfileOffer, setExistingProfileOffer] = useState<{
    run_id: string;
    source_name?: string;
    created_at?: string;
    table_count?: number;
    column_count?: number;
    row_count?: number;
    aggregate_score?: number;
  } | null>(null);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);

  /* ── Dataset context (pre-profile) ── */
  const [pendingUploadResult, setPendingUploadResult] =
    useState<UploadResponse | null>(null);
  const [datasetContext, setDatasetContext] = useState({
    domain: "",
    domainCustom: "",
    rowMeaning: "",
    purpose: "",
    consumers: "",
    businessRules: "",
    relatedSystems: "",
    specialRequirements: "",
    knowledgeSkillSlug: "",
  });

  /* ── Available Knowledge Base Skills (functional-domain packs) ── */
  const [knowledgeSkills, setKnowledgeSkills] = useState<
    { slug: string; label: string; description?: string }[]
  >([]);
  useEffect(() => {
    api
      .getKnowledgeSkills()
      .then((res) => setKnowledgeSkills(res.skills))
      .catch(() => setKnowledgeSkills([]));
  }, []);

  /** Column/sheet names profiled so far — grounds the Business Domain
      suggestions (see ``buildDatasetAwareContext``) and the Dataset
      Context step's smart placeholders. Single source of truth for both
      the Step 1.7 domain picker and the Step 1.8 review fields. */
  const ctxColumns = useMemo(() => {
    const cols: string[] =
      pendingSheets.length > 0
        ? pendingSheets
            .filter((s) => selectedSheets.has(s.name))
            .flatMap((s) => s.column_names)
        : (pendingUploadResult?.profile?.schema?.map(
            (c: ColumnProfile) => c.column,
          ) ?? []);
    return [...new Set(cols)];
  }, [pendingSheets, selectedSheets, pendingUploadResult]);
  const ctxSheets = useMemo(
    () =>
      pendingSheets.length > 0
        ? pendingSheets
            .filter((s) => selectedSheets.has(s.name))
            .map((s) => s.name)
        : [],
    [pendingSheets, selectedSheets],
  );

  /** All selectable Business Domain labels: the static industry list plus
      any functional-skill pack that doesn't already correspond to one of
      them (e.g. "Higher Education") — one picker drives both the Q&A
      template text and column-vocabulary matching. */
  const mergedDomainOptions = useMemo(() => {
    const covered = new Set(DOMAIN_OPTIONS.map(slugify));
    const extra = knowledgeSkills
      .filter((s) => !covered.has(s.slug))
      .map((s) => s.label);
    return [...DOMAIN_OPTIONS, ...extra];
  }, [knowledgeSkills]);

  const skillSlugForDomain = useCallback(
    (label: string) =>
      knowledgeSkills.find((s) => s.slug === slugify(label))?.slug ?? "",
    [knowledgeSkills],
  );

  /** Selecting a Business Domain fills any field that's still blank or
      still holds the previous domain's (unedited) suggestion — never
      overwrites text the user actually typed — and auto-resolves the
      matching knowledge-base skill pack (if one exists) for column-level
      vocabulary matching. */
  const applyBusinessDomain = useCallback(
    (nextDomain: string) => {
      setDatasetContext((prev) => {
        const prevSuggestion = buildDatasetAwareContext(
          prev.domain,
          ctxColumns,
          ctxSheets,
        );
        const nextSuggestion = buildDatasetAwareContext(
          nextDomain,
          ctxColumns,
          ctxSheets,
        );
        const updated = {
          ...prev,
          domain: nextDomain,
          knowledgeSkillSlug: nextDomain ? skillSlugForDomain(nextDomain) : "",
        };
        (
          [
            "rowMeaning",
            "purpose",
            "consumers",
            "businessRules",
            "specialRequirements",
          ] as const
        ).forEach((field) => {
          const current = prev[field];
          const wasSuggested = !current || current === prevSuggestion[field];
          if (!wasSuggested) return;
          updated[field] = nextSuggestion[field] ?? "";
        });
        return updated;
      });
    },
    [ctxColumns, ctxSheets, skillSlugForDomain],
  );

  /* ── Chat ── */
  const [chatMessages, setChatMessages] = useState<ChatEntry[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  /* ── Q&A Form (step 3) ── */
  const [qaAnswers, setQaAnswers] = useState<QAFormState>({});
  const [qaQuestions, setQaQuestions] = useState<ProfileQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [memoryAnswers, setMemoryAnswers] = useState<Record<string, string>>(
    {},
  );
  const [qaLoading, setQaLoading] = useState(false);
  const qaInitializedRef = useRef(false);
  const [qaConfirmed, setQaConfirmed] = useState(false);
  const [qaSummary, setQaSummary] = useState("");
  const [qaFollowupLoading, setQaFollowupLoading] = useState(false);
  const qaFileInputRef = useRef<HTMLInputElement>(null);
  const [qaImportError, setQaImportError] = useState<string | null>(null);
  const [qaDownloading, setQaDownloading] = useState(false);

  const handleDownloadQaAnswers = async () => {
    const safeName = (pendingFileName || "dataset").replace(
      /[^a-z0-9-_]+/gi,
      "_",
    );
    setQaDownloading(true);
    try {
      const blob = await buildQaAnswersWorkbook(qaQuestions, qaAnswers);
      await saveBlobFile(blob, `analytical-requirements-${safeName}.xlsx`);
    } catch (err) {
      if (!isAbortError(err)) {
        setQaImportError(
          err instanceof Error ? err.message : "Failed to download answers.",
        );
      }
    } finally {
      setQaDownloading(false);
    }
  };

  const handleImportQaClick = () => {
    setQaImportError(null);
    qaFileInputRef.current?.click();
  };

  const handleImportQaFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;
    try {
      const imported = await parseQaAnswersWorkbook(file, qaQuestions);
      if (Object.keys(imported).length === 0) {
        throw new Error(
          "No answers found — use the downloaded workbook's own ID/Question columns.",
        );
      }
      setQaAnswers((prev) => ({ ...prev, ...imported }));
      setQaImportError(null);
    } catch (err) {
      setQaImportError(
        err instanceof Error
          ? `Import failed: ${err.message}`
          : "Import failed: invalid file.",
      );
    }
  };
  const [panelCollapsed, setPanelCollapsed] = useState<Record<string, boolean>>(
    {
      overview: false,
      dq: false,
      types: false,
    },
  );
  const [profileSidebarOpen, setProfileSidebarOpen] = useState(false);

  /* ── Semantic view ── */
  const [semanticView, setSemanticView] = useState<SemanticViewResponse | null>(
    null,
  );
  const [svLoading, setSvLoading] = useState(false);

  /* ── Target model ── */
  const [targetModel, setTargetModel] = useState<TargetModelResponse | null>(
    null,
  );
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [targetSchema, setTargetSchema] = useState("");
  const [targetLoading, setTargetLoading] = useState(false);

  /* ── Documentation ── */
  const [docError, setDocError] = useState<string | null>(null);
  const [showOutline, setShowOutline] = useState(true);

  /* ── Target model artifact downloads ── */
  const [downloadingArtifact, setDownloadingArtifact] = useState<string | null>(
    null,
  );
  const [downloadingAllArtifacts, setDownloadingAllArtifacts] = useState(false);

  /* ── Review & Refine (step 4.5) ── */
  const [refineMessages, setRefineMessages] = useState<ChatEntry[]>([]);
  const [refineInput, setRefineInput] = useState("");
  const [refineLoading, setRefineLoading] = useState(false);
  const refineBottomRef = useRef<HTMLDivElement>(null);
  const refineInputRef = useRef<HTMLTextAreaElement>(null);

  /* ── "Current Semantic View" table search/filter (step 4.5) ── */
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [entitySearch, setEntitySearch] = useState("");

  /* ── Add-entity form (step 4.5) — target-only tables with no source
      counterpart (deterministic, no LLM). ── */
  const [addingEntity, setAddingEntity] = useState(false);
  const [newEntityDraft, setNewEntityDraft] = useState({
    name: "",
    entity_type: "",
    description: "",
  });
  const [addEntityBusy, setAddEntityBusy] = useState(false);
  const [addEntityError, setAddEntityError] = useState<string | null>(null);

  const addEntity = useCallback(async () => {
    if (!sessionId || !newEntityDraft.name.trim()) return;
    setAddEntityBusy(true);
    setAddEntityError(null);
    try {
      const res = await api.addSemanticEntity(sessionId, newEntityDraft.name, {
        entity_type: newEntityDraft.entity_type || undefined,
        description: newEntityDraft.description || undefined,
      });
      const wrapper = (res as { semantic_view?: unknown }).semantic_view;
      if (wrapper) {
        setSemanticView((prev: any) =>
          prev
            ? { ...prev, semantic_view: wrapper }
            : { semantic_view: wrapper },
        );
      }
      setNewEntityDraft({ name: "", entity_type: "", description: "" });
      setAddingEntity(false);
    } catch (err: unknown) {
      setAddEntityError(
        err instanceof Error ? err.message : "Add entity failed",
      );
    } finally {
      setAddEntityBusy(false);
    }
  }, [sessionId, newEntityDraft]);

  /* Shared "+ Add entity" button/form — rendered at both entity-list call
     sites (step 4 Entity Details tab and step 4.5 Review & Refine panel)
     so a target-only table can be added from wherever the model is viewed. */
  const addEntityFormUI = (
    <div className="glass rounded-xl p-3">
      {!addingEntity ? (
        <button
          onClick={() => setAddingEntity(true)}
          className="flex items-center gap-1.5 text-[11px] text-brand-400 hover:text-brand-300 transition-colors"
        >
          <Plus size={12} /> Add entity
        </button>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <input
              value={newEntityDraft.name}
              onChange={(e) =>
                setNewEntityDraft((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="Entity name (technical)"
              className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
            />
            <select
              value={newEntityDraft.entity_type}
              onChange={(e) =>
                setNewEntityDraft((p) => ({
                  ...p,
                  entity_type: e.target.value,
                }))
              }
              className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
            >
              <option value="">Entity type…</option>
              {ENTITY_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <input
            value={newEntityDraft.description}
            onChange={(e) =>
              setNewEntityDraft((p) => ({
                ...p,
                description: e.target.value,
              }))
            }
            placeholder="Description (optional)"
            className="w-full px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => void addEntity()}
              disabled={addEntityBusy || !newEntityDraft.name.trim()}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white transition-colors"
            >
              {addEntityBusy ? "Adding…" : "Add"}
            </button>
            <button
              onClick={() => {
                setAddingEntity(false);
                setAddEntityError(null);
              }}
              className="text-[11px] text-white/40 hover:text-white/70 transition-colors"
            >
              Cancel
            </button>
            {addEntityError && (
              <span className="text-[11px] text-red-400">{addEntityError}</span>
            )}
          </div>
          <p className="text-[11px] text-white/25">
            Starts empty — use "Add column" on the new entity card below to
            populate it.
          </p>
        </div>
      )}
    </div>
  );

  /* ── Refine-panel mode: chat to edit the model vs. ask NL questions ── */
  const [refinePanelMode, setRefinePanelMode] = useState<"refine" | "ask">(
    "refine",
  );
  const [qaChatMessages, setQaChatMessages] = useState<ChatEntry[]>([]);
  const [qaChatInput, setQaChatInput] = useState("");
  const [qaChatLoading, setQaChatLoading] = useState(false);
  /* `null` = not checked yet; checked proactively as soon as step 4.5 loads
     so the Conversational-AI-handover / deterministic-edit choice can be
     offered up front instead of only after a failed message. Also kept in
     sync reactively from ask/refine responses (both report unavailability
     via a boolean flag) in case the proactive check was stale. */
  const [llmAvailable, setLlmAvailable] = useState<boolean | null>(null);
  const [showDeterministicGuide, setShowDeterministicGuide] = useState(false);

  /* ── Relationship editor (step 4.5) ── */
  const [relEditing, setRelEditing] = useState(false);
  const [relDraft, setRelDraft] = useState<SemanticRelationship[]>([]);
  const [relSaving, setRelSaving] = useState(false);
  const [relError, setRelError] = useState<string | null>(null);

  /* ── ER canvas: name of the entity currently open in the click-to-edit
      modal (null when the modal is closed). ── */
  const [canvasEditEntityName, setCanvasEditEntityName] = useState<
    string | null
  >(null);

  /* ── Model overview stat-card filter (step 4) — which stat's detail panel
      is expanded below the stats row; null hides the panel entirely so a
      large model doesn't force a long initial scroll. ── */
  const [overviewTab, setOverviewTab] = useState<
    | "entities"
    | "facts"
    | "dimensions"
    | "columns"
    | "measures"
    | "relationships"
    | null
  >(null);

  /* ── Step 4 top-level view — Overview / Business Glossary / ER Diagram
      are mutually-exclusive tabs rather than stacked collapsible sections. ── */
  const [modelViewTab, setModelViewTab] = useState<
    "overview" | "glossary" | "erd" | "canvas" | "metrics"
  >("overview");

  /* ── Session snapshots (step 4.5) ── */
  const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotLabel, setSnapshotLabel] = useState("");
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotRestoring, setSnapshotRestoring] = useState<string | null>(
    null,
  );
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  /* ── Snapshot diff (compare two snapshots before restoring) ── */
  const [diffFromId, setDiffFromId] = useState<string | null>(null);
  const [diffToId, setDiffToId] = useState<string | null>(null);
  const [diffResult, setDiffResult] = useState<SnapshotDiffResponse | null>(
    null,
  );
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  /* ── Modeling intelligence (FK suggestions / warnings / SCD) ── */
  const [modelingInsights, setModelingInsights] =
    useState<ModelingInsightsResponse | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  /* ── Semantic-layer export (dbt / LookML / Cube.dev) ── */
  const [exportingFormat, setExportingFormat] =
    useState<SemanticLayerExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  /* ── HITL approval gate (step 6, before deploy) ── */
  const [approval, setApproval] = useState<ApprovalState | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [approvalError, setApprovalError] = useState<string | null>(null);

  /* ── Guardian masking suggestions (step 6) ── */
  const [maskingPlan, setMaskingPlan] = useState<MaskingPlanItem[] | null>(
    null,
  );
  const [maskingLoading, setMaskingLoading] = useState(false);
  const [maskingApplying, setMaskingApplying] = useState<string | null>(null);
  const [maskingError, setMaskingError] = useState<string | null>(null);
  const [maskingApplied, setMaskingApplied] = useState<Record<string, boolean>>(
    {},
  );

  /* ── DDL validation (step 6) ── */
  const [ddlValidation, setDdlValidation] =
    useState<DdlValidationResponse | null>(null);
  const [ddlValidating, setDdlValidating] = useState(false);
  const [ddlError, setDdlError] = useState<string | null>(null);

  /* ── Cost estimation (step 6) ── */
  const [costEstimate, setCostEstimate] = useState<CostEstimateResponse | null>(
    null,
  );
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  /* ── Lineage capture (step 6) ── */
  const [lineageResult, setLineageResult] =
    useState<CaptureLineageResponse | null>(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [lineageError, setLineageError] = useState<string | null>(null);

  /* ── Catalog export (step 6) ── */
  const [catalogEndpoint, setCatalogEndpoint] = useState("");
  const [catalogApiKey, setCatalogApiKey] = useState("");
  const [catalogExporting, setCatalogExporting] = useState(false);
  const [catalogResult, setCatalogResult] =
    useState<CatalogExportResponse | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  /* ── Validation Gate (step 4.7) ── */
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);

  /* ── Naming standards (global conventions enforcement) ── */
  const [namingStandardsLoading, setNamingStandardsLoading] = useState(false);
  const [namingStandardsReport, setNamingStandardsReport] =
    useState<NamingStandardsReport | null>(null);
  const [namingStandardsError, setNamingStandardsError] = useState<
    string | null
  >(null);

  /* ── Metric definitions (metric-definition layer) ── */
  const [addingMetric, setAddingMetric] = useState(false);
  const [newMetricDraft, setNewMetricDraft] = useState({
    name: "",
    entity: "",
    expression: "",
    aggregation: "sum",
    description: "",
  });
  const [addMetricBusy, setAddMetricBusy] = useState(false);
  const [addMetricError, setAddMetricError] = useState<string | null>(null);
  const [deletingMetric, setDeletingMetric] = useState<string | null>(null);

  /* ── Validation Gate: inline quick-fix (no navigation to Refine) ── */
  const [quickFixOpen, setQuickFixOpen] = useState<Set<string>>(new Set());
  const [quickFixValues, setQuickFixValues] = useState<Record<string, string>>(
    {},
  );
  const [quickFixSaving, setQuickFixSaving] = useState<Set<string>>(new Set());
  const [quickFixSaved, setQuickFixSaved] = useState<Set<string>>(new Set());

  /* ── PK/FK LLM Suggestions ── */
  const [keySuggestions, setKeySuggestions] =
    useState<KeySuggestionsResponse | null>(null);
  const [keySuggestLoading, setKeySuggestLoading] = useState(false);

  /* ── Deployment ──
     Deploy and reconcile both connect to the target DB via the same shared
     ConnectDatabaseDialog every agent uses (kind="target") instead of a
     bespoke raw-credential form — credentials are resolved server-side
     from the project's saved connections (or a fresh manual connect that
     gets saved for reuse), never re-entered per action. */
  const [expanded, setExpanded] = useState<string | null>("deployment_guide");
  const [showDeploy, setShowDeploy] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState<any>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deploySchemaName, setDeploySchemaName] = useState("");
  const [showDeployConnectDialog, setShowDeployConnectDialog] = useState(false);
  const [deployDialogAction, setDeployDialogAction] = useState<
    "deploy" | "reconcile"
  >("deploy");
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] =
    useState<ReconcileDeploymentResponse | null>(null);
  const [reconcileError, setReconcileError] = useState<string | null>(null);

  /* ── DB Connect state ── */
  const [dbForm, setDbForm] = useState<DatabaseConnectionParams>({
    db_type: "snowflake",
    host: "",
    port: undefined,
    database: "",
    schema_name: "",
    username: "",
    password: "",
    account: "",
    warehouse: "",
    role: "",
    table_or_query: "",
  });
  const [dbConnecting, setDbConnecting] = useState(false);

  /* ── Project DB connection state ── */
  const [projectDbConfig, setProjectDbConfig] = useState<{
    id: number;
    db_type: string;
    host?: string;
    port?: number;
    database?: string;
    schema_name?: string;
    username?: string;
    has_password: boolean;
    account?: string;
    warehouse?: string;
    role?: string;
  } | null>(null);
  const [projectDbTables, setProjectDbTables] = useState<string[]>([]);
  const [projectDbViews, setProjectDbViews] = useState<string[]>([]);
  const [projectDbLoading, setProjectDbLoading] = useState(false);
  const [selectedProjectTable, setSelectedProjectTable] = useState("");
  const [tableSearchQuery, setTableSearchQuery] = useState("");

  /* ── Shared Connect Database dialog: table picker (step 1.6) ──
     Populated after a blank "Table or Query" connect via the shared
     dialog, discovered but not yet profiled — avoids eagerly profiling
     every table in the schema before the user has chosen any. */
  const [sharedDbTables, setSharedDbTables] = useState<string[]>([]);
  const [sharedDbViews, setSharedDbViews] = useState<string[]>([]);
  const [selectedSharedDbTables, setSelectedSharedDbTables] = useState<
    Set<string>
  >(new Set());
  const [sharedDbTableSearch, setSharedDbTableSearch] = useState("");
  const [profilingSharedDbTables, setProfilingSharedDbTables] = useState(false);
  // The primary source's schema (only set once the user browses a
  // different schema than the one connected to — see handleSchemaBrowse).
  const [primaryDbSchema, setPrimaryDbSchema] = useState<string | undefined>(
    undefined,
  );
  // Additional source databases/schemas registered via `addSemanticSource`
  // (AG-SEMANTIC multi-source) — each contributes its own tables/views to
  // the picker below, tagged with its own `sourceId` so `select_tables` can
  // pull from more than one connection into a single target model.
  const [additionalDbSources, setAdditionalDbSources] = useState<
    Array<{
      sourceId: string;
      label: string;
      schema?: string;
      tables: string[];
      views: string[];
    }>
  >([]);
  const [showAddSourceDialog, setShowAddSourceDialog] = useState(false);
  const [addSourceLabel, setAddSourceLabel] = useState("");
  // Inline "browse a different schema" mini-form — `null` sourceId means
  // no browser is open; "primary" targets the primary connection.
  const [schemaBrowseSourceId, setSchemaBrowseSourceId] = useState<
    string | null
  >(null);
  const [schemaBrowseValue, setSchemaBrowseValue] = useState("");
  const [schemaBrowseLoading, setSchemaBrowseLoading] = useState(false);
  const [schemaBrowseError, setSchemaBrowseError] = useState<string | null>(
    null,
  );
  // Schema names available on the source being browsed, fetched from
  // listSemanticSchemas — populates the schema dropdown so users pick from
  // real schemas instead of typing a name.
  const [schemaOptions, setSchemaOptions] = useState<string[]>([]);
  const [schemaOptionsLoading, setSchemaOptionsLoading] = useState(false);
  const [schemaOptionsError, setSchemaOptionsError] = useState<string | null>(
    null,
  );
  // Rows sampled per table (mirrors the Data Profiler agent's configurable
  // sample size — same SAMPLE_PRESETS, incl. "All" — never a full scan
  // unless "All" is picked). Defaults to the same 10K preset Data Profiler
  // defaults to, for consistency across agents.
  const [sharedDbSampleSize, setSharedDbSampleSize] = useState<number | null>(
    10_000,
  );
  const [sharedDbProgress, setSharedDbProgress] = useState<{
    tablesDone: number;
    tablesTotal: number;
    currentTable: string | null;
  } | null>(null);

  // Composite selection key ("<source_id>::<table>") — lets the same table
  // name be selected independently from two different sources without
  // colliding (disambiguation happens server-side too, via a "__<source_id>"
  // suffix only when names actually collide across the chosen sources).
  const sdKey = useCallback(
    (sourceId: string, table: string) => `${sourceId}::${table}`,
    [],
  );

  /* ── Profile view state ── */
  const [activeSheet, setActiveSheet] = useState<string>("all");
  const [sheetDropdownOpen, setSheetDropdownOpen] = useState(false);
  const [collapsedSheets, setCollapsedSheets] = useState<Set<string>>(
    new Set(),
  );

  /* ── Resume existing session ── */
  useEffect(() => {
    if (!resumeSessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getSession(resumeSessionId)
      .then((res: any) => {
        if (cancelled) return;
        setSessionId(res.session_id || resumeSessionId);
        if (res.source_name)
          setSourceType(res.source_type === "database" ? "db" : "file");
        if (res.profile) {
          setProfile(res.profile);
          setPreview(res.preview || []);
        }
        // Restore chat history
        if (
          res.chat_history &&
          Array.isArray(res.chat_history) &&
          res.chat_history.length > 0
        ) {
          setChatMessages(res.chat_history);
        }
        // Restore Q&A questions + answers if available
        if (
          res.profile_questions &&
          Array.isArray(res.profile_questions) &&
          res.profile_questions.length > 0
        ) {
          setQaQuestions(res.profile_questions);
          qaInitializedRef.current = true; // Prevent Q&A useEffect from re-fetching
          // Pre-fill answers from suggested_answer or previous business_context
          const prefilled: Record<string, string> = {};
          res.profile_questions.forEach((q: any) => {
            if (q.suggested_answer) prefilled[q.id] = q.suggested_answer;
          });
          setQaAnswers(prefilled);
        }
        // Determine which step to resume at
        if (res.semantic_view) {
          setSemanticView(res.semantic_view);
          setStep(4); // Jump to semantic view step
        } else if (res.profile) {
          setStep(3); // Jump to Q&A step
        } else {
          setStep(2); // Jump to profile step
        }
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message || "Failed to load session");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeSessionId]);

  /* ── Fetch project DB config if available ── */
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    import("../apiv2").then(({ apiv2 }) => {
      apiv2
        .getProject(projectId)
        .then((project: any) => {
          if (cancelled) return;
          if (project?.source_db_config) {
            setProjectDbConfig(project.source_db_config);
          }
        })
        .catch(() => {
          /* no project DB config — user will connect via the dialog */
        });
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  /* ── Proactive LLM-availability check for the Ask/Refine panels — run
      once a session exists so the choice can be shown up front. ── */
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    api
      .checkLlmAvailability(sessionId)
      .then((res) => {
        if (!cancelled) setLlmAvailable(res.available);
      })
      .catch(() => {
        if (!cancelled) setLlmAvailable(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  /* ═══════════════════════════════════════════
     Handlers
     ═══════════════════════════════════════════ */

  /* ── Fetch tables/views from project DB connection ── */
  const handleFetchProjectDbTables = useCallback(async () => {
    if (!projectId) return;
    setProjectDbLoading(true);
    setError(null);
    try {
      const res = await api.metadataProjectDbTables(projectId, "source");
      setProjectDbTables(res.tables || []);
      setProjectDbViews(res.views || []);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch tables from project database",
      );
    } finally {
      setProjectDbLoading(false);
    }
  }, [projectId]);

  /* ── Connect & profile using project DB connection ── */
  const handleProjectDbConnect = useCallback(async () => {
    if (!projectId || !selectedProjectTable) return;
    setError(null);
    setDbConnecting(true);
    try {
      const result = await api.connectDatabaseFromProject(
        projectId,
        selectedProjectTable,
      );
      setPendingUploadResult(result);
      setSessionId(result.session_id);
      setStep(1.7); // Dataset context step
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setDbConnecting(false);
    }
  }, [projectId, selectedProjectTable]);

  /* ── File upload ── */
  const applyUploadResult = useCallback(
    (result: UploadResult, fileName: string) => {
      if (isSheetSelection(result)) {
        setSessionId(result.session_id);
        setPendingSheets(result.sheets);
        setPendingFileName(result.file_name);
        setSelectedSheets(new Set(result.sheets.map((s) => s.name)));
        setIsMultiSheet(true);
        // Initialize contexts & keys for each sheet
        const contexts: Record<string, string> = {};
        const keys: Record<string, SheetKeyDef> = {};
        const allCols = result.sheets.flatMap((s) => s.column_names);
        const fp = buildFingerprint(result.file_name, allCols);
        setDatasetFingerprint(fp);
        const cached = loadCachedContext(fp);
        result.sheets.forEach((s) => {
          contexts[s.name] = cached?.sheetContexts?.[s.name] ?? "";
          keys[s.name] = cached?.sheetKeys?.[s.name] ?? {
            primaryKey: "",
            alternateKeys: [],
            foreignKeys: [],
          };
        });
        setSheetContexts(contexts);
        setSheetKeys(keys);
        if (cached?.datasetContext) setDatasetContext(cached.datasetContext);
        setStep(1.5); // Sheet selection sub-step
      } else {
        setPendingUploadResult(result);
        setSessionId(result.session_id);
        // Build fingerprint from profile columns
        const cols =
          result.profile?.schema?.map((c: any) => c.column ?? c.name) ?? [];
        const fp = buildFingerprint(fileName, cols);
        setDatasetFingerprint(fp);
        const cached = loadCachedContext(fp);
        if (cached?.datasetContext) setDatasetContext(cached.datasetContext);
        setStep(1.7); // Dataset context step
      }
    },
    [],
  );

  const uploadAndApply = useCallback(
    async (file: File) => {
      setError(null);
      setLoading(true);
      try {
        const result = await api.uploadFile(file, projectId);
        applyUploadResult(result, file.name);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Upload failed");
      } finally {
        setLoading(false);
      }
    },
    [projectId, applyUploadResult],
  );

  /* Common entry point for both the file-picker input and drag-and-drop:
   * checks whether the Data Profiler agent already has a completed run for
   * this exact file name in this project, and if so offers to reuse it
   * instead of silently re-profiling from scratch (see CLAUDE.md handoff
   * design notes). Falls back to a normal upload if the check fails or no
   * existing run is found. */
  const handleFileSelected = useCallback(
    async (file: File) => {
      setError(null);
      if (projectId) {
        try {
          const existing = await api.checkExistingProfile(projectId, file.name);
          if (existing.found && existing.run_id) {
            setExistingProfileOffer({
              run_id: existing.run_id,
              source_name: existing.source_name,
              created_at: existing.created_at,
              table_count: existing.table_count,
              column_count: existing.column_count,
              row_count: existing.row_count,
              aggregate_score: existing.aggregate_score,
            });
            setPendingUploadFile(file);
            return;
          }
        } catch {
          // Best-effort — fall through to a normal upload.
        }
      }
      await uploadAndApply(file);
    },
    [projectId, uploadAndApply],
  );

  const handleUseExistingProfile = useCallback(async () => {
    if (!existingProfileOffer || !projectId) return;
    setError(null);
    setLoading(true);
    try {
      const result = await api.importProfilerRun(
        projectId,
        existingProfileOffer.run_id,
      );
      applyUploadResult(
        result,
        pendingUploadFile?.name ??
          existingProfileOffer.source_name ??
          "profiled_source",
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to import the existing profile",
      );
    } finally {
      setLoading(false);
      setExistingProfileOffer(null);
      setPendingUploadFile(null);
    }
  }, [existingProfileOffer, pendingUploadFile, projectId, applyUploadResult]);

  const handleReprofileAnyway = useCallback(async () => {
    const file = pendingUploadFile;
    setExistingProfileOffer(null);
    setPendingUploadFile(null);
    if (file) await uploadAndApply(file);
  }, [pendingUploadFile, uploadAndApply]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await handleFileSelected(file);
      if (e.target) e.target.value = "";
    },
    [handleFileSelected],
  );

  /* ── Drag & drop ── */
  const onDrop = useCallback(
    async (accepted: File[]) => {
      if (accepted.length === 0) return;
      await handleFileSelected(accepted[0]);
    },
    [handleFileSelected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "text/csv": [".csv", ".tsv", ".txt"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "application/json": [".json", ".jsonl"],
      "application/octet-stream": [".parquet"],
    },
  });

  /* ── Sheet selection + context → process ── */
  const handleProcessSheets = useCallback(async () => {
    if (!sessionId || selectedSheets.size === 0) return;
    setLoading(true);
    setError(null);
    try {
      const sheetsArray = Array.from(selectedSheets);
      // Build context map for selected sheets only
      const contextMap: Record<string, string> = {};
      sheetsArray.forEach((s) => {
        if (sheetContexts[s]?.trim()) {
          contextMap[s] = sheetContexts[s].trim();
        }
      });
      // Build PK/FK map for selected sheets (including alternate/conditional keys)
      const keysMap: Record<
        string,
        {
          primary_key: string;
          alternate_keys?: {
            column: string;
            condition_column: string;
            condition_value: string;
          }[];
          foreign_keys: {
            column: string;
            references_sheet: string;
            references_column: string;
          }[];
        }
      > = {};
      sheetsArray.forEach((s) => {
        const k = sheetKeys[s];
        if (
          k &&
          (k.primaryKey ||
            k.foreignKeys.length > 0 ||
            (k.alternateKeys && k.alternateKeys.length > 0))
        ) {
          keysMap[s] = {
            primary_key: k.primaryKey,
            alternate_keys: (k.alternateKeys || [])
              .filter(
                (ak) => ak.column && ak.conditionColumn && ak.conditionValue,
              )
              .map((ak) => ({
                column: ak.column,
                condition_column: ak.conditionColumn,
                condition_value: ak.conditionValue,
              })),
            foreign_keys: k.foreignKeys
              .filter(
                (fk) => fk.column && fk.referencesSheet && fk.referencesColumn,
              )
              .map((fk) => ({
                column: fk.column,
                references_sheet: fk.referencesSheet,
                references_column: fk.referencesColumn,
              })),
          };
        }
      });
      const result = await api.selectSheets(
        sessionId,
        sheetsArray,
        contextMap,
        Object.keys(keysMap).length > 0 ? keysMap : undefined,
        projectId,
      );
      setPendingUploadResult(result);
      setStep(1.7); // Dataset context step
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to process sheets");
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedSheets, sheetContexts, sheetKeys, projectId]);

  /* ── Suggest PK/FK via LLM ── */
  const handleSuggestKeys = useCallback(async () => {
    if (!sessionId) return;
    setKeySuggestLoading(true);
    try {
      const result = await api.suggestKeys(sessionId);
      setKeySuggestions(result);
      // Auto-apply suggestions to sheetKeys state
      if (result.suggestions) {
        setSheetKeys((prev) => {
          const updated = { ...prev };
          Object.entries(result.suggestions).forEach(([sheet, suggestion]) => {
            const existing = updated[sheet] || {
              primaryKey: "",
              alternateKeys: [],
              foreignKeys: [],
            };
            // Only set PK if user hasn't already set one
            if (!existing.primaryKey && suggestion.primary_key) {
              existing.primaryKey = suggestion.primary_key;
            }
            // Add suggested FKs that aren't already present
            if (suggestion.foreign_keys) {
              suggestion.foreign_keys.forEach((fk) => {
                const alreadyExists = existing.foreignKeys.some(
                  (efk) =>
                    efk.column === fk.column &&
                    efk.referencesSheet === fk.references_sheet,
                );
                if (!alreadyExists && fk.confidence >= 0.6) {
                  existing.foreignKeys.push({
                    column: fk.column,
                    referencesSheet: fk.references_sheet,
                    referencesColumn: fk.references_column,
                  });
                }
              });
            }
            updated[sheet] = existing;
          });
          return updated;
        });
      }
    } catch (err) {
      console.error("Key suggestion failed:", err);
    } finally {
      setKeySuggestLoading(false);
    }
  }, [sessionId]);

  /* ── Data ready: go straight to Q&A ── */
  const handleDataReady = useCallback(async (res: UploadResponse) => {
    setSessionId(res.session_id);
    setProfile(res.profile);
    setPreview(res.preview);
    setLoading(false);
    setStep(3);
  }, []);

  /* ── Connect Database dialog (shared component every agent uses) ── */
  // Primary entry point for connecting a source database: reuses a saved
  // connection ANY agent stored for this project, or opens a fresh
  // credentials form via the shared `ConnectDatabaseDialog`. Kept alongside
  // the project-connection card above, which implements the deliberate
  // seamless-handoff continuity (e.g. Profiler → Semantic) this platform is
  // building toward, plus a table/view browser the generic dialog doesn't
  // replicate.
  const [showSharedConnectDialog, setShowSharedConnectDialog] = useState(false);

  const handleSharedDialogConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      const table = (dbForm.table_or_query || "").trim();
      try {
        if (!table) {
          // No specific table/query given — discover only, then let the
          // user pick which table(s) to actually profile (step 1.6)
          // instead of eagerly profiling the whole schema.
          const discovered = await api.connectDatabaseDiscover({
            db_type: values.db_type,
            host: values.host,
            port: parseInt(values.port, 10),
            database: values.database,
            schema_name: values.schema_name,
            username: values.username,
            password: values.password,
            account: values.account,
            warehouse: values.warehouse,
            role: values.role,
            project_id: projectId,
          });
          setSessionId(discovered.session_id);
          setSharedDbTables(discovered.tables || []);
          setSharedDbViews(discovered.views || []);
          setSelectedSharedDbTables(new Set());
          setPrimaryDbSchema(undefined);
          setAdditionalDbSources([]);
          setStep(1.6);
          return { ok: true };
        }
        const result = await api.connectDatabase({
          db_type: values.db_type,
          host: values.host,
          port: parseInt(values.port, 10),
          database: values.database,
          schema_name: values.schema_name,
          username: values.username,
          password: values.password,
          account: values.account,
          warehouse: values.warehouse,
          role: values.role,
          table_or_query: table,
          project_id: projectId,
        });
        setPendingUploadResult(result);
        setSessionId(result.session_id);
        setStep(1.7);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [dbForm.table_or_query, projectId],
  );

  const handleSharedDialogConnectExisting = useCallback(
    async (connectionId: string): Promise<ConnectResult> => {
      const table = (dbForm.table_or_query || "").trim();
      try {
        if (!table) {
          const discovered = await api.connectDatabaseDiscover({
            connection_id: connectionId,
            project_id: projectId,
          });
          setSessionId(discovered.session_id);
          setSharedDbTables(discovered.tables || []);
          setSharedDbViews(discovered.views || []);
          setSelectedSharedDbTables(new Set());
          setPrimaryDbSchema(undefined);
          setAdditionalDbSources([]);
          setStep(1.6);
          return { ok: true };
        }
        const result = await api.connectDatabase({
          connection_id: connectionId,
          table_or_query: table,
          project_id: projectId,
        });
        setPendingUploadResult(result);
        setSessionId(result.session_id);
        setStep(1.7);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [dbForm.table_or_query, projectId],
  );

  /* ── Add source dialog (step 1.6 "+ Add another source") — registers an
     ADDITIONAL source database/schema on the SAME session, so tables from
     more than one connection can be merged into one target model. ── */
  const handleAddSourceDialogConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      if (!sessionId) return { ok: false, error: "No active session" };
      try {
        const added = await api.addSemanticSource(sessionId, {
          db_type: values.db_type,
          host: values.host,
          port: parseInt(values.port, 10),
          database: values.database,
          schema_name: values.schema_name,
          username: values.username,
          password: values.password,
          account: values.account,
          warehouse: values.warehouse,
          role: values.role,
          project_id: projectId,
          label: addSourceLabel || undefined,
        });
        setAdditionalDbSources((prev) => [
          ...prev,
          {
            sourceId: added.source_id,
            label: added.label,
            schema: added.schema,
            tables: added.tables || [],
            views: added.views || [],
          },
        ]);
        setAddSourceLabel("");
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [sessionId, projectId, addSourceLabel],
  );

  const handleAddSourceDialogConnectExisting = useCallback(
    async (connectionId: string, label?: string): Promise<ConnectResult> => {
      if (!sessionId) return { ok: false, error: "No active session" };
      try {
        const added = await api.addSemanticSource(sessionId, {
          connection_id: connectionId,
          project_id: projectId,
          label: addSourceLabel || label || undefined,
        });
        setAdditionalDbSources((prev) => [
          ...prev,
          {
            sourceId: added.source_id,
            label: added.label,
            schema: added.schema,
            tables: added.tables || [],
            views: added.views || [],
          },
        ]);
        setAddSourceLabel("");
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [sessionId, projectId, addSourceLabel],
  );

  /* ── Open the "browse a different schema" mini-form and fetch the
     available schema names for that source, so the field can be a
     dropdown instead of free text. ── */
  const handleOpenSchemaBrowser = useCallback(
    async (sourceId: string, currentSchema: string | undefined) => {
      setSchemaBrowseSourceId(sourceId);
      setSchemaBrowseValue(currentSchema || "");
      setSchemaBrowseError(null);
      setSchemaOptions([]);
      setSchemaOptionsError(null);
      if (!sessionId) return;
      setSchemaOptionsLoading(true);
      try {
        const targetSourceId = sourceId === "primary" ? null : sourceId;
        const result = await api.listSemanticSchemas(sessionId, targetSourceId);
        setSchemaOptions(result.schemas || []);
      } catch (err: unknown) {
        setSchemaOptionsError(
          err instanceof Error ? err.message : "Could not load schemas",
        );
      } finally {
        setSchemaOptionsLoading(false);
      }
    },
    [sessionId],
  );

  /* ── Browse a different schema on an already-connected source (primary
     or one of additionalDbSources) — replaces that source's table/view
     list in the picker; doesn't touch the current selection for other
     sources. ── */
  const handleSchemaBrowse = useCallback(async () => {
    if (!sessionId || !schemaBrowseSourceId || !schemaBrowseValue.trim())
      return;
    setSchemaBrowseLoading(true);
    setSchemaBrowseError(null);
    try {
      const targetSourceId =
        schemaBrowseSourceId === "primary" ? null : schemaBrowseSourceId;
      const result = await api.listSemanticSchemaTables(
        sessionId,
        schemaBrowseValue.trim(),
        targetSourceId,
      );
      if (schemaBrowseSourceId === "primary") {
        setPrimaryDbSchema(result.schema);
        setSharedDbTables(result.tables || []);
        setSharedDbViews(result.views || []);
        setSelectedSharedDbTables((prev) => {
          const next = new Set(prev);
          for (const key of prev) {
            if (key.startsWith("primary::")) next.delete(key);
          }
          return next;
        });
      } else {
        setAdditionalDbSources((prev) =>
          prev.map((s) =>
            s.sourceId === schemaBrowseSourceId
              ? {
                  ...s,
                  schema: result.schema,
                  tables: result.tables || [],
                  views: result.views || [],
                }
              : s,
          ),
        );
        setSelectedSharedDbTables((prev) => {
          const next = new Set(prev);
          for (const key of prev) {
            if (key.startsWith(`${schemaBrowseSourceId}::`)) next.delete(key);
          }
          return next;
        });
      }
      setSchemaBrowseSourceId(null);
      setSchemaBrowseValue("");
      setSchemaOptions([]);
      setSchemaOptionsError(null);
    } catch (err: unknown) {
      setSchemaBrowseError(
        err instanceof Error ? err.message : "Could not browse that schema",
      );
    } finally {
      setSchemaBrowseLoading(false);
    }
  }, [sessionId, schemaBrowseSourceId, schemaBrowseValue]);

  /* ── Confirm table picker (step 1.6): profile only the user's selection ── */
  const handleConfirmSharedDbTables = useCallback(async () => {
    if (!sessionId || selectedSharedDbTables.size === 0) return;
    setProfilingSharedDbTables(true);
    setSharedDbProgress({
      tablesDone: 0,
      tablesTotal: selectedSharedDbTables.size,
      currentTable: null,
    });
    setError(null);
    // Poll the live "N of M tables sampled" progress while the (blocking)
    // select-tables request below is in flight — the backend samples
    // tables concurrently and updates this after each one completes.
    const pollTimer = window.setInterval(async () => {
      try {
        const progress = await api.getSelectTablesProgress(sessionId);
        setSharedDbProgress({
          tablesDone: progress.tables_done,
          tablesTotal: progress.tables_total || selectedSharedDbTables.size,
          currentTable: progress.current_table,
        });
      } catch {
        // Best-effort — a poll failure shouldn't interrupt the main request.
      }
    }, 700);
    try {
      // Composite keys ("<source_id>::<table>") -> qualified selector
      // objects for every non-primary/non-default-schema table; a bare
      // primary-source table (the pre-multi-source common case) is still
      // sent as a plain string so single-source sessions get the exact
      // same request shape as before.
      const tables = Array.from(selectedSharedDbTables).map((key) => {
        const sep = key.indexOf("::");
        const sourceId = key.slice(0, sep);
        const table = key.slice(sep + 2);
        if (sourceId === "primary") {
          return primaryDbSchema ? { table, schema: primaryDbSchema } : table;
        }
        const src = additionalDbSources.find((s) => s.sourceId === sourceId);
        return { table, source_id: sourceId, schema: src?.schema };
      });
      const result = await api.selectDatabaseTables(
        sessionId,
        tables,
        sharedDbSampleSize,
      );
      setPendingUploadResult(result);
      setStep(1.7);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to apply semantic intelligence to selected tables",
      );
    } finally {
      window.clearInterval(pollTimer);
      setProfilingSharedDbTables(false);
      setSharedDbProgress(null);
    }
  }, [
    sessionId,
    selectedSharedDbTables,
    sharedDbSampleSize,
    additionalDbSources,
    primaryDbSchema,
  ]);

  /* ── Column metadata file upload (step 1.7) ── */
  const handleMetadataUpload = useCallback(
    async (file: File) => {
      if (!sessionId) return;
      setMetadataUploading(true);
      setMetadataError(null);
      try {
        const result = await api.uploadColumnMetadata(sessionId, file);
        setMetadataParsed(result.columns);
      } catch (err: unknown) {
        setMetadataError(
          err instanceof Error ? err.message : "Failed to parse metadata file",
        );
      } finally {
        setMetadataUploading(false);
      }
    },
    [sessionId],
  );

  /* ── Dataset context submit ── */
  const handleContextSubmit = useCallback(
    async (skip = false) => {
      if (!pendingUploadResult || !sessionId) return;
      setLoading(true);
      setError(null);
      try {
        if (!skip) {
          await api.updateSessionContext(sessionId, {
            domain: datasetContext.domain || undefined,
            domain_custom: datasetContext.domainCustom || undefined,
            row_meaning: datasetContext.rowMeaning || undefined,
            purpose: datasetContext.purpose || undefined,
            consumers: datasetContext.consumers || undefined,
            business_rules: datasetContext.businessRules || undefined,
            related_systems: datasetContext.relatedSystems || undefined,
            special_requirements:
              datasetContext.specialRequirements || undefined,
            knowledge_skill_slug:
              datasetContext.knowledgeSkillSlug || undefined,
            project_id: projectId || undefined,
          });
        }
        // Persist context to localStorage for next time
        if (datasetFingerprint) {
          saveCachedContext(datasetFingerprint, {
            datasetContext,
            sheetContexts:
              Object.keys(sheetContexts).length > 0 ? sheetContexts : undefined,
            sheetKeys:
              Object.keys(sheetKeys).length > 0 ? sheetKeys : undefined,
            savedAt: new Date().toISOString(),
          });
        }
        await handleDataReady(pendingUploadResult);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to save context");
        setLoading(false);
      }
    },
    [
      pendingUploadResult,
      sessionId,
      datasetContext,
      datasetFingerprint,
      sheetContexts,
      sheetKeys,
      handleDataReady,
      projectId,
    ],
  );

  /* ── Chat ── */
  const sendChatMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !sessionId) return;
      setChatLoading(true);
      setChatMessages((prev) => [...prev, { role: "user", content: text }]);
      setChatInput("");
      try {
        const res = await api.chat(sessionId, text);
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.response },
        ]);
      } catch {
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "⚠️ Sorry, I encountered an error. Please try again.",
          },
        ]);
      } finally {
        setChatLoading(false);
        chatInputRef.current?.focus();
      }
    },
    [sessionId],
  );

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  /* ── Q&A: generate LLM questions + load memory answers in parallel ── */
  useEffect(() => {
    if (step !== 3 || !sessionId || qaInitializedRef.current) return;
    let cancelled = false;
    qaInitializedRef.current = true;
    setQuestionsLoading(true);

    const memoryPromise: Promise<Record<string, string>> = projectId
      ? import("../apiv2")
          .then(({ apiv2 }) => apiv2.getProject(projectId))
          .then((project: any) => {
            try {
              const stored = JSON.parse(project?.source_data_info || "{}");
              if (stored?.qa_answers && typeof stored.qa_answers === "object") {
                return stored.qa_answers as Record<string, string>;
              }
            } catch {
              /* non-fatal */
            }
            return {};
          })
          .catch(() => ({}))
      : Promise.resolve({});

    Promise.all([api.profileQuestions(sessionId), memoryPromise])
      .then(([r, savedAnswers]) => {
        if (cancelled) return;
        setQaQuestions(r.questions);
        if (Object.keys(savedAnswers).length > 0)
          setMemoryAnswers(savedAnswers);
        const prefilled: QAFormState = {};
        r.questions.forEach((q) => {
          if (savedAnswers[q.id]) {
            prefilled[q.id] = savedAnswers[q.id];
          } else if (q.suggested_answer) {
            prefilled[q.id] = q.suggested_answer;
          }
        });
        setQaAnswers(prefilled);
        setQuestionsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setQuestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, sessionId, projectId]);

  // Reset confirmation when user edits answers
  useEffect(() => {
    if (qaConfirmed) {
      setQaConfirmed(false);
      setQaSummary("");
    }
  }, [qaAnswers]);

  /* ── Generate semantic view ── */
  const handleGenerateView = useCallback(async () => {
    if (!sessionId) return;
    setSvLoading(true);
    setError(null);
    try {
      const sv = await api.generateSemanticView(sessionId);
      setSemanticView(sv);
      setStep(4);
    } catch (err: any) {
      setError(err?.message ?? "Semantic view generation failed");
    } finally {
      setSvLoading(false);
    }
  }, [sessionId]);

  /* ── Confirm answers & check for follow-ups ── */
  const handleConfirmAnswers = useCallback(async () => {
    if (!sessionId) return;
    setQaFollowupLoading(true);
    try {
      const res = await api.qaFollowup(sessionId, qaAnswers);
      setQaSummary(res.summary || "");
      if (res.followup_questions && res.followup_questions.length > 0) {
        // Append follow-up questions to the existing list
        setQaQuestions((prev) => {
          const existingIds = new Set(prev.map((q) => q.id));
          const newQs = res.followup_questions.filter(
            (q) => !existingIds.has(q.id),
          );
          return [...prev, ...newQs];
        });
      }
      setQaConfirmed(true);
    } catch {
      // Non-fatal: just let them proceed
      setQaSummary("Your answers look good — ready to generate.");
      setQaConfirmed(true);
    } finally {
      setQaFollowupLoading(false);
    }
  }, [sessionId, qaAnswers]);

  /* ── Generate with Q&A: save memory → submit to backend → generate ── */
  const handleGenerateWithQA = useCallback(async () => {
    if (!sessionId) return;
    setQaLoading(true);
    setError(null);

    if (projectId) {
      try {
        const { apiv2 } = await import("../apiv2");
        await apiv2.updateProject(projectId, {
          source_data_info: JSON.stringify({
            qa_answers: qaAnswers,
            last_answered: new Date().toISOString(),
          }),
        });
      } catch {
        /* non-fatal */
      }
    }

    try {
      await api.qaSubmit(sessionId, qaAnswers);
    } catch {
      /* non-fatal */
    }

    setQaLoading(false);
    await handleGenerateView();
  }, [sessionId, projectId, qaAnswers, handleGenerateView]);

  /* ── Refine semantic view chat ── */
  const sendRefineMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !sessionId) return;
      setRefineLoading(true);
      setRefineMessages((prev) => [...prev, { role: "user", content: text }]);
      setRefineInput("");
      try {
        const res = await api.refineSemanticView(sessionId, text);
        setRefineMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.response },
        ]);
        setLlmAvailable(res.updated);
        if (res.updated && res.semantic_view) {
          setSemanticView((prev) =>
            prev
              ? {
                  ...prev,
                  semantic_view: res.semantic_view,
                  glossary_markdown:
                    res.glossary_markdown ?? prev.glossary_markdown,
                  er_diagram_markdown:
                    res.er_diagram_markdown ?? prev.er_diagram_markdown,
                }
              : prev,
          );
        }
      } catch {
        setRefineMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "⚠️ Sorry, I encountered an error. Please try again.",
          },
        ]);
      } finally {
        setRefineLoading(false);
        refineInputRef.current?.focus();
      }
    },
    [sessionId],
  );

  useEffect(() => {
    refineBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [refineMessages, qaChatMessages]);

  /* ── Validate semantic view ── */
  const handleValidate = useCallback(async () => {
    if (!sessionId) return;
    setValidationLoading(true);
    try {
      const result = await api.validateSemanticView(sessionId);
      setValidation(result);
    } catch (err: any) {
      setError(err?.message ?? "Validation failed");
    } finally {
      setValidationLoading(false);
    }
  }, [sessionId]);

  /* ── Apply naming standards (global conventions enforcement) ── */
  const handleApplyNamingStandards = useCallback(async () => {
    if (!sessionId) return;
    setNamingStandardsLoading(true);
    setNamingStandardsError(null);
    try {
      const res = await api.applyNamingStandards(sessionId, {
        case_style: "snake_case",
        target_platform: selectedTarget || undefined,
      });
      setNamingStandardsReport(res.report);
      if (res.semantic_view) {
        setSemanticView((prev) =>
          prev
            ? {
                ...prev,
                semantic_view: res.semantic_view,
                glossary_markdown:
                  res.glossary_markdown ?? prev.glossary_markdown,
                er_diagram_markdown:
                  res.er_diagram_markdown ?? prev.er_diagram_markdown,
              }
            : prev,
        );
      }
    } catch (err: any) {
      setNamingStandardsError(
        err?.message ?? "Failed to apply naming standards",
      );
    } finally {
      setNamingStandardsLoading(false);
    }
  }, [sessionId, selectedTarget]);

  /* ── Metric definitions (metric-definition layer) ── */
  const addMetric = useCallback(async () => {
    if (
      !sessionId ||
      !newMetricDraft.name.trim() ||
      !newMetricDraft.entity.trim() ||
      !newMetricDraft.expression.trim()
    )
      return;
    setAddMetricBusy(true);
    setAddMetricError(null);
    try {
      const res = await api.addMetricDefinition(sessionId, {
        name: newMetricDraft.name.trim(),
        entity: newMetricDraft.entity,
        expression: newMetricDraft.expression.trim(),
        aggregation: newMetricDraft.aggregation || undefined,
        description: newMetricDraft.description || undefined,
      });
      if (res.semantic_view) {
        setSemanticView((prev: any) =>
          prev
            ? { ...prev, semantic_view: res.semantic_view }
            : { semantic_view: res.semantic_view },
        );
      }
      setNewMetricDraft({
        name: "",
        entity: "",
        expression: "",
        aggregation: "sum",
        description: "",
      });
      setAddingMetric(false);
    } catch (err: unknown) {
      setAddMetricError(
        err instanceof Error ? err.message : "Add metric failed",
      );
    } finally {
      setAddMetricBusy(false);
    }
  }, [sessionId, newMetricDraft]);

  const deleteMetric = useCallback(
    async (metricName: string) => {
      if (!sessionId) return;
      setDeletingMetric(metricName);
      try {
        const res = await api.deleteMetricDefinition(sessionId, metricName);
        if (res.semantic_view) {
          setSemanticView((prev: any) =>
            prev
              ? { ...prev, semantic_view: res.semantic_view }
              : { semantic_view: res.semantic_view },
          );
        }
      } catch {
        // Non-fatal — the list simply won't reflect the deletion; a refresh recovers.
      } finally {
        setDeletingMetric(null);
      }
    },
    [sessionId],
  );

  /* ── Ask a question about the model (NL Q&A) ── */
  const sendQaChatMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !sessionId) return;
      setQaChatLoading(true);
      setQaChatMessages((prev) => [...prev, { role: "user", content: text }]);
      setQaChatInput("");
      try {
        const res = await api.askQuestion(sessionId, text);
        setQaChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.answer },
        ]);
        setLlmAvailable(res.answered);
      } catch (err: any) {
        setQaChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `⚠️ ${err?.message ?? "Sorry, I encountered an error. Please try again."}`,
          },
        ]);
      } finally {
        setQaChatLoading(false);
      }
    },
    [sessionId],
  );

  /* ── Relationship editor ── */
  const startEditingRelationships = useCallback(() => {
    const sv = (semanticView?.semantic_view as Record<string, any>)
      ?.semantic_view;
    const rels: SemanticRelationship[] = (sv?.relationships ?? []).map(
      toRelationshipPayload,
    );
    setRelDraft(rels);
    setRelError(null);
    setRelEditing(true);
  }, [semanticView]);

  const saveRelationships = useCallback(async () => {
    if (!sessionId) return;
    setRelSaving(true);
    setRelError(null);
    try {
      const res = await api.updateRelationships(sessionId, relDraft);
      setSemanticView((prev) => {
        if (!prev) return prev;
        const wrapper = prev.semantic_view as Record<string, any>;
        const sv = wrapper?.semantic_view ?? wrapper;
        return {
          ...prev,
          semantic_view: {
            ...wrapper,
            semantic_view: { ...sv, relationships: res.relationships },
          },
          er_diagram_markdown: res.er_diagram_markdown,
        };
      });
      setRelEditing(false);
    } catch (err: any) {
      setRelError(err?.message ?? "Failed to save relationships");
    } finally {
      setRelSaving(false);
    }
  }, [sessionId, relDraft]);

  /** Applies a single mutation to the full relationships array and persists
   * it via the existing bulk `updateRelationships` endpoint — used by the
   * ER canvas's per-linkage edit/delete actions, which don't go through the
   * relDraft bulk-editor UI. */
  const applyRelationshipMutation = useCallback(
    async (
      mutate: (rels: SemanticRelationship[]) => SemanticRelationship[],
    ) => {
      if (!sessionId) throw new Error("No active session");
      const sv = (semanticView?.semantic_view as Record<string, any>)
        ?.semantic_view;
      const rawRels: any[] = sv?.relationships ?? [];
      const payload = mutate(rawRels.map(toRelationshipPayload));
      const res = await api.updateRelationships(sessionId, payload);
      setSemanticView((prev) => {
        if (!prev) return prev;
        const wrapper = prev.semantic_view as Record<string, any>;
        const prevSv = wrapper?.semantic_view ?? wrapper;
        return {
          ...prev,
          semantic_view: {
            ...wrapper,
            semantic_view: { ...prevSv, relationships: res.relationships },
          },
          er_diagram_markdown: res.er_diagram_markdown,
        };
      });
    },
    [sessionId, semanticView],
  );

  const handleCanvasRelationshipCardinalityChange = useCallback(
    (index: number, cardinality: string) =>
      applyRelationshipMutation((rels) =>
        rels.map((r, i) => (i === index ? { ...r, cardinality } : r)),
      ),
    [applyRelationshipMutation],
  );

  const handleCanvasRelationshipDelete = useCallback(
    (index: number) =>
      applyRelationshipMutation((rels) => rels.filter((_, i) => i !== index)),
    [applyRelationshipMutation],
  );

  /** Called when the user drags from one column's connection dot to another
   * on the ER canvas — appends a new relationship (default cardinality
   * many_to_one, since that's the most common FK shape) via the same bulk
   * `updateRelationships` path the other canvas mutations use. */
  const handleCanvasCreateRelationship = useCallback(
    (
      fromEntity: string,
      fromColumn: string,
      toEntity: string,
      toColumn: string,
    ) =>
      applyRelationshipMutation((rels) => [
        ...rels,
        {
          from_entity: fromEntity,
          to_entity: toEntity,
          from_column: fromColumn,
          to_column: toColumn,
          cardinality: "many_to_one",
          confidence: 1,
        },
      ]),
    [applyRelationshipMutation],
  );

  /* ── Modeling intelligence (deterministic FK suggestions / warnings / SCD) ── */
  const loadModelingInsights = useCallback(async () => {
    if (!sessionId) return;
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      const res = await api.modelingInsights(sessionId);
      setModelingInsights(res);
    } catch (err: any) {
      setInsightsError(err?.message ?? "Failed to load modeling insights");
    } finally {
      setInsightsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (modelViewTab === "canvas" && sessionId) {
      void loadModelingInsights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelViewTab, sessionId]);

  const acceptFkSuggestion = useCallback(
    async (s: {
      from_entity: string;
      from_column: string;
      to_entity: string;
      to_column: string;
      cardinality: string;
      confidence: number;
    }) => {
      await applyRelationshipMutation((rels) => [
        ...rels,
        {
          from_entity: s.from_entity,
          to_entity: s.to_entity,
          from_column: s.from_column,
          to_column: s.to_column,
          cardinality: s.cardinality,
          confidence: s.confidence,
        },
      ]);
      await loadModelingInsights();
    },
    [applyRelationshipMutation, loadModelingInsights],
  );

  /* ── Semantic-layer export (dbt / LookML / Cube.dev) ── */
  const handleExportSemanticLayer = useCallback(
    async (format: SemanticLayerExportFormat) => {
      if (!sessionId) return;
      setExportingFormat(format);
      setExportError(null);
      try {
        const res = await api.exportSemanticLayer(sessionId, format);
        const blob = new Blob([res.content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.filename;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err: any) {
        setExportError(err?.message ?? "Failed to export semantic layer");
      } finally {
        setExportingFormat(null);
      }
    },
    [sessionId],
  );

  /* ── Session snapshots ── */
  const refreshSnapshots = useCallback(async () => {
    if (!sessionId) return;
    setSnapshotsLoading(true);
    try {
      const res = await api.listSnapshots(sessionId);
      setSnapshots(res.snapshots);
    } catch (err: any) {
      setSnapshotError(err?.message ?? "Failed to load snapshots");
    } finally {
      setSnapshotsLoading(false);
    }
  }, [sessionId]);

  const saveSnapshot = useCallback(async () => {
    if (!sessionId) return;
    setSnapshotSaving(true);
    setSnapshotError(null);
    try {
      await api.snapshotSession(sessionId, snapshotLabel || undefined);
      setSnapshotLabel("");
      await refreshSnapshots();
    } catch (err: any) {
      setSnapshotError(err?.message ?? "Failed to save snapshot");
    } finally {
      setSnapshotSaving(false);
    }
  }, [sessionId, snapshotLabel, refreshSnapshots]);

  const restoreSnapshot = useCallback(
    async (snapshotId: string) => {
      if (!sessionId) return;
      setSnapshotRestoring(snapshotId);
      setSnapshotError(null);
      try {
        await api.restoreSnapshot(sessionId, snapshotId);
        const detail = await api.getSession(sessionId);
        if (detail.semantic_view) {
          setSemanticView(detail.semantic_view as SemanticViewResponse);
        }
        // PR #580 review finding #4 — restoring an older snapshot swaps in a
        // different semantic model, so every downstream artifact computed
        // against the model that was in place a moment ago (target model/
        // DDL, schema validation, DDL validation, cost estimate, HITL
        // approval, masking plan/badges, lineage/catalog export results) is
        // now stale and must not keep showing a misleading "Valid" /
        // "Approved" badge for a model that no longer applies.
        setTargetModel(null);
        setValidation(null);
        setDdlValidation(null);
        setCostEstimate(null);
        setApproval(detail.approval ?? null);
        setMaskingPlan(null);
        setMaskingApplied({});
        setLineageResult(null);
        setCatalogResult(null);
      } catch (err: any) {
        setSnapshotError(err?.message ?? "Failed to restore snapshot");
      } finally {
        setSnapshotRestoring(null);
      }
    },
    [sessionId],
  );

  /** Structural diff between two saved snapshots — lets a user preview what
   * a restore would actually change before doing it. */
  const runSnapshotDiff = useCallback(async () => {
    if (!sessionId || !diffFromId || !diffToId) return;
    setDiffLoading(true);
    setDiffError(null);
    try {
      const res = await api.diffSnapshots(sessionId, diffFromId, diffToId);
      setDiffResult(res);
    } catch (err: any) {
      setDiffError(err?.message ?? "Failed to diff snapshots");
    } finally {
      setDiffLoading(false);
    }
  }, [sessionId, diffFromId, diffToId]);

  /* ── HITL approval gate ── */
  const submitApprovalRequest = useCallback(async () => {
    if (!sessionId || !selectedTarget) return;
    setApprovalLoading(true);
    setApprovalError(null);
    try {
      const res = await api.requestApproval(
        sessionId,
        selectedTarget,
        approvalNotes,
      );
      setApproval(res.approval);
      setApprovalNotes("");
    } catch (err: any) {
      setApprovalError(err?.message ?? "Failed to request approval");
    } finally {
      setApprovalLoading(false);
    }
  }, [sessionId, selectedTarget, approvalNotes]);

  const decideApprovalRequest = useCallback(
    async (decision: "approved" | "rejected") => {
      if (!sessionId) return;
      setApprovalLoading(true);
      setApprovalError(null);
      try {
        const res = await api.decideApproval(
          sessionId,
          decision,
          approvalNotes,
        );
        setApproval(res.approval);
        setApprovalNotes("");
      } catch (err: any) {
        setApprovalError(err?.message ?? "Failed to record decision");
      } finally {
        setApprovalLoading(false);
      }
    },
    [sessionId, approvalNotes],
  );

  /* ── Guardian masking suggestions ── */
  const fetchMaskingPlan = useCallback(async () => {
    if (!sessionId) return;
    setMaskingLoading(true);
    setMaskingError(null);
    try {
      const res = await api.suggestMaskingPlan(sessionId);
      setMaskingPlan(res.plan);
    } catch (err: any) {
      setMaskingError(err?.message ?? "Failed to suggest a masking plan");
    } finally {
      setMaskingLoading(false);
    }
  }, [sessionId]);

  const applyMaskingForEntity = useCallback(
    async (entityName: string) => {
      if (!sessionId) return;
      setMaskingApplying(entityName);
      setMaskingError(null);
      try {
        await api.applyMaskingPlan(sessionId, entityName);
        setMaskingApplied((prev) => ({ ...prev, [entityName]: true }));
      } catch (err: any) {
        setMaskingError(err?.message ?? "Failed to apply masking plan");
      } finally {
        setMaskingApplying(null);
      }
    },
    [sessionId],
  );

  /* ── DDL validation ── */
  const runDdlValidation = useCallback(async () => {
    if (!sessionId || !selectedTarget) return;
    setDdlValidating(true);
    setDdlError(null);
    try {
      const res = await api.validateDdl(sessionId, selectedTarget);
      setDdlValidation(res);
    } catch (err: any) {
      setDdlError(err?.message ?? "DDL validation failed");
    } finally {
      setDdlValidating(false);
    }
  }, [sessionId, selectedTarget]);

  /* ── Cost estimation ── */
  const runCostEstimate = useCallback(async () => {
    if (!sessionId || !selectedTarget) return;
    setCostLoading(true);
    setCostError(null);
    try {
      const res = await api.estimateCost(sessionId, selectedTarget);
      setCostEstimate(res);
    } catch (err: any) {
      setCostError(err?.message ?? "Cost estimation failed");
    } finally {
      setCostLoading(false);
    }
  }, [sessionId, selectedTarget]);

  /* ── Lineage capture ── */
  const runCaptureLineage = useCallback(async () => {
    if (!sessionId) return;
    setLineageLoading(true);
    setLineageError(null);
    try {
      const res = await api.captureLineage(sessionId);
      setLineageResult(res);
    } catch (err: any) {
      setLineageError(err?.message ?? "Failed to capture lineage");
    } finally {
      setLineageLoading(false);
    }
  }, [sessionId]);

  /* ── Catalog export ── */
  const runCatalogExport = useCallback(async () => {
    if (!sessionId || !catalogEndpoint.trim()) return;
    setCatalogExporting(true);
    setCatalogError(null);
    try {
      const res = await api.exportToCatalog(
        sessionId,
        catalogEndpoint,
        catalogApiKey || undefined,
      );
      setCatalogResult(res);
    } catch (err: any) {
      setCatalogError(err?.message ?? "Catalog export failed");
    } finally {
      setCatalogExporting(false);
    }
  }, [sessionId, catalogEndpoint, catalogApiKey]);

  /* ── Load snapshots + approval status once a session enters refine/deploy ── */
  useEffect(() => {
    if (!sessionId) return;
    if (step === 4.5 || step === 6) {
      refreshSnapshots();
    }
  }, [sessionId, step, refreshSnapshots]);

  /* ── Build a targeted fix prompt from a validation issue ── */
  const buildFixPrompt = (issue: ValidationIssue): string => {
    const items = issue.affected_items.slice(0, 10);
    const fact =
      items.find((i) => i.toUpperCase().includes("FACT")) || items[0] || "";
    const dims = items.filter((i) => !i.toUpperCase().includes("FACT"));
    switch (issue.category) {
      case "Relationships":
        return `Add FK relationships between the fact table and each dimension table. For ${fact}, create many-to-one relationships to: ${dims.join(", ")}. For each relationship specify the source_column (surrogate key in the fact) and target_column (primary key in the dimension).`;
      case "Fact Table":
        return `Review these entities and set entity_type correctly — one should be "Fact" (the central measures entity) and the others should be "Dimension": ${items.join(", ")}.`;
      case "Grain Definition":
        return `Define the grain for these entities — write a clear statement of what one row represents in each: ${items.join(", ")}.`;
      case "Measure Identification":
        return `In ${fact || items.join(", ")}, mark all numeric financial measures (revenue, cost, hours, amounts, percentages) as is_measure=true with aggregation_type set to additive or semi-additive as appropriate.`;
      case "Column Descriptions":
        return `Add meaningful business descriptions to all columns that currently have empty or very short descriptions. Use context from our conversation about this dataset.`;
      case "Entity Classification":
        return `Set entity_type for these entities to Fact, Dimension, Bridge, or Snapshot as appropriate: ${items.join(", ")}.`;
      case "Aggregation Types":
        return `For these columns set aggregation_type (additive, semi-additive, non-additive, or attribute) and is_measure appropriately: ${items.slice(0, 8).join(", ")}.`;
      default:
        return `Fix the following issue — ${issue.message}. Affected: ${items.join(", ")}.`;
    }
  };

  /* ── Navigate to refine and fire a fix prompt automatically ── */
  const fixIssueWithAI = useCallback(
    (issue: ValidationIssue) => {
      const prompt = buildFixPrompt(issue);
      setStep(4.5);
      sendRefineMessage(prompt);
    },
    [sendRefineMessage],
  );

  /* ── Fix all critical issues in one refine pass ── */
  const fixAllCritical = useCallback(
    (issues: ValidationIssue[]) => {
      const criticals = issues.filter((i) => i.severity === "critical");
      if (!criticals.length) return;
      const parts = criticals.map((issue) => buildFixPrompt(issue));
      const prompt = `Please fix all of the following issues in one pass:\n\n${parts.join("\n\n")}`;
      setStep(4.5);
      sendRefineMessage(prompt);
    },
    [sendRefineMessage],
  );

  /* ── Quick-fix an entity field (primary key / grain / business purpose)
     directly from the Validation Gate — no navigation to Refine. ── */
  const saveQuickFix = useCallback(
    async (
      entityName: string,
      field: "primary_keys" | "grain" | "business_purpose",
      value: string,
    ) => {
      if (!sessionId || !value.trim()) return;
      const key = `${field}::${entityName}`;
      setQuickFixSaving((prev) => new Set(prev).add(key));
      try {
        const patch =
          field === "primary_keys"
            ? { primary_keys: [value] }
            : { [field]: value };
        const res = await api.updateSemanticEntity(sessionId, entityName, {
          entity: patch,
        });
        const wrapper = (res as { semantic_view?: unknown }).semantic_view;
        if (wrapper) {
          setSemanticView((prev: any) =>
            prev
              ? { ...prev, semantic_view: wrapper }
              : { semantic_view: wrapper },
          );
        }
        setQuickFixSaved((prev) => new Set(prev).add(key));
      } finally {
        setQuickFixSaving((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [sessionId],
  );

  /* ── Generate target model ── */
  const handleGenerateTarget = useCallback(async () => {
    if (!selectedTarget || !sessionId) return;
    setTargetLoading(true);
    setError(null);
    try {
      const result = await api.generateTargetModel(
        sessionId,
        selectedTarget,
        targetSchema || undefined,
      );
      setTargetModel(result);
      setDeploySchemaName(result.schema || "");
      setStep(6); // Documentation step
    } catch (err: any) {
      setError(err?.message ?? "Model generation failed");
    } finally {
      setTargetLoading(false);
    }
  }, [selectedTarget, sessionId, targetSchema]);

  /* ── Deploy / Reconcile: shared target-connection dialog callbacks ──
     Both actions resolve the target connection the same way (saved
     connection_id, or a fresh manual connect) — which action actually runs
     is decided by deployDialogAction, set when the triggering button is
     clicked. */
  const runDeployAction = useCallback(
    async (connRef: {
      connectionId?: string;
      values?: DbConnectFormValues;
    }) => {
      if (!sessionId || !targetModel) return { ok: false, error: "No session" };
      const base = {
        session_id: sessionId,
        target: targetModel.target,
        project_id: projectId,
        schema_name: deploySchemaName || targetModel.schema || undefined,
        ...(connRef.connectionId
          ? { connection_id: connRef.connectionId }
          : {
              host: connRef.values?.host,
              port: connRef.values?.port
                ? parseInt(connRef.values.port, 10)
                : undefined,
              database: connRef.values?.database || "",
              username: connRef.values?.username,
              password: connRef.values?.password,
              account: connRef.values?.account,
              warehouse: connRef.values?.warehouse,
              role: connRef.values?.role,
              service_name: connRef.values?.service_name,
            }),
      };
      if (deployDialogAction === "reconcile") {
        setReconciling(true);
        setReconcileError(null);
        try {
          const result = await api.reconcileDeployment(
            base as ReconcileDeploymentRequest,
          );
          setReconcileResult(result);
          return { ok: true };
        } catch (err: unknown) {
          const message =
            err instanceof Error ? err.message : "Reconciliation failed";
          setReconcileError(message);
          return { ok: false, error: message };
        } finally {
          setReconciling(false);
        }
      }
      setDeploying(true);
      setDeployError(null);
      try {
        const result = await api.deployModel({
          ...base,
          database: connRef.values?.database || "",
        } as DeployModelRequest);
        setDeployResult(result);
        return { ok: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Deployment failed";
        setDeployError(message);
        return { ok: false, error: message };
      } finally {
        setDeploying(false);
      }
    },
    [sessionId, targetModel, projectId, deploySchemaName, deployDialogAction],
  );

  const handleDeployDialogConnectNew = useCallback(
    (values: DbConnectFormValues): Promise<ConnectResult> =>
      runDeployAction({ values }),
    [runDeployAction],
  );

  const handleDeployDialogConnectExisting = useCallback(
    (connectionId: string): Promise<ConnectResult> =>
      runDeployAction({ connectionId }),
    [runDeployAction],
  );

  /* ── Target model artifact download (individual) ── */
  /* Target-model DDL/config artifacts plus the business glossary + ER
   * diagram generated in the semantic-view step — merged so both are
   * downloadable (individually and via "Download All") alongside the DDL. */
  const displayArtifacts = React.useMemo(() => {
    const merged: Record<string, string> = {
      ...(targetModel?.artifacts ?? {}),
    };
    if (semanticView?.glossary_markdown) {
      merged.business_glossary = semanticView.glossary_markdown;
    }
    if (semanticView?.er_diagram_markdown) {
      merged.er_diagram = semanticView.er_diagram_markdown;
    }
    return merged;
  }, [targetModel, semanticView]);

  // Refine panel toggles between the existing "edit the model" chat and the
  // new read-only NL Q&A chat — both share the same ChatEntry-shaped state
  // and rendering, so pick the active one rather than duplicating the panel.
  const activeChatMessages =
    refinePanelMode === "refine" ? refineMessages : qaChatMessages;
  const activeChatLoading =
    refinePanelMode === "refine" ? refineLoading : qaChatLoading;
  const activeChatInput =
    refinePanelMode === "refine" ? refineInput : qaChatInput;
  const setActiveChatInput =
    refinePanelMode === "refine" ? setRefineInput : setQaChatInput;
  const sendActiveChatMessage =
    refinePanelMode === "refine" ? sendRefineMessage : sendQaChatMessage;

  const handleArtifactDownload = useCallback(
    async (key: string, content: string) => {
      setDownloadingArtifact(key);
      setDocError(null);
      try {
        await saveTextFile(content, artifactFilename(key), artifactMime(key));
      } catch (err: unknown) {
        if (!isAbortError(err)) {
          setDocError(err instanceof Error ? err.message : "Download failed");
        }
      } finally {
        setDownloadingArtifact(null);
      }
    },
    [],
  );

  /* ── ER diagram image export — rasterizes the already-rendered Mermaid SVG
   * client-side, no server round-trip needed. */
  const handleDownloadErDiagramImage = useCallback(
    async (format: "png" | "svg") => {
      const svg = erDiagramSvgRef.current;
      if (!svg) return;
      setErDiagramDownloading(format);
      setDocError(null);
      try {
        if (format === "svg") {
          await saveBlobFile(
            new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
            "er-diagram.svg",
          );
        } else {
          const pngBlob = await svgStringToPngBlob(svg);
          await saveBlobFile(pngBlob, "er-diagram.png");
        }
      } catch (err: unknown) {
        if (!isAbortError(err)) {
          setDocError(
            err instanceof Error ? err.message : "Diagram download failed",
          );
        }
      } finally {
        setErDiagramDownloading(null);
      }
    },
    [],
  );

  /* ── Markdown artifact export (PDF / Excel-CSV) — client-side conversion
   * of a generated markdown artifact (deployment guide, business glossary)
   * into a formatted, readable document instead of the raw pipe-table text. */
  const handleExportArtifactDocument = useCallback(
    async (key: string, content: string, format: "pdf" | "csv") => {
      const exportKey = `${key}:${format}`;
      setExportingArtifact(exportKey);
      setDocError(null);
      try {
        const label = key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        const baseName = artifactFilename(key).replace(/\.[^.]+$/, "");
        if (format === "pdf") {
          downloadMarkdownAsPdf(content, label, `${baseName}.pdf`);
        } else {
          const csv = markdownToSectionedCsv(content, label);
          await saveBlobFile(
            new Blob([csv], { type: "text/csv;charset=utf-8" }),
            `${baseName}.csv`,
          );
        }
      } catch (err: unknown) {
        if (!isAbortError(err)) {
          setDocError(err instanceof Error ? err.message : "Export failed");
        }
      } finally {
        setExportingArtifact(null);
      }
    },
    [],
  );

  /* ── Target model artifacts download (all) ──
   * Saves straight into a user-chosen folder when supported (Chromium),
   * otherwise zips them client-side into one downloadable file. */
  const handleDownloadAllArtifacts = useCallback(async () => {
    if (!targetModel) return;
    setDownloadingAllArtifacts(true);
    setDocError(null);
    try {
      const entries = Object.entries(displayArtifacts);
      if (window.showDirectoryPicker) {
        const dir = await window.showDirectoryPicker({ mode: "readwrite" });
        for (const [key, content] of entries) {
          const handle = await dir.getFileHandle(artifactFilename(key), {
            create: true,
          });
          const writable = await handle.createWritable();
          await writable.write(
            new Blob([content], { type: artifactMime(key) }),
          );
          await writable.close();
        }
        return;
      }
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      for (const [key, content] of entries) {
        zip.file(artifactFilename(key), content);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const filename = `${targetModel.target}-model-artifacts.zip`;
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        setDocError(err instanceof Error ? err.message : "Download all failed");
      }
    } finally {
      setDownloadingAllArtifacts(false);
    }
  }, [targetModel]);

  /* ── Reset ── */
  const handleReset = () => {
    setStep(1);
    setLoading(false);
    setError(null);
    setSourceType("file");
    setSessionId(null);
    setProfile(null);
    setPreview([]);
    setIsMultiSheet(false);
    setPendingSheets([]);
    setPendingFileName("");
    setSelectedSheets(new Set());
    setSheetContexts({});
    setSheetKeys({});
    setPendingUploadResult(null);
    setDatasetFingerprint("");
    setDatasetContext({
      domain: "",
      domainCustom: "",
      rowMeaning: "",
      purpose: "",
      consumers: "",
      businessRules: "",
      relatedSystems: "",
      specialRequirements: "",
      knowledgeSkillSlug: "",
    });
    setChatMessages([]);
    setChatInput("");
    setSemanticView(null);
    setTargetModel(null);
    setSelectedTarget(null);
    setTargetSchema("");
    setDocError(null);
    setRefineMessages([]);
    setRefineInput("");
    setValidation(null);
    setKeySuggestions(null);
    setExpanded("deployment_guide");
    setShowDeploy(false);
    setDeployResult(null);
    setDeployError(null);
    // PR #580 review finding #4 — clear downstream artifacts too, so a fresh
    // session never shows a stale DDL-validation/approval/masking/lineage
    // badge carried over from the previous session.
    setDdlValidation(null);
    setCostEstimate(null);
    setApproval(null);
    setApprovalNotes("");
    setApprovalError(null);
    setMaskingPlan(null);
    setMaskingApplied({});
    setLineageResult(null);
    setLineageError(null);
    setCatalogResult(null);
    // Reset project DB state
    setProjectDbTables([]);
    setProjectDbViews([]);
    setSelectedProjectTable("");
    setTableSearchQuery("");
    // Reset shared Connect Database dialog table-picker state
    setSharedDbTables([]);
    setSharedDbViews([]);
    setSelectedSharedDbTables(new Set());
    setSharedDbTableSearch("");
    setPrimaryDbSchema(undefined);
    setAdditionalDbSources([]);
    setShowAddSourceDialog(false);
    setAddSourceLabel("");
    setSchemaBrowseSourceId(null);
    setSchemaBrowseValue("");
    setSchemaBrowseError(null);
    setSchemaOptions([]);
    setSchemaOptionsLoading(false);
    setSchemaOptionsError(null);
  };

  /* ── Sheet toggle helpers ── */
  const toggleSheetSelection = (name: string) => {
    setSelectedSheets((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const updateSheetContext = (name: string, context: string) => {
    setSheetContexts((prev) => ({ ...prev, [name]: context }));
  };

  /* ── Profile derived values ── */
  const sheetNames = profile?.metadata?.sheets_processed ?? [];
  const isProfileMultiSheet =
    sheetNames.length > 1 && !!profile?.per_sheet_profiles;

  const toggleCollapsedSheet = (sn: string) => {
    setCollapsedSheets((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  /* ═══════════════════════════════════════════
     Render
     ═══════════════════════════════════════════ */
  if (searchParams.get("tab") === "history" && projectId) {
    return (
      <AgentSessionHistoryView
        projectId={projectId}
        sessionType="semantic"
        agentLabel="Semantic Intelligence"
        onResume={(id) => navigate(`?session=${id}`)}
        onNewSession={() => navigate("?")}
      />
    );
  }

  return (
    <div className="relative min-h-full">
      <GreenTendrils />
      <div className="relative z-10 max-w-6xl mx-auto p-6 space-y-6">
        {/* ── Header ── */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 rounded-lg hover:bg-surface-200 text-white/40 hover:text-white transition"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-2xl font-bold">
                  <span className="gradient-text-green">Semantic</span>
                  <span className="text-white/40 font-extralight">
                    {" "}
                    Intelligence
                  </span>
                </h1>
                <p className="text-xs text-white/30 mt-0.5">
                  Upload datasets, auto-profile, ask smart questions, and deploy
                  semantic models
                </p>
              </div>
            </div>
            <StepIndicator current={step} />
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} /> {error}
            </span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs"
            >
              dismiss
            </button>
          </div>
        )}

        {/* ── Global loading overlay (skip when step 2 has its own spinner) ── */}
        {(loading || svLoading) && step !== 2 && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <div
                className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto"
                style={{ boxShadow: "0 0 30px rgba(134,188,37,0.2)" }}
              />
              <p className="text-sm text-white/50">
                {svLoading ? "Generating Semantic View..." : "Processing..."}
              </p>
              {svLoading && (
                <p className="text-xs text-white/25">
                  Claude is analyzing your dataset
                </p>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 1: Upload / Connect
           ════════════════════════════════════════════════ */}
        {step === 1 && !loading && (
          <div className="space-y-4 animate-fade-in">
            {/* Source type toggle */}
            <div className="glass rounded-xl p-4">
              <div className="flex gap-3">
                <button
                  data-testid="semantic-source-file"
                  onClick={() => setSourceType("file")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all border ${
                    sourceType === "file"
                      ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                      : "bg-surface-200 border-white/5 text-white/40 hover:text-white/60"
                  }`}
                >
                  <Upload size={16} /> Upload File
                </button>
                <button
                  data-testid="semantic-source-database"
                  onClick={() => setSourceType("db")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all border ${
                    sourceType === "db"
                      ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                      : "bg-surface-200 border-white/5 text-white/40 hover:text-white/60"
                  }`}
                >
                  <Database size={16} /> Connect Database
                </button>
              </div>
            </div>

            {existingProfileOffer && (
              <div
                data-testid="existing-profile-offer"
                className="glass rounded-xl p-6 space-y-3 border border-brand-500/30"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-brand-400">
                  <FileSpreadsheet size={16} />
                  Existing Data Profiler run found
                </div>
                <p className="text-xs text-white/60">
                  "{pendingUploadFile?.name ?? existingProfileOffer.source_name}
                  " was already profiled by the Data Profiler agent
                  {existingProfileOffer.created_at
                    ? ` on ${new Date(existingProfileOffer.created_at).toLocaleString()}`
                    : ""}
                  {typeof existingProfileOffer.table_count === "number" &&
                    ` — ${existingProfileOffer.table_count} table(s), ${existingProfileOffer.column_count ?? 0} column(s)`}
                  . Reuse it instead of re-profiling, or re-profile from
                  scratch.
                </p>
                <div className="flex gap-3">
                  <button
                    data-testid="use-existing-profile-btn"
                    onClick={handleUseExistingProfile}
                    className="px-4 py-2 rounded-lg text-xs font-medium bg-brand-500 text-black hover:bg-brand-400 transition-colors"
                  >
                    Use existing profile
                  </button>
                  <button
                    data-testid="reprofile-anyway-btn"
                    onClick={handleReprofileAnyway}
                    className="px-4 py-2 rounded-lg text-xs font-medium bg-surface-200 border border-white/10 text-white/60 hover:text-white/80 transition-colors"
                  >
                    Re-profile anyway
                  </button>
                </div>
              </div>
            )}

            {sourceType === "file" ? (
              <div className="glass rounded-xl p-8">
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                    isDragActive
                      ? "border-brand-500 bg-brand-500/5"
                      : "border-white/10 hover:border-brand-500/40 hover:bg-brand-500/5"
                  }`}
                >
                  <input {...getInputProps()} />
                  <FileSpreadsheet
                    size={40}
                    className="mx-auto mb-4 text-brand-500/60"
                  />
                  {isDragActive ? (
                    <p className="text-brand-500 font-medium">
                      Drop your file here
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-white/60 mb-1">
                        Drag & drop your dataset, or click to browse
                      </p>
                      <p className="text-[11px] text-white/25">
                        CSV, Excel (multi-sheet supported), JSON, Parquet
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* ── Database connection form ── */
              <div className="space-y-4">
                {/* ── Project Connection Card ── */}
                {projectId && projectDbConfig && (
                  <div className="glass rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-brand-400 flex items-center gap-2">
                        <Server size={16} /> Project Database Connection
                      </h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-400 border border-brand-500/20">
                        {projectDbConfig.db_type?.toUpperCase()}
                      </span>
                    </div>

                    {/* Connection summary */}
                    <div className="grid grid-cols-2 gap-3">
                      {projectDbConfig.database && (
                        <div className="text-xs">
                          <span className="text-white/30">Database:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.database}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.schema_name && (
                        <div className="text-xs">
                          <span className="text-white/30">Schema:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.schema_name}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.host && (
                        <div className="text-xs">
                          <span className="text-white/30">Host:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.host}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.account && (
                        <div className="text-xs">
                          <span className="text-white/30">Account:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.account}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.warehouse && (
                        <div className="text-xs">
                          <span className="text-white/30">Warehouse:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.warehouse}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.username && (
                        <div className="text-xs">
                          <span className="text-white/30">User:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.username}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Fetch tables button or table list */}
                    {projectDbTables.length === 0 &&
                    projectDbViews.length === 0 ? (
                      <button
                        data-testid="semantic-db-list-btn"
                        onClick={handleFetchProjectDbTables}
                        disabled={projectDbLoading}
                        className="w-full py-3 bg-brand-600/20 hover:bg-brand-600/30 border border-brand-500/30 disabled:opacity-50 text-brand-400 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                      >
                        {projectDbLoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />{" "}
                            Fetching tables...
                          </>
                        ) : (
                          <>
                            <Table2 size={16} /> Load Tables & Views
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        {/* Search */}
                        <div className="relative">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25"
                          />
                          <input
                            type="text"
                            value={tableSearchQuery}
                            onChange={(e) =>
                              setTableSearchQuery(e.target.value)
                            }
                            placeholder="Search tables and views..."
                            className="w-full pl-9 pr-3 py-2 bg-surface-200 border border-white/10 rounded-lg text-sm text-white/80 placeholder:text-white/20 focus:outline-none focus:border-brand-500/40"
                          />
                        </div>

                        {/* Tables */}
                        {projectDbTables.length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1">
                              <Table2 size={10} /> Tables (
                              {projectDbTables.length})
                            </p>
                            <div className="max-h-40 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                              {projectDbTables
                                .filter(
                                  (t) =>
                                    !tableSearchQuery ||
                                    t
                                      .toLowerCase()
                                      .includes(tableSearchQuery.toLowerCase()),
                                )
                                .map((table) => (
                                  <button
                                    key={`t-${table}`}
                                    data-testid="semantic-table-option"
                                    data-object={table}
                                    onClick={() =>
                                      setSelectedProjectTable(table)
                                    }
                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                                      selectedProjectTable === table
                                        ? "bg-brand-500/15 border border-brand-500/30 text-brand-400"
                                        : "bg-surface-200/50 border border-transparent text-white/50 hover:text-white/70 hover:bg-surface-200"
                                    }`}
                                  >
                                    <Table2
                                      size={12}
                                      className="inline mr-2 opacity-40"
                                    />
                                    {table}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Views */}
                        {projectDbViews.length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1">
                              <Eye size={10} /> Views ({projectDbViews.length})
                            </p>
                            <div className="max-h-40 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                              {projectDbViews
                                .filter(
                                  (v) =>
                                    !tableSearchQuery ||
                                    v
                                      .toLowerCase()
                                      .includes(tableSearchQuery.toLowerCase()),
                                )
                                .map((view) => (
                                  <button
                                    key={`v-${view}`}
                                    data-testid="semantic-view-option"
                                    data-object={view}
                                    onClick={() =>
                                      setSelectedProjectTable(view)
                                    }
                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                                      selectedProjectTable === view
                                        ? "bg-brand-500/15 border border-brand-500/30 text-brand-400"
                                        : "bg-surface-200/50 border border-transparent text-white/50 hover:text-white/70 hover:bg-surface-200"
                                    }`}
                                  >
                                    <Eye
                                      size={12}
                                      className="inline mr-2 opacity-40"
                                    />
                                    {view}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Connect & Profile with selected table */}
                        <button
                          data-testid="semantic-db-connect-btn"
                          onClick={handleProjectDbConnect}
                          disabled={dbConnecting || !selectedProjectTable}
                          className="w-full py-3 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {dbConnecting ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />{" "}
                              Connecting...
                            </>
                          ) : (
                            <>
                              <Database size={16} /> Connect & Apply Semantic
                              Intelligence
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Link to the shared Connect Database dialog — the
                        same component every agent (Profiler, Guardian,
                        Converter, Metadata Curator) uses to connect. */}
                    <div className="flex items-center gap-3 pt-2">
                      <div className="flex-1 h-px bg-white/5" />
                      <button
                        type="button"
                        data-testid="semantic-toggle-manual-mode"
                        onClick={() => setShowSharedConnectDialog(true)}
                        className="text-[11px] text-white/25 hover:text-white/40 transition-colors"
                      >
                        Or connect a different database…
                      </button>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>
                  </div>
                )}

                {/* ── No project connection configured yet: open the shared
                    Connect Database dialog directly ── */}
                {!projectDbConfig && (
                  <div className="glass rounded-xl p-8 flex flex-col items-center gap-3 text-center">
                    <Database size={28} className="text-brand-500/40" />
                    <p className="text-xs text-white/40 max-w-xs">
                      Connect to a source database to apply semantic
                      intelligence and model its tables.
                    </p>
                    <button
                      type="button"
                      data-testid="semantic-db-connect-manual"
                      onClick={() => setShowSharedConnectDialog(true)}
                      className="px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <Database size={16} /> Connect Database
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 1.6: DB table/view picker (shared Connect Database
           dialog, blank "Table or Query") — profile only what's chosen
           instead of the whole schema.
           ════════════════════════════════════════════════ */}
        {step === 1.6 &&
          !loading &&
          (() => {
            const allKeys = [
              ...sharedDbTables.map((t) => sdKey("primary", t)),
              ...sharedDbViews.map((t) => sdKey("primary", t)),
              ...additionalDbSources.flatMap((s) => [
                ...s.tables.map((t) => sdKey(s.sourceId, t)),
                ...s.views.map((t) => sdKey(s.sourceId, t)),
              ]),
            ];
            const renderSchemaSwitcher = (
              sourceId: string,
              currentSchema: string | undefined,
            ) => (
              <div className="flex items-center gap-2 text-[11px]">
                {currentSchema && (
                  <span className="text-white/25">schema: {currentSchema}</span>
                )}
                {schemaBrowseSourceId === sourceId ? (
                  <div className="flex items-center gap-1.5">
                    {schemaOptionsLoading ? (
                      <span className="flex items-center gap-1 px-2 py-1 text-white/40">
                        <Loader2 size={12} className="animate-spin" />
                        Loading schemas…
                      </span>
                    ) : schemaOptionsError || schemaOptions.length === 0 ? (
                      <input
                        type="text"
                        autoFocus
                        value={schemaBrowseValue}
                        onChange={(e) => setSchemaBrowseValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSchemaBrowse();
                          if (e.key === "Escape") setSchemaBrowseSourceId(null);
                        }}
                        placeholder="schema name"
                        title={
                          schemaOptionsError
                            ? `Could not list schemas — enter one manually (${schemaOptionsError})`
                            : undefined
                        }
                        className="px-2 py-1 w-28 bg-surface-200 border border-white/10 rounded text-[11px] text-white/80 placeholder:text-white/20 focus:outline-none focus:border-brand-500/40"
                      />
                    ) : (
                      <select
                        autoFocus
                        value={schemaBrowseValue}
                        onChange={(e) => setSchemaBrowseValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSchemaBrowse();
                          if (e.key === "Escape") setSchemaBrowseSourceId(null);
                        }}
                        className="px-2 py-1 w-36 bg-surface-200 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500/40"
                      >
                        {!schemaOptions.includes(schemaBrowseValue) && (
                          <option value={schemaBrowseValue}>
                            {schemaBrowseValue || "Select a schema…"}
                          </option>
                        )}
                        {schemaOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={handleSchemaBrowse}
                      disabled={
                        schemaBrowseLoading ||
                        schemaOptionsLoading ||
                        !schemaBrowseValue.trim()
                      }
                      className="text-brand-400 hover:text-brand-300 disabled:opacity-40"
                    >
                      {schemaBrowseLoading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        "Go"
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSchemaBrowseSourceId(null);
                        setSchemaOptions([]);
                        setSchemaOptionsError(null);
                      }}
                      className="text-white/30 hover:text-white/50"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      handleOpenSchemaBrowser(sourceId, currentSchema)
                    }
                    className="text-white/30 hover:text-white/60 underline decoration-dotted"
                  >
                    Switch schema
                  </button>
                )}
              </div>
            );
            const renderTableList = (
              sourceId: string,
              tables: string[],
              icon: "table" | "view",
            ) =>
              tables
                .filter(
                  (t) =>
                    !sharedDbTableSearch ||
                    t.toLowerCase().includes(sharedDbTableSearch.toLowerCase()),
                )
                .map((table) => {
                  const key = sdKey(sourceId, table);
                  const isSelected = selectedSharedDbTables.has(key);
                  return (
                    <button
                      key={`sd-${key}`}
                      type="button"
                      onClick={() =>
                        setSelectedSharedDbTables((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        })
                      }
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 ${
                        isSelected
                          ? "bg-brand-500/15 border border-brand-500/30 text-brand-400"
                          : "bg-surface-200/50 border border-transparent text-white/50 hover:text-white/70 hover:bg-surface-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="pointer-events-none"
                      />
                      {icon === "table" ? (
                        <Table2 size={12} className="opacity-40" />
                      ) : (
                        <Eye size={12} className="opacity-40" />
                      )}
                      {table}
                    </button>
                  );
                });
            return (
              <div className="space-y-4 animate-fade-in max-w-2xl mx-auto">
                <div className="text-center space-y-2">
                  <h2 className="text-xl font-bold text-white">
                    Choose tables to run Analysis
                  </h2>
                  <p className="text-white/35 text-sm">
                    Select one or more tables/views — only these will be sampled
                    and analyzed. Add another source database/schema below to
                    merge tables from more than one connection into a single
                    target model.
                  </p>
                </div>
                <div className="glass rounded-xl p-5 space-y-3">
                  <div className="relative">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25"
                    />
                    <input
                      type="text"
                      value={sharedDbTableSearch}
                      onChange={(e) => setSharedDbTableSearch(e.target.value)}
                      placeholder="Search tables and views..."
                      className="w-full pl-9 pr-3 py-2 bg-surface-200 border border-white/10 rounded-lg text-sm text-white/80 placeholder:text-white/20 focus:outline-none focus:border-brand-500/40"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[11px] text-white/30">
                      <span>Sample size cap (per table)</span>
                    </div>
                    {/* Same SAMPLE_PRESETS (incl. "All") as the Data Profiler
                        agent's table picker, for a consistent cross-agent UX. */}
                    <div className="flex flex-wrap gap-2">
                      {SAMPLE_PRESETS.map((p) => {
                        const active = sharedDbSampleSize === p.value;
                        return (
                          <button
                            key={p.label}
                            type="button"
                            onClick={() => setSharedDbSampleSize(p.value)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                              active
                                ? "bg-brand-600 text-white border-brand-600"
                                : "bg-surface-200 text-white/40 border-white/10 hover:text-white/70"
                            }`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-white/30">
                      {sharedDbSampleSize == null
                        ? "Profiling all rows — slow for large tables."
                        : `Up to ${sharedDbSampleSize.toLocaleString()} rows per table.`}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-white/30">
                    <span>
                      {selectedSharedDbTables.size} of {allKeys.length} selected
                    </span>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedSharedDbTables(new Set(allKeys))
                        }
                        className="text-brand-400 hover:text-brand-300"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedSharedDbTables(new Set())}
                        className="text-white/40 hover:text-white/60"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* ── Primary source ── */}
                  <div className="rounded-lg border border-white/5 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-medium">
                        Primary source
                      </p>
                      {renderSchemaSwitcher("primary", primaryDbSchema)}
                    </div>
                    {sharedDbTables.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1">
                          <Table2 size={10} /> Tables ({sharedDbTables.length})
                        </p>
                        <div className="max-h-56 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                          {renderTableList("primary", sharedDbTables, "table")}
                        </div>
                      </div>
                    )}
                    {sharedDbViews.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1">
                          <Eye size={10} /> Views ({sharedDbViews.length})
                        </p>
                        <div className="max-h-56 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                          {renderTableList("primary", sharedDbViews, "view")}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Additional sources (multi-database/schema) ── */}
                  {additionalDbSources.map((src) => (
                    <div
                      key={src.sourceId}
                      className="rounded-lg border border-white/5 p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-wider text-white/40 font-medium">
                          {src.label}
                        </p>
                        {renderSchemaSwitcher(src.sourceId, src.schema)}
                      </div>
                      {src.tables.length > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1">
                            <Table2 size={10} /> Tables ({src.tables.length})
                          </p>
                          <div className="max-h-56 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                            {renderTableList(src.sourceId, src.tables, "table")}
                          </div>
                        </div>
                      )}
                      {src.views.length > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1">
                            <Eye size={10} /> Views ({src.views.length})
                          </p>
                          <div className="max-h-56 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                            {renderTableList(src.sourceId, src.views, "view")}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {schemaBrowseError && (
                    <p className="text-[11px] text-red-400">
                      {schemaBrowseError}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowAddSourceDialog(true)}
                    className="w-full px-3 py-2 rounded-lg border border-dashed border-white/15 text-xs text-white/40 hover:text-white/70 hover:border-white/30 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Database size={12} /> + Add another source
                  </button>
                </div>
                {profilingSharedDbTables && sharedDbProgress && (
                  <div className="glass rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between text-[11px] text-white/40">
                      <span>
                        Sampling {sharedDbProgress.tablesDone} of{" "}
                        {sharedDbProgress.tablesTotal} tables
                        {sharedDbProgress.currentTable
                          ? ` — ${sharedDbProgress.currentTable}`
                          : ""}
                      </span>
                      <span>
                        {sharedDbProgress.tablesTotal > 0
                          ? Math.round(
                              (sharedDbProgress.tablesDone /
                                sharedDbProgress.tablesTotal) *
                                100,
                            )
                          : 0}
                        %
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-200 overflow-hidden">
                      <div
                        className="h-full bg-brand-500 transition-all duration-300"
                        style={{
                          width: `${
                            sharedDbProgress.tablesTotal > 0
                              ? (sharedDbProgress.tablesDone /
                                  sharedDbProgress.tablesTotal) *
                                100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="px-4 py-2 text-sm text-white/40 hover:text-white/60 transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmSharedDbTables}
                    disabled={
                      profilingSharedDbTables ||
                      selectedSharedDbTables.size === 0
                    }
                    className="px-5 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    {profilingSharedDbTables ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {sharedDbProgress && sharedDbProgress.tablesTotal > 0
                          ? `Analyzing ${sharedDbProgress.tablesDone}/${sharedDbProgress.tablesTotal}…`
                          : "Running Analysis…"}
                      </>
                    ) : (
                      <>
                        Run Analysis on {selectedSharedDbTables.size} selected
                      </>
                    )}
                  </button>
                </div>
                <ConnectDatabaseDialog
                  open={showAddSourceDialog}
                  onClose={() => setShowAddSourceDialog(false)}
                  title="Add Another Source"
                  projectId={projectId}
                  agentKey="semantic_intelligence"
                  extraField={{
                    key: "label",
                    label: "Label (optional)",
                    value: addSourceLabel,
                    onChange: (v) => setAddSourceLabel(v),
                    placeholder: "e.g. HR system",
                    testId: "semantic-add-source-label",
                  }}
                  testIdPrefix="semantic-add-source"
                  onConnectExisting={handleAddSourceDialogConnectExisting}
                  onConnectNew={handleAddSourceDialogConnectNew}
                />
              </div>
            );
          })()}

        {/* ════════════════════════════════════════════════
           STEP 1.5: Multi-Sheet Selection + Context
           ════════════════════════════════════════════════ */}
        {step === 1.5 && !loading && (
          <div className="space-y-4 animate-fade-in">
            <div className="text-center space-y-3">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium"
                style={{
                  background: "rgba(134,188,37,0.08)",
                  borderColor: "rgba(134,188,37,0.2)",
                  color: "#86BC25",
                }}
              >
                <Layers size={14} />
                Multi-Sheet Excel Detected
              </div>
              <h2 className="text-2xl font-bold text-white">
                {pendingFileName}
              </h2>
              <p className="text-white/35 max-w-lg mx-auto">
                This workbook has{" "}
                <strong className="text-white">
                  {pendingSheets.length} sheets
                </strong>
                . Select which sheets to include and{" "}
                <strong className="text-brand-400">
                  provide brief context
                </strong>{" "}
                for each so the AI can understand the data better and find
                relationships between sheets.
              </p>
            </div>

            <div className="w-full max-w-4xl mx-auto space-y-3">
              {/* Bulk actions */}
              <div className="flex items-center justify-between text-xs text-white/30">
                <span>
                  {selectedSheets.size} of {pendingSheets.length} selected
                </span>
                <div className="flex gap-3">
                  <button
                    onClick={handleSuggestKeys}
                    disabled={keySuggestLoading || selectedSheets.size === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20 disabled:opacity-40"
                  >
                    {keySuggestLoading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Sparkles size={12} />
                    )}
                    {keySuggestLoading ? "Analyzing..." : "AI Suggest Keys"}
                  </button>
                  <button
                    onClick={() =>
                      setSelectedSheets(
                        new Set(pendingSheets.map((s) => s.name)),
                      )
                    }
                    className="hover:text-white/60 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedSheets(new Set())}
                    className="hover:text-white/60 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Sheet cards with context input */}
              {pendingSheets.map((sheet) => {
                const isSelected = selectedSheets.has(sheet.name);
                return (
                  <div
                    key={sheet.name}
                    className="glass-card rounded-2xl p-4 transition-all duration-200"
                    style={
                      isSelected
                        ? {
                            borderColor: "rgba(134,188,37,0.4)",
                            boxShadow: "0 0 20px rgba(134,188,37,0.05)",
                          }
                        : {}
                    }
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleSheetSelection(sheet.name)}
                        className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          isSelected
                            ? "bg-brand-500 text-white"
                            : "border border-white/10"
                        }`}
                        style={
                          !isSelected
                            ? { background: "rgba(14,14,24,0.6)" }
                            : {}
                        }
                      >
                        {isSelected && <Check size={14} />}
                      </button>

                      {/* Sheet info + context */}
                      <div className="flex-1 min-w-0 space-y-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <FileSpreadsheet
                              size={16}
                              className={
                                isSelected ? "text-brand-500" : "text-white/25"
                              }
                            />
                            <span
                              className={`font-medium ${isSelected ? "text-white" : "text-white/50"}`}
                            >
                              {sheet.name}
                            </span>
                            <span className="text-xs text-white/25 ml-auto">
                              {sheet.columns} columns
                            </span>
                          </div>
                          {/* Column preview chips */}
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {sheet.column_names.slice(0, 8).map((col) => (
                              <span
                                key={col}
                                className="px-2 py-0.5 rounded-md text-xs"
                                style={{
                                  background: "rgba(255,255,255,0.04)",
                                  color: "rgba(255,255,255,0.35)",
                                }}
                              >
                                {col}
                              </span>
                            ))}
                            {sheet.column_names.length > 8 && (
                              <span
                                className="px-2 py-0.5 rounded-md text-xs text-white/20"
                                style={{ background: "rgba(255,255,255,0.03)" }}
                              >
                                +{sheet.column_names.length - 8} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* ── Context + Keys input for this sheet ── */}
                        {isSelected &&
                          (() => {
                            const sk = sheetKeys[sheet.name] || {
                              primaryKey: "",
                              alternateKeys: [],
                              foreignKeys: [],
                            };
                            const updatePK = (val: string) =>
                              setSheetKeys((prev) => ({
                                ...prev,
                                [sheet.name]: {
                                  ...(prev[sheet.name] || {
                                    primaryKey: "",
                                    alternateKeys: [],
                                    foreignKeys: [],
                                  }),
                                  primaryKey: val,
                                },
                              }));
                            const addFK = () =>
                              setSheetKeys((prev) => {
                                const cur = prev[sheet.name] || {
                                  primaryKey: "",
                                  alternateKeys: [],
                                  foreignKeys: [],
                                };
                                return {
                                  ...prev,
                                  [sheet.name]: {
                                    ...cur,
                                    foreignKeys: [
                                      ...cur.foreignKeys,
                                      {
                                        column: "",
                                        referencesSheet: "",
                                        referencesColumn: "",
                                      },
                                    ],
                                  },
                                };
                              });
                            const updateFK = (
                              idx: number,
                              field: string,
                              val: string,
                            ) =>
                              setSheetKeys((prev) => {
                                const cur = prev[sheet.name] || {
                                  primaryKey: "",
                                  alternateKeys: [],
                                  foreignKeys: [],
                                };
                                const fks = [...cur.foreignKeys];
                                fks[idx] = { ...fks[idx], [field]: val };
                                if (field === "referencesSheet")
                                  fks[idx].referencesColumn = "";
                                return {
                                  ...prev,
                                  [sheet.name]: { ...cur, foreignKeys: fks },
                                };
                              });
                            const removeFK = (idx: number) =>
                              setSheetKeys((prev) => {
                                const cur = prev[sheet.name] || {
                                  primaryKey: "",
                                  alternateKeys: [],
                                  foreignKeys: [],
                                };
                                return {
                                  ...prev,
                                  [sheet.name]: {
                                    ...cur,
                                    foreignKeys: cur.foreignKeys.filter(
                                      (_, i) => i !== idx,
                                    ),
                                  },
                                };
                              });
                            const addAK = () =>
                              setSheetKeys((prev) => {
                                const cur = prev[sheet.name] || {
                                  primaryKey: "",
                                  alternateKeys: [],
                                  foreignKeys: [],
                                };
                                return {
                                  ...prev,
                                  [sheet.name]: {
                                    ...cur,
                                    alternateKeys: [
                                      ...(cur.alternateKeys || []),
                                      {
                                        column: "",
                                        conditionColumn: "",
                                        conditionValue: "",
                                      },
                                    ],
                                  },
                                };
                              });
                            const updateAK = (
                              idx: number,
                              field: string,
                              val: string,
                            ) =>
                              setSheetKeys((prev) => {
                                const cur = prev[sheet.name] || {
                                  primaryKey: "",
                                  alternateKeys: [],
                                  foreignKeys: [],
                                };
                                const aks = [...(cur.alternateKeys || [])];
                                aks[idx] = { ...aks[idx], [field]: val };
                                return {
                                  ...prev,
                                  [sheet.name]: { ...cur, alternateKeys: aks },
                                };
                              });
                            const removeAK = (idx: number) =>
                              setSheetKeys((prev) => {
                                const cur = prev[sheet.name] || {
                                  primaryKey: "",
                                  alternateKeys: [],
                                  foreignKeys: [],
                                };
                                return {
                                  ...prev,
                                  [sheet.name]: {
                                    ...cur,
                                    alternateKeys: (
                                      cur.alternateKeys || []
                                    ).filter((_, i) => i !== idx),
                                  },
                                };
                              });
                            const otherSheets = pendingSheets.filter(
                              (s) =>
                                s.name !== sheet.name &&
                                selectedSheets.has(s.name),
                            );
                            const hasKeys =
                              sk.primaryKey ||
                              sk.foreignKeys.length > 0 ||
                              (sk.alternateKeys && sk.alternateKeys.length > 0);
                            const isKeysOpen = collapsedSheets.has(
                              `keys-${sheet.name}`,
                            );
                            const isAltOpen = collapsedSheets.has(
                              `altKeys-${sheet.name}`,
                            );

                            // Build sheet-specific placeholder from actual columns
                            const sampleCols = sheet.column_names
                              .slice(0, 4)
                              .join(", ");
                            const idCols = sheet.column_names
                              .filter((c) => /(_id|_key|_code)$/i.test(c))
                              .slice(0, 2);
                            const sheetPlaceholder =
                              idCols.length > 0
                                ? `e.g., "This sheet contains records with ${sampleCols}. Columns like ${idCols.join(", ")} may reference other tables."`
                                : `e.g., "This sheet has columns ${sampleCols}. Describe what each row represents and any known relationships."`;

                            return (
                              <div className="space-y-2">
                                {/* Context textarea */}
                                <div className="space-y-1">
                                  <label className="flex items-center gap-1.5 text-[11px] font-medium text-brand-400">
                                    <Info size={11} />
                                    Describe this sheet (helps AI understand the
                                    data)
                                  </label>
                                  <textarea
                                    value={sheetContexts[sheet.name] || ""}
                                    onChange={(e) =>
                                      updateSheetContext(
                                        sheet.name,
                                        e.target.value,
                                      )
                                    }
                                    placeholder={sheetPlaceholder}
                                    rows={2}
                                    className="w-full px-3 py-2 bg-surface-300 border border-white/10 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 resize-none transition-all"
                                  />
                                </div>

                                {/* ── PK/FK — compact inline row with expand toggle ── */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* PK inline */}
                                  <div className="flex items-center gap-1.5">
                                    <KeyRound
                                      size={11}
                                      className="text-accent-cyan flex-shrink-0"
                                    />
                                    <span className="text-[11px] text-accent-cyan font-medium">
                                      PK:
                                    </span>
                                    <select
                                      value={sk.primaryKey}
                                      onChange={(e) => updatePK(e.target.value)}
                                      className="px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-brand-500/50 transition-all appearance-none cursor-pointer min-w-[120px]"
                                      style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' fill='none' stroke='rgba(255,255,255,0.3)' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "right 8px center",
                                      }}
                                    >
                                      <option
                                        value=""
                                        className="bg-surface-300"
                                      >
                                        None
                                      </option>
                                      {sheet.column_names.map((col) => (
                                        <option
                                          key={col}
                                          value={col}
                                          className="bg-surface-300"
                                        >
                                          {col}
                                        </option>
                                      ))}
                                    </select>
                                    {/* Alt PK badge — only visible when alt keys exist */}
                                    {(sk.alternateKeys || []).length > 0 && (
                                      <button
                                        onClick={() =>
                                          toggleCollapsedSheet(
                                            `altKeys-${sheet.name}`,
                                          )
                                        }
                                        className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors"
                                        title="Conditional keys defined"
                                      >
                                        +{sk.alternateKeys.length} alt
                                      </button>
                                    )}
                                  </div>

                                  <span className="text-white/10">|</span>

                                  {/* FK toggle */}
                                  <button
                                    onClick={() =>
                                      toggleCollapsedSheet(`keys-${sheet.name}`)
                                    }
                                    className="flex items-center gap-1.5 text-[11px] font-medium text-purple-400 hover:text-purple-300 transition-colors"
                                  >
                                    <Link size={11} />
                                    FK:{" "}
                                    {sk.foreignKeys.length > 0
                                      ? `${sk.foreignKeys.length} defined`
                                      : "None"}
                                    {isKeysOpen ? (
                                      <ChevronUp size={10} />
                                    ) : (
                                      <ChevronDown size={10} />
                                    )}
                                  </button>

                                  {/* Quick add FK (always visible) */}
                                  {!isKeysOpen && (
                                    <button
                                      onClick={() => {
                                        addFK();
                                        if (!isKeysOpen)
                                          toggleCollapsedSheet(
                                            `keys-${sheet.name}`,
                                          );
                                      }}
                                      className="flex items-center gap-0.5 text-[11px] text-brand-500 hover:text-brand-400 transition-colors"
                                    >
                                      <Plus size={10} /> Add FK
                                    </button>
                                  )}

                                  {/* Spacer to push Alt PK link to the right */}
                                  <div className="flex-1" />

                                  {/* + Alt PK — compact link to add conditional keys */}
                                  <button
                                    onClick={() => {
                                      if ((sk.alternateKeys || []).length === 0)
                                        addAK();
                                      toggleCollapsedSheet(
                                        `altKeys-${sheet.name}`,
                                      );
                                    }}
                                    className="flex items-center gap-0.5 text-[11px] text-amber-500/60 hover:text-amber-400 transition-colors"
                                    title="Add conditional/alternate primary key"
                                  >
                                    <Plus size={9} /> Alt PK
                                  </button>
                                </div>

                                {/* Alternate/Conditional PK expanded section */}
                                {isAltOpen && (
                                  <div className="space-y-1.5 pl-1 border-l-2 border-amber-500/20 ml-1">
                                    <div className="flex items-center justify-between pl-2">
                                      <span className="text-[11px] text-white/25">
                                        Conditional primary keys — use alternate
                                        PK when a flag column has a specific
                                        value
                                      </span>
                                      <button
                                        onClick={addAK}
                                        className="flex items-center gap-0.5 text-[11px] text-amber-500 hover:text-amber-400 transition-colors"
                                      >
                                        <Plus size={10} /> Add
                                      </button>
                                    </div>

                                    {(sk.alternateKeys || []).length === 0 && (
                                      <p className="text-[11px] text-white/20 italic pl-2">
                                        No alternate keys. Click "Add" to define
                                        conditional PKs.
                                      </p>
                                    )}

                                    {(sk.alternateKeys || []).map(
                                      (ak, akIdx) => (
                                        <div
                                          key={akIdx}
                                          className="flex items-center gap-1.5 pl-2 py-1 rounded-lg flex-wrap"
                                          style={{
                                            background:
                                              "rgba(255,255,255,0.02)",
                                          }}
                                        >
                                          <span className="text-[11px] text-amber-400/60 flex-shrink-0">
                                            When
                                          </span>
                                          <select
                                            value={ak.conditionColumn}
                                            onChange={(e) =>
                                              updateAK(
                                                akIdx,
                                                "conditionColumn",
                                                e.target.value,
                                              )
                                            }
                                            className="px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-amber-500/50 transition-all appearance-none"
                                            title="Flag/condition column"
                                          >
                                            <option
                                              value=""
                                              className="bg-surface-300"
                                            >
                                              Flag column...
                                            </option>
                                            {sheet.column_names.map((c) => (
                                              <option
                                                key={c}
                                                value={c}
                                                className="bg-surface-300"
                                              >
                                                {c}
                                              </option>
                                            ))}
                                          </select>

                                          <span className="text-[11px] text-amber-400/60 flex-shrink-0">
                                            =
                                          </span>
                                          <input
                                            type="text"
                                            value={ak.conditionValue}
                                            onChange={(e) =>
                                              updateAK(
                                                akIdx,
                                                "conditionValue",
                                                e.target.value,
                                              )
                                            }
                                            placeholder="e.g. TRUE"
                                            className="px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white placeholder-white/20 focus:outline-none focus:border-amber-500/50 transition-all w-20"
                                            title="Condition value"
                                          />

                                          <span className="text-[11px] text-amber-400/60 flex-shrink-0">
                                            → PK:
                                          </span>
                                          <select
                                            value={ak.column}
                                            onChange={(e) =>
                                              updateAK(
                                                akIdx,
                                                "column",
                                                e.target.value,
                                              )
                                            }
                                            className="px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-amber-500/50 transition-all appearance-none min-w-[120px]"
                                            title="Alternate PK column"
                                          >
                                            <option
                                              value=""
                                              className="bg-surface-300"
                                            >
                                              PK column...
                                            </option>
                                            {sheet.column_names.map((c) => (
                                              <option
                                                key={c}
                                                value={c}
                                                className="bg-surface-300"
                                              >
                                                {c}
                                              </option>
                                            ))}
                                          </select>

                                          <button
                                            onClick={() => removeAK(akIdx)}
                                            className="p-0.5 text-white/20 hover:text-red-400 transition-colors flex-shrink-0"
                                            title="Remove alternate key"
                                          >
                                            <Trash2 size={11} />
                                          </button>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}

                                {/* FK expanded section */}
                                {isKeysOpen && (
                                  <div className="space-y-1.5 pl-1 border-l-2 border-purple-500/20 ml-1">
                                    <div className="flex items-center justify-between pl-2">
                                      <span className="text-[11px] text-white/25">
                                        Foreign key relationships
                                      </span>
                                      <button
                                        onClick={addFK}
                                        className="flex items-center gap-0.5 text-[11px] text-brand-500 hover:text-brand-400 transition-colors"
                                      >
                                        <Plus size={10} /> Add
                                      </button>
                                    </div>

                                    {sk.foreignKeys.length === 0 && (
                                      <p className="text-[11px] text-white/20 italic pl-2">
                                        No foreign keys. Click "Add" to define
                                        relationships.
                                      </p>
                                    )}

                                    {sk.foreignKeys.map((fk, fkIdx) => {
                                      const refSheet = pendingSheets.find(
                                        (s) => s.name === fk.referencesSheet,
                                      );
                                      return (
                                        <div
                                          key={fkIdx}
                                          className="flex items-center gap-1.5 pl-2 py-1 rounded-lg"
                                          style={{
                                            background:
                                              "rgba(255,255,255,0.02)",
                                          }}
                                        >
                                          <select
                                            value={fk.column}
                                            onChange={(e) =>
                                              updateFK(
                                                fkIdx,
                                                "column",
                                                e.target.value,
                                              )
                                            }
                                            className="flex-1 px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-brand-500/50 transition-all appearance-none"
                                            title="Column in this sheet"
                                          >
                                            <option
                                              value=""
                                              className="bg-surface-300"
                                            >
                                              Column...
                                            </option>
                                            {sheet.column_names.map((c) => (
                                              <option
                                                key={c}
                                                value={c}
                                                className="bg-surface-300"
                                              >
                                                {c}
                                              </option>
                                            ))}
                                          </select>

                                          <ArrowRight
                                            size={10}
                                            className="text-white/20 flex-shrink-0"
                                          />

                                          <select
                                            value={fk.referencesSheet}
                                            onChange={(e) =>
                                              updateFK(
                                                fkIdx,
                                                "referencesSheet",
                                                e.target.value,
                                              )
                                            }
                                            className="flex-1 px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-brand-500/50 transition-all appearance-none"
                                            title="Referenced sheet"
                                          >
                                            <option
                                              value=""
                                              className="bg-surface-300"
                                            >
                                              Sheet...
                                            </option>
                                            {otherSheets.map((s) => (
                                              <option
                                                key={s.name}
                                                value={s.name}
                                                className="bg-surface-300"
                                              >
                                                {s.name}
                                              </option>
                                            ))}
                                          </select>

                                          <span className="text-white/15 text-[11px] flex-shrink-0">
                                            .
                                          </span>

                                          <select
                                            value={fk.referencesColumn}
                                            onChange={(e) =>
                                              updateFK(
                                                fkIdx,
                                                "referencesColumn",
                                                e.target.value,
                                              )
                                            }
                                            className="flex-1 px-2 py-1 bg-surface-300 border border-white/10 rounded-lg text-[11px] text-white focus:outline-none focus:border-brand-500/50 transition-all appearance-none"
                                            title="Referenced column"
                                            disabled={!fk.referencesSheet}
                                          >
                                            <option
                                              value=""
                                              className="bg-surface-300"
                                            >
                                              Column...
                                            </option>
                                            {refSheet?.column_names.map((c) => (
                                              <option
                                                key={c}
                                                value={c}
                                                className="bg-surface-300"
                                              >
                                                {c}
                                              </option>
                                            ))}
                                          </select>

                                          <button
                                            onClick={() => removeFK(fkIdx)}
                                            className="p-0.5 text-white/20 hover:text-red-400 transition-colors flex-shrink-0"
                                            title="Remove foreign key"
                                          >
                                            <Trash2 size={11} />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Helpful tip */}
              <div className="glass rounded-xl p-4 flex items-start gap-3">
                <Sparkles
                  size={16}
                  className="text-brand-500 flex-shrink-0 mt-0.5"
                />
                <div className="text-xs text-white/40 space-y-1">
                  <p className="text-white/60 font-medium">
                    Pro tip: Providing context helps the AI
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Understand what each sheet/table represents</li>
                    <li>
                      Discover relationships between sheets (e.g., foreign keys)
                    </li>
                    <li>Determine if data is related or independent</li>
                    <li>Generate more accurate semantic models</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-center gap-4 pt-2">
              <button
                onClick={() => {
                  setStep(1);
                  setIsMultiSheet(false);
                  setPendingSheets([]);
                }}
                className="px-5 py-2.5 text-sm text-white/35 hover:text-white transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleProcessSheets}
                disabled={selectedSheets.size === 0}
                className="px-6 py-2.5 text-white rounded-xl font-medium text-sm transition-all flex items-center gap-2 disabled:opacity-30"
                style={{
                  background:
                    selectedSheets.size === 0
                      ? "rgba(255,255,255,0.05)"
                      : "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  boxShadow:
                    selectedSheets.size === 0
                      ? "none"
                      : "0 0 20px rgba(134,188,37,0.2)",
                }}
              >
                <FileSpreadsheet size={16} />
                Process {selectedSheets.size} sheet
                {selectedSheets.size !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 1.7: Knowledge Base (metadata, documents, skills)
           ════════════════════════════════════════════════ */}
        {step === 1.7 && (
          <div className="max-w-5xl mx-auto space-y-6 animate-fade-in py-8">
            {/* Header */}
            <div className="text-center space-y-3">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium"
                style={{
                  background: "rgba(134,188,37,0.08)",
                  borderColor: "rgba(134,188,37,0.2)",
                  color: "#86BC25",
                }}
              >
                <BookOpen size={14} />
                Knowledge Base
              </div>
              <h2 className="text-2xl font-bold text-white">
                Give the agent more to work with
              </h2>
              <p className="text-white/35 text-sm max-w-2xl mx-auto">
                Anything you provide here — column metadata, functional or
                technical documents, or a domain knowledge pack — helps the
                agent generate a more accurate semantic model. All of it is
                optional.
              </p>
            </div>

            {/* Knowledge sources, side by side */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
              {/* Column metadata / data dictionary */}
              <div className="glass rounded-xl p-5 space-y-3 text-left h-full flex flex-col">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={14} className="text-white/50" />
                  <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                    Column Metadata
                  </span>
                </div>
                <p className="text-[11px] text-white/40">
                  Upload a data dictionary (CSV, Excel, JSON, SQL DDL, or PDF)
                  with column_name, description, business_name, sensitivity.
                </p>
                {!metadataParsed ? (
                  <>
                    <label
                      className="flex flex-col items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-white/10 hover:border-brand-500/40 rounded-xl p-5 transition-all flex-1"
                      htmlFor="metadata-file-input"
                    >
                      <Upload size={20} className="text-white/20" />
                      <span className="text-xs text-white/40 text-center">
                        Drag a file here, or{" "}
                        <span className="text-brand-500">browse</span>
                      </span>
                      <input
                        id="metadata-file-input"
                        type="file"
                        accept=".csv,.xlsx,.xls,.json,.sql,.pdf"
                        className="hidden"
                        onChange={(e) =>
                          e.target.files?.[0] &&
                          handleMetadataUpload(e.target.files[0])
                        }
                      />
                    </label>
                    {metadataUploading && (
                      <p className="text-center text-xs text-white/40 animate-pulse">
                        Parsing file…
                      </p>
                    )}
                    {metadataError && (
                      <p className="text-center text-xs text-red-400">
                        {metadataError}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-surface-400/30 border border-white/8 rounded-lg">
                    <span className="text-xs text-white/70">
                      {metadataParsed.length} columns loaded
                    </span>
                    <button
                      onClick={() => setMetadataParsed(null)}
                      className="text-xs text-white/30 hover:text-white/60"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Functional / technical documents */}
              <ProjectKnowledgeUploadCard projectId={projectId} />

              {/* Business Domain — merges industry selection with knowledge-base vocabulary packs */}
              <div className="glass rounded-xl p-5 space-y-3 text-left h-full flex flex-col">
                <div className="flex items-center gap-2">
                  <Globe size={14} className="text-white/50" />
                  <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                    Business Domain
                  </span>
                </div>
                <p className="text-[11px] text-white/40">
                  Pick the industry/business domain this data comes from.
                  Options marked "vocabulary pack" also bias column suggestions
                  toward that domain's glossary.
                </p>
                <select
                  value={datasetContext.domain}
                  onChange={(e) => applyBusinessDomain(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white focus:outline-none focus:border-brand-500/50"
                >
                  <option value="">None</option>
                  {mergedDomainOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                      {skillSlugForDomain(d) ? " — vocabulary pack" : ""}
                    </option>
                  ))}
                </select>
                {datasetContext.domain && (
                  <input
                    type="text"
                    value={datasetContext.domainCustom}
                    onChange={(e) =>
                      setDatasetContext((prev) => ({
                        ...prev,
                        domainCustom: e.target.value,
                      }))
                    }
                    placeholder="Optionally refine: e.g., Commercial auto insurance..."
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                  />
                )}
              </div>
            </div>

            {/* Parsed metadata preview */}
            {metadataParsed && (
              <div className="glass rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                    {metadataParsed.length} columns loaded
                  </span>
                  <button
                    onClick={() => setMetadataParsed(null)}
                    className="text-xs text-white/30 hover:text-white/60"
                  >
                    Remove
                  </button>
                </div>
                <div className="overflow-auto max-h-64 custom-scrollbar">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-white/30 border-b border-white/5">
                        <th className="text-left py-1 pr-3">Column</th>
                        <th className="text-left py-1 pr-3">Business Name</th>
                        <th className="text-left py-1 pr-3">Description</th>
                        <th className="text-left py-1">Sensitivity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metadataParsed.slice(0, 20).map((row) => (
                        <tr
                          key={row.column_name}
                          className="border-b border-white/5 text-white/60"
                        >
                          <td className="py-1 pr-3 font-mono text-white/80">
                            {row.column_name}
                          </td>
                          <td className="py-1 pr-3">
                            {row.business_name || "—"}
                          </td>
                          <td className="py-1 pr-3 text-white/40">
                            {row.description?.slice(0, 60) || "—"}
                          </td>
                          <td className="py-1">{row.sensitivity || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {metadataParsed.length > 20 && (
                    <p className="text-xs text-white/20 text-center pt-2">
                      +{metadataParsed.length - 20} more columns
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setStep(isMultiSheet ? 1.5 : 1)}
                className="text-sm text-white/30 hover:text-white/60 flex items-center gap-1"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1.8)}
                  className="px-4 py-2 text-sm text-white/40 hover:text-white/60"
                >
                  Skip
                </button>
                <button
                  onClick={() => setStep(1.8)}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-black"
                  style={{ background: "#86BC25" }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 1.8: Dataset Context
           ════════════════════════════════════════════════ */}
        {step === 1.8 && !loading && (
          <DatasetContextStep
            datasetContext={datasetContext}
            setDatasetContext={setDatasetContext}
            onSubmit={() => handleContextSubmit(false)}
            onSkip={() => handleContextSubmit(true)}
            onBack={() => {
              setStep(1.7);
            }}
            sourceName={
              pendingUploadResult?.profile?.metadata?.source ||
              pendingFileName ||
              "Your Dataset"
            }
            allColumns={ctxColumns}
            sheetNames={ctxSheets}
          />
        )}

        {/* ════════════════════════════════════════════════
           STEP 2: Auto-Profile (loading state)
           ════════════════════════════════════════════════ */}
        {step === 2 && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center space-y-4">
              <div
                className="w-14 h-14 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto"
                style={{ boxShadow: "0 0 30px rgba(134,188,37,0.2)" }}
              />
              <p className="text-lg text-white/80 font-medium">
                Analyzing Your Dataset
              </p>
              <p className="text-sm text-white/30">
                Profiling the data and gathering deep understanding...
              </p>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 3: Smart Q&A (Full-width Q&A + collapsible profile sidebar)
           ════════════════════════════════════════════════ */}
        {step === 3 && profile && sessionId && !svLoading && (
          <div
            className="animate-fade-in flex"
            style={{ minHeight: "calc(100vh - 250px)" }}
          >
            {/* ── Collapsible Profile Sidebar ── */}
            <div
              className={`transition-all duration-300 ease-in-out flex-shrink-0 overflow-hidden ${
                profileSidebarOpen ? "w-[380px]" : "w-0"
              }`}
            >
              <div
                className="w-[380px] h-full overflow-y-auto custom-scrollbar space-y-5 pr-4 pl-1 py-1"
                style={{ maxHeight: "calc(100vh - 250px)" }}
              >
                {/* Sheet selector for multi-sheet */}
                {isProfileMultiSheet && (
                  <div className="glass rounded-xl p-3 relative z-20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Layers size={14} className="text-brand-500" />
                        <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                          Sheet View
                        </span>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() =>
                            setSheetDropdownOpen(!sheetDropdownOpen)
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-surface-200 hover:bg-surface-300 border border-white/10 rounded-lg text-sm text-white transition-all min-w-[200px] justify-between"
                        >
                          <span className="truncate">
                            {activeSheet === "all"
                              ? `All Combined (${sheetNames.length} sheets)`
                              : activeSheet}
                          </span>
                          <ChevronDown
                            size={14}
                            className={`text-white/40 flex-shrink-0 transition-transform ${sheetDropdownOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {sheetDropdownOpen && (
                          <div className="absolute right-0 top-full mt-1 z-50 bg-surface-300 border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[280px] max-h-[300px] overflow-y-auto custom-scrollbar">
                            <button
                              onClick={() => {
                                setActiveSheet("all");
                                setSheetDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2.5 text-xs flex items-center justify-between hover:bg-surface-200 transition border-b border-white/5 ${
                                activeSheet === "all"
                                  ? "text-brand-500 font-semibold bg-brand-500/5"
                                  : "text-white/60"
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <Layers size={12} /> All Combined
                              </span>
                              <span className="text-[11px] text-white/25">
                                {sheetNames.length} sheets
                              </span>
                            </button>
                            {sheetNames.map((sn) => {
                              const detail =
                                profile.metadata.sheet_details?.find(
                                  (d) => d.name === sn,
                                );
                              return (
                                <button
                                  key={sn}
                                  onClick={() => {
                                    setActiveSheet(sn);
                                    setSheetDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-4 py-2.5 text-xs flex items-center justify-between hover:bg-surface-200 transition ${
                                    activeSheet === sn
                                      ? "text-brand-500 font-semibold bg-brand-500/5"
                                      : "text-white/60"
                                  }`}
                                >
                                  <span className="truncate mr-3">{sn}</span>
                                  {detail && (
                                    <span className="text-[11px] text-white/25 flex-shrink-0">
                                      {detail.rows}r × {detail.columns}c
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary cards */}
                {(() => {
                  const currentSchema_ =
                    isProfileMultiSheet && activeSheet !== "all"
                      ? (profile.per_sheet_profiles![activeSheet]?.schema ??
                        profile.schema)
                      : profile.schema;
                  const currentQuality_ =
                    isProfileMultiSheet && activeSheet !== "all"
                      ? (profile.per_sheet_profiles![activeSheet]?.quality ??
                        profile.quality)
                      : profile.quality;
                  const currentMeta_ =
                    isProfileMultiSheet && activeSheet !== "all"
                      ? (profile.per_sheet_profiles![activeSheet]?.metadata ??
                        profile.metadata)
                      : profile.metadata;

                  return (
                    <>
                      {/* Overview (rows/cols + quality bars) */}
                      <div className="glass rounded-xl overflow-hidden">
                        <button
                          onClick={() =>
                            setPanelCollapsed((p) => ({
                              ...p,
                              overview: !p.overview,
                            }))
                          }
                          className="w-full flex items-center justify-between px-4 py-2.5 border-b border-surface-200/50 hover:bg-white/5 transition"
                        >
                          <div className="flex items-center gap-2">
                            <BarChart3 size={13} className="text-brand-500" />
                            <span className="text-xs font-semibold text-gray-300">
                              Overview
                            </span>
                          </div>
                          <ChevronDown
                            size={14}
                            className={`text-white/30 transition-transform ${panelCollapsed.overview ? "" : "rotate-180"}`}
                          />
                        </button>
                        {!panelCollapsed.overview && (
                          <div className="p-3 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-surface-200/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-gray-400 mb-1">
                                  <Table2 size={14} />
                                  <span className="text-xs font-medium">
                                    Rows
                                  </span>
                                </div>
                                <p className="text-lg font-bold text-white">
                                  {currentMeta_.rows.toLocaleString()}
                                </p>
                              </div>
                              <div className="bg-surface-200/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-gray-400 mb-1">
                                  <Hash size={14} />
                                  <span className="text-xs font-medium">
                                    Columns
                                  </span>
                                </div>
                                <p className="text-lg font-bold text-white">
                                  {currentMeta_.columns}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              {(
                                [
                                  "completeness",
                                  "uniqueness",
                                  "consistency",
                                ] as const
                              ).map((dim) => (
                                <div key={dim} className="space-y-1.5">
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-gray-400 capitalize">
                                      {dim}
                                    </span>
                                    <span
                                      className={qualityColor(
                                        currentQuality_[dim].score,
                                      )}
                                    >
                                      {currentQuality_[dim].score}%
                                    </span>
                                  </div>
                                  <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-700 ${qualityBg(currentQuality_[dim].score)}`}
                                      style={{
                                        width: `${currentQuality_[dim].score}%`,
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Data Quality Flags — actionable column-risk summary
                       * (replaces the old per-column completeness bar chart,
                       * which was unreadable past a handful of columns and
                       * didn't help answer the "known data quality issues"
                       * question this step is actually asking). */}
                      {(() => {
                        const cols = currentSchema_.filter(
                          (c) => c.column !== "__source_sheet__",
                        );
                        const empty = cols.filter((c) => c.null_pct === 100);
                        const highNull = cols.filter(
                          (c) => c.null_pct > 20 && c.null_pct < 100,
                        );
                        const constant = cols.filter(
                          (c) => c.unique_count <= 1 && c.null_pct < 100,
                        );
                        const idLike = cols.filter(
                          (c) => c.cardinality_ratio >= 0.95,
                        );
                        const flagged = [...empty, ...highNull]
                          .sort((a, b) => b.null_pct - a.null_pct)
                          .slice(0, 5);
                        return (
                          <div className="glass rounded-xl overflow-hidden">
                            <button
                              onClick={() =>
                                setPanelCollapsed((p) => ({
                                  ...p,
                                  dq: !p.dq,
                                }))
                              }
                              className="w-full flex items-center justify-between px-4 py-2.5 border-b border-surface-200/50 hover:bg-white/5 transition"
                            >
                              <div className="flex items-center gap-2">
                                <AlertTriangle
                                  size={13}
                                  className="text-brand-500"
                                />
                                <span className="text-xs font-semibold text-gray-300">
                                  Data Quality Flags
                                </span>
                              </div>
                              <ChevronDown
                                size={14}
                                className={`text-white/30 transition-transform ${panelCollapsed.dq ? "" : "rotate-180"}`}
                              />
                            </button>
                            {!panelCollapsed.dq && (
                              <div className="p-3 space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { label: "Empty columns", n: empty.length },
                                    {
                                      label: ">20% missing",
                                      n: highNull.length,
                                    },
                                    {
                                      label: "Single-value",
                                      n: constant.length,
                                    },
                                    {
                                      label: "Likely ID/key",
                                      n: idLike.length,
                                    },
                                  ].map((s) => (
                                    <div
                                      key={s.label}
                                      className="bg-surface-200/50 rounded-lg p-2.5"
                                    >
                                      <p
                                        className={`text-lg font-bold ${s.n > 0 ? "text-yellow-400" : "text-white"}`}
                                      >
                                        {s.n}
                                      </p>
                                      <p className="text-[11px] text-gray-400">
                                        {s.label}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                                {flagged.length > 0 && (
                                  <div className="space-y-1">
                                    <p className="text-[11px] text-white/30 uppercase tracking-wider">
                                      Needs attention
                                    </p>
                                    {flagged.map((c) => (
                                      <div
                                        key={c.column}
                                        className="flex items-center justify-between text-[11px]"
                                      >
                                        <span className="font-mono text-gray-300 truncate mr-2">
                                          {c.column}
                                        </span>
                                        <span className="text-yellow-400 flex-shrink-0">
                                          {c.null_pct}% null
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Column Types — compact type-mix summary (replaces
                       * the old full 1-row-per-column schema table, which
                       * wasn't practical to scan for wide datasets). */}
                      {(() => {
                        const cols = currentSchema_.filter(
                          (c) => c.column !== "__source_sheet__",
                        );
                        const buckets: Record<string, number> = {
                          Numeric: 0,
                          Date: 0,
                          Boolean: 0,
                          Text: 0,
                        };
                        for (const c of cols) {
                          const d = c.dtype.toLowerCase();
                          if (d.includes("int") || d.includes("float"))
                            buckets.Numeric++;
                          else if (d.includes("datetime")) buckets.Date++;
                          else if (d.includes("bool")) buckets.Boolean++;
                          else buckets.Text++;
                        }
                        const icons: Record<string, React.ReactNode> = {
                          Numeric: (
                            <Hash size={13} className="text-accent-cyan" />
                          ),
                          Date: (
                            <Calendar size={13} className="text-purple-400" />
                          ),
                          Boolean: (
                            <CheckCircle2
                              size={13}
                              className="text-green-400"
                            />
                          ),
                          Text: <Type size={13} className="text-orange-400" />,
                        };
                        return (
                          <div className="glass rounded-xl overflow-hidden">
                            <button
                              onClick={() =>
                                setPanelCollapsed((p) => ({
                                  ...p,
                                  types: !p.types,
                                }))
                              }
                              className="w-full flex items-center justify-between px-4 py-2.5 border-b border-surface-200 hover:bg-white/5 transition"
                            >
                              <div className="flex items-center gap-2">
                                <Table2 size={13} className="text-brand-500" />
                                <h3 className="text-xs font-semibold text-gray-300">
                                  Column Types
                                </h3>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-white/30">
                                  {cols.length} columns
                                </span>
                                <ChevronDown
                                  size={14}
                                  className={`text-white/30 transition-transform ${panelCollapsed.types ? "" : "rotate-180"}`}
                                />
                              </div>
                            </button>
                            {!panelCollapsed.types && (
                              <div className="p-3 space-y-2">
                                {Object.entries(buckets)
                                  .filter(([, n]) => n > 0)
                                  .map(([label, n]) => (
                                    <div
                                      key={label}
                                      className="flex items-center gap-2"
                                    >
                                      {icons[label]}
                                      <span className="text-xs text-gray-300 flex-1">
                                        {label}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {n}
                                      </span>
                                      <div className="w-16 h-1.5 bg-surface-200 rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full bg-brand-500"
                                          style={{
                                            width: `${(n / (cols.length || 1)) * 100}%`,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}

                {/* Handover to Data Profiler agent — this sidebar only shows a
                    quick profile; deep-link out instead of duplicating the
                    Profiler's PII detection / DQ rules / full run flow here. */}
                {projectId && (
                  <div className="glass rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-white/50 min-w-0">
                      <Microscope size={14} className="flex-shrink-0" />
                      <span className="text-xs text-white/60">
                        Need deeper PII detection, DQ rules, or full column
                        stats? Hand over to the Data Profiler agent.
                      </span>
                    </div>
                    <button
                      onClick={() =>
                        openAgentInNewTab(
                          `/projects/${projectId}/agents/data-profiler`,
                        )
                      }
                      className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                    >
                      <Microscope size={12} /> Open Data Profiler
                      <ArrowUpRight size={12} className="text-white/40" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Full-width Q&A Area ── */}
            <div className="flex-1 min-w-0 flex flex-col gap-4">
              {/* Header bar with sidebar toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStep(1.8)}
                  title="Back to Dataset Context"
                  className="flex items-center gap-1.5 px-3 py-2 glass rounded-lg text-xs font-medium text-white/50 hover:text-white/80 hover:bg-white/10 transition border border-white/10"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  onClick={() => setProfileSidebarOpen((p) => !p)}
                  className="flex items-center gap-2 px-3 py-2 glass rounded-lg text-xs font-medium hover:bg-white/10 transition border border-white/10"
                  title={
                    profileSidebarOpen
                      ? "Hide data profile"
                      : "Show data profile"
                  }
                >
                  <BarChart3 size={14} className="text-brand-500" />
                  <span className="text-white/70">
                    {profileSidebarOpen ? "Hide Profile" : "Data Profile"}
                  </span>
                  <ChevronLeft
                    size={12}
                    className={`text-white/30 transition-transform ${profileSidebarOpen ? "" : "rotate-180"}`}
                  />
                </button>
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-7 h-7 rounded-lg bg-brand-500/20 flex items-center justify-center">
                    <ClipboardCheck size={14} className="text-brand-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">
                      Analytical Requirements
                    </h3>
                    <p className="text-[11px] text-white/30">
                      Answer to guide semantic model generation
                    </p>
                  </div>
                </div>
                {Object.values(memoryAnswers).some((v) => v) && (
                  <span className="flex items-center gap-1 text-[11px] text-brand-400 bg-brand-500/10 px-2 py-1 rounded-full border border-brand-500/20">
                    <Clock size={9} /> Answers from memory
                  </span>
                )}
                <input
                  ref={qaFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => void handleImportQaFile(e)}
                  className="hidden"
                />
                <button
                  onClick={handleImportQaClick}
                  title="Import a completed Excel workbook of these questions & answers"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/50 hover:text-white/80 border border-white/10 rounded-lg transition-colors"
                >
                  <Upload size={12} /> Import
                </button>
                <button
                  onClick={() => void handleDownloadQaAnswers()}
                  disabled={qaDownloading || questionsLoading}
                  title="Download these questions & answers as an Excel workbook to fill in offline or share"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/50 hover:text-white/80 border border-white/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Download size={12} />{" "}
                  {qaDownloading ? "Preparing…" : "Download Excel"}
                </button>
              </div>
              {qaImportError && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {qaImportError}
                </div>
              )}

              {/* Q&A Form panel */}
              <div
                className="glass rounded-xl overflow-hidden flex-1 flex flex-col min-h-0"
                style={{ maxHeight: "calc(100vh - 310px)" }}
              >
                {/* Questions / loading skeleton */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-5 custom-scrollbar">
                  {questionsLoading ? (
                    /* Loading skeleton while LLM analyzes the data */
                    <div className="flex flex-col items-center justify-center h-full min-h-[280px] space-y-4 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                        <Loader2
                          size={20}
                          className="animate-spin text-brand-500"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white/80">
                          Analyzing your dataset…
                        </p>
                        <p className="text-xs text-white/30 mt-1">
                          Generating questions tailored to your data
                        </p>
                      </div>
                      <div className="w-48 space-y-2 mt-2">
                        {[0.9, 0.7, 0.8, 0.6].map((w, i) => (
                          <div
                            key={i}
                            className="h-2 bg-surface-200/60 rounded-full animate-pulse"
                            style={{
                              width: `${w * 100}%`,
                              opacity: 1 - i * 0.15,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    qaQuestions.map((q, idx) => {
                      const isFromMemory = Boolean(memoryAnswers[q.id]);
                      const isSuggested =
                        !isFromMemory &&
                        Boolean(q.suggested_answer) &&
                        qaAnswers[q.id] === q.suggested_answer;
                      return (
                        <div key={q.id} className="space-y-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-500/20 text-brand-400 text-[11px] font-bold flex items-center justify-center">
                                {idx + 1}
                              </span>
                              <label className="text-xs font-medium text-white/85 leading-tight">
                                {q.question}
                                {q.required && (
                                  <span className="text-red-400 ml-0.5">*</span>
                                )}
                              </label>
                            </div>
                            <span className="text-[11px] uppercase tracking-wider text-white/20 flex-shrink-0 mt-0.5">
                              {q.category}
                            </span>
                          </div>

                          {(isFromMemory || isSuggested) && (
                            <div className="pl-7">
                              {isFromMemory ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-brand-400 bg-brand-500/10 px-1.5 py-0.5 rounded border border-brand-500/20">
                                  <Clock size={8} /> Saved from memory
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
                                  <Wand2 size={8} /> AI suggested — review &amp;
                                  confirm
                                </span>
                              )}
                            </div>
                          )}

                          <div className="pl-7">
                            {q.type === "select" &&
                            Array.isArray(q.options) &&
                            q.options.length >= 2 ? (
                              <select
                                value={qaAnswers[q.id] ?? ""}
                                onChange={(e) =>
                                  setQaAnswers((p) => ({
                                    ...p,
                                    [q.id]: e.target.value,
                                  }))
                                }
                                className="w-full px-3 py-2 bg-surface-300 border border-surface-200 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500 appearance-none cursor-pointer"
                              >
                                <option value="">Select an option...</option>
                                {q.options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : q.type === "text" ? (
                              <input
                                type="text"
                                value={qaAnswers[q.id] ?? ""}
                                onChange={(e) =>
                                  setQaAnswers((p) => ({
                                    ...p,
                                    [q.id]: e.target.value,
                                  }))
                                }
                                placeholder={q.placeholder}
                                className="w-full px-3 py-2 bg-surface-300 border border-surface-200 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500"
                              />
                            ) : (
                              <textarea
                                value={qaAnswers[q.id] ?? ""}
                                onChange={(e) =>
                                  setQaAnswers((p) => ({
                                    ...p,
                                    [q.id]: e.target.value,
                                  }))
                                }
                                placeholder={q.placeholder}
                                rows={3}
                                className="w-full px-3 py-2 bg-surface-300 border border-surface-200 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 resize-none"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer: progress + Confirm / Generate buttons */}
                <div
                  className="flex-shrink-0 p-4 border-t border-surface-200 space-y-2"
                  style={{ background: "rgba(14,14,24,0.95)" }}
                >
                  {(() => {
                    const required = qaQuestions.filter((q) => q.required);
                    const answeredCount = required.filter((q) =>
                      qaAnswers[q.id]?.trim(),
                    ).length;
                    const allRequired =
                      required.length > 0 && answeredCount === required.length;
                    return (
                      <>
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="text-white/30">
                            {questionsLoading
                              ? "Loading questions…"
                              : `${answeredCount}/${required.length} required fields`}
                          </span>
                          {allRequired && !questionsLoading && (
                            <span className="text-brand-400 font-medium">
                              {qaConfirmed ? "Confirmed" : "Ready to confirm"}
                            </span>
                          )}
                        </div>
                        <div className="h-1 bg-surface-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-500 rounded-full transition-all duration-300"
                            style={{
                              width: required.length
                                ? `${(answeredCount / required.length) * 100}%`
                                : "0%",
                            }}
                          />
                        </div>
                        {qaSummary && (
                          <div className="mt-2 px-3 py-2 bg-brand-500/10 border border-brand-500/20 rounded-lg">
                            <p className="text-[11px] text-brand-300">
                              {qaSummary}
                            </p>
                          </div>
                        )}
                        {!qaConfirmed ? (
                          <button
                            onClick={handleConfirmAnswers}
                            disabled={
                              !allRequired ||
                              questionsLoading ||
                              qaFollowupLoading
                            }
                            className="w-full py-2.5 mt-2 bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 disabled:from-gray-700 disabled:to-gray-700 disabled:text-gray-500 text-white rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2"
                          >
                            {qaFollowupLoading ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />{" "}
                                Reviewing your answers…
                              </>
                            ) : (
                              <>
                                <CheckCircle2 size={16} /> Confirm Answers
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            data-testid="semantic-generate-btn"
                            onClick={handleGenerateWithQA}
                            disabled={
                              !allRequired ||
                              questionsLoading ||
                              qaLoading ||
                              svLoading
                            }
                            className="w-full py-2.5 mt-2 bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 disabled:from-gray-700 disabled:to-gray-700 disabled:text-gray-500 text-white rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2"
                          >
                            {qaLoading ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />{" "}
                                Saving answers…
                              </>
                            ) : svLoading ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />{" "}
                                Generating semantic view…
                              </>
                            ) : (
                              <>
                                <Sparkles size={16} /> Generate Semantic View
                              </>
                            )}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 4: Semantic View
           ════════════════════════════════════════════════ */}
        {step === 4 &&
          semanticView &&
          sessionId &&
          (() => {
            const sv = (semanticView.semantic_view as Record<string, any>)
              ?.semantic_view;
            const entities: any[] = sv?.entities ?? [];
            const factEntities = entities.filter(
              (e) => e.entity_type?.toLowerCase() === "fact",
            );
            const dimEntities = entities.filter(
              (e) => e.entity_type?.toLowerCase() === "dimension",
            );
            const relationships: any[] = sv?.relationships ?? [];
            const metrics: any[] = sv?.metric_definitions ?? [];
            const totalCols = entities.reduce(
              (n: number, e: any) => n + (e.columns?.length || 0),
              0,
            );
            const measureCols = entities.flatMap((e: any) =>
              (e.columns ?? []).filter(
                (c: any) => c.semantic_type === "Measure" || c.aggregation,
              ),
            );

            return (
              <div className="space-y-5 animate-fade-in">
                {/* ── Tabs: Overview / Business Glossary / ER Diagram ── */}
                <div className="flex items-center gap-1.5">
                  {(
                    [
                      {
                        key: "overview",
                        label: "Dimensional Model Overview",
                        icon: Database,
                      },
                      {
                        key: "glossary",
                        label: "Business Glossary",
                        icon: BookOpen,
                      },
                      {
                        key: "erd",
                        label: "Entity Relationship Diagram",
                        icon: GitBranch,
                      },
                      {
                        key: "canvas",
                        label: "ER Canvas",
                        icon: LayoutGrid,
                      },
                      {
                        key: "metrics",
                        label: "Metrics",
                        icon: Sigma,
                      },
                    ] as const
                  ).map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setModelViewTab(key)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                        modelViewTab === key
                          ? "bg-brand-500/15 text-brand-400 border border-brand-500/30"
                          : "text-white/40 hover:text-white/70 border border-transparent hover:bg-white/5"
                      }`}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  ))}
                </div>

                {modelViewTab === "overview" && (
                  <div className="glass rounded-xl border border-brand-500/20 overflow-hidden">
                    <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
                      <Database size={16} className="text-brand-500" />
                      <h3 className="text-sm font-bold text-white">
                        Dimensional Model Overview
                      </h3>
                      <span className="ml-auto text-[11px] text-white/25">
                        {sv?.name || "Semantic View"}
                      </span>
                    </div>
                    <div className="p-5">
                      {/* Stats row — click a stat to open its detail panel below */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
                        <button
                          type="button"
                          data-testid="semantic-stat-entities"
                          onClick={() =>
                            setOverviewTab((t) =>
                              t === "entities" ? null : "entities",
                            )
                          }
                          className={`bg-surface-200/50 rounded-lg p-3 text-center transition-all hover:bg-surface-200/80 focus:outline-none focus:ring-2 focus:ring-white/30 ${overviewTab === "entities" ? "ring-2 ring-white/40" : ""}`}
                        >
                          <div className="text-xl font-bold text-white">
                            {entities.length}
                          </div>
                          <div className="text-[11px] text-white/30 mt-0.5">
                            Entities
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setOverviewTab((t) =>
                              t === "facts" ? null : "facts",
                            )
                          }
                          className={`bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center transition-all hover:bg-orange-500/20 focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${overviewTab === "facts" ? "ring-2 ring-orange-400/60" : ""}`}
                        >
                          <div className="text-xl font-bold text-orange-400">
                            {factEntities.length}
                          </div>
                          <div className="text-[11px] text-orange-400/50 mt-0.5">
                            Fact Tables
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setOverviewTab((t) =>
                              t === "dimensions" ? null : "dimensions",
                            )
                          }
                          className={`bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-center transition-all hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${overviewTab === "dimensions" ? "ring-2 ring-blue-400/60" : ""}`}
                        >
                          <div className="text-xl font-bold text-blue-400">
                            {dimEntities.length}
                          </div>
                          <div className="text-[11px] text-blue-400/50 mt-0.5">
                            Dimensions
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setOverviewTab((t) =>
                              t === "columns" ? null : "columns",
                            )
                          }
                          className={`bg-surface-200/50 rounded-lg p-3 text-center transition-all hover:bg-surface-200/80 focus:outline-none focus:ring-2 focus:ring-white/30 ${overviewTab === "columns" ? "ring-2 ring-white/40" : ""}`}
                        >
                          <div className="text-xl font-bold text-white">
                            {totalCols}
                          </div>
                          <div className="text-[11px] text-white/30 mt-0.5">
                            Columns
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setOverviewTab((t) =>
                              t === "measures" ? null : "measures",
                            )
                          }
                          className={`bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center transition-all hover:bg-green-500/20 focus:outline-none focus:ring-2 focus:ring-green-500/30 ${overviewTab === "measures" ? "ring-2 ring-green-400/60" : ""}`}
                        >
                          <div className="text-xl font-bold text-green-400">
                            {measureCols.length}
                          </div>
                          <div className="text-[11px] text-green-400/50 mt-0.5">
                            Measures
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setOverviewTab((t) =>
                              t === "relationships" ? null : "relationships",
                            )
                          }
                          className={`bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 text-center transition-all hover:bg-purple-500/20 focus:outline-none focus:ring-2 focus:ring-purple-500/30 ${overviewTab === "relationships" ? "ring-2 ring-purple-400/60" : ""}`}
                        >
                          <div className="text-xl font-bold text-purple-400">
                            {relationships.length}
                          </div>
                          <div className="text-[11px] text-purple-400/50 mt-0.5">
                            Relationships
                          </div>
                        </button>
                      </div>

                      {/* Detail panel for whichever stat was clicked — capped
                        height + scroll so a large model never blows out the
                        page's scroll length. */}
                      {overviewTab ? (
                        <div className="bg-surface-300/30 rounded-lg p-4 max-h-96 overflow-y-auto">
                          {overviewTab === "facts" && (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-[11px] text-white/25 uppercase tracking-wider font-semibold">
                                  Fact Tables ({factEntities.length})
                                </div>
                                <TableDownloadButtons
                                  title="Fact Tables"
                                  headers={[
                                    "Entity",
                                    "Type",
                                    "Grain",
                                    "Columns",
                                  ]}
                                  rows={factEntities.map((f: any) => [
                                    f.name ?? "",
                                    "Fact",
                                    f.grain ?? "",
                                    f.columns?.length || 0,
                                  ])}
                                  filenameBase="fact-tables"
                                />
                              </div>
                              {factEntities.length === 0 ? (
                                <p className="text-xs text-white/30 italic">
                                  No fact tables detected.
                                </p>
                              ) : (
                                <div className="flex flex-col gap-3">
                                  {factEntities.map((f: any) => (
                                    <div key={f.name} className="space-y-2">
                                      <div className="flex items-center gap-2 p-2.5 bg-orange-500/10 border border-orange-500/25 rounded-lg">
                                        <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                                        <span className="text-xs font-bold text-orange-400 flex-1">
                                          {f.name}
                                        </span>
                                        <span className="text-[11px] text-orange-400/40 uppercase tracking-wider font-semibold">
                                          Fact
                                        </span>
                                        <span className="text-[11px] text-white/20">
                                          {f.columns?.length || 0} cols
                                        </span>
                                      </div>
                                      {f.grain && (
                                        <div className="ml-4 text-[11px] text-white/30">
                                          <span className="text-white/20">
                                            Grain:
                                          </span>{" "}
                                          {f.grain}
                                        </div>
                                      )}
                                      <div className="ml-4 flex flex-wrap gap-2">
                                        {(f.foreign_keys ?? [])
                                          .filter(
                                            (fk: any) =>
                                              typeof fk === "object" &&
                                              fk !== null,
                                          )
                                          .map((fk: any, i: number) => {
                                            const col =
                                              fk.column ||
                                              fk.source_column ||
                                              "";
                                            const refEntity =
                                              fk.ref_table ||
                                              fk.references_entity ||
                                              fk.target_entity ||
                                              fk.target_table ||
                                              fk.references_table ||
                                              "";
                                            if (!col && !refEntity) return null;
                                            return (
                                              <div
                                                key={i}
                                                className="flex items-center gap-1.5 text-[11px]"
                                              >
                                                <span className="text-white/15">
                                                  ├─
                                                </span>
                                                <code className="text-white/30">
                                                  {col}
                                                </code>
                                                <span className="text-white/10">
                                                  →
                                                </span>
                                                <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded">
                                                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                                  <span className="text-blue-400/80 font-medium">
                                                    {refEntity}
                                                  </span>
                                                </div>
                                                <span className="text-white/15 text-[11px]">
                                                  {fk.relationship ||
                                                    fk.cardinality ||
                                                    "1:N"}
                                                </span>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {overviewTab === "dimensions" && (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-[11px] text-white/25 uppercase tracking-wider font-semibold">
                                  Dimensions ({dimEntities.length})
                                </div>
                                <TableDownloadButtons
                                  title="Dimensions"
                                  headers={["Entity", "Type", "Columns"]}
                                  rows={dimEntities.map((d: any) => [
                                    d.name ?? "",
                                    "Dimension",
                                    d.columns?.length || 0,
                                  ])}
                                  filenameBase="dimensions"
                                />
                              </div>
                              {dimEntities.length === 0 ? (
                                <p className="text-xs text-white/30 italic">
                                  No dimensions detected.
                                </p>
                              ) : (
                                <div className="flex flex-col gap-2">
                                  {dimEntities.map((d: any) => (
                                    <div
                                      key={d.name}
                                      className="flex items-center gap-2 p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                                    >
                                      <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                                      <span className="text-xs font-medium text-blue-400/80 flex-1">
                                        {d.name}
                                      </span>
                                      <span className="text-[11px] text-blue-400/40 uppercase tracking-wider font-semibold">
                                        Dim
                                      </span>
                                      <span className="text-[11px] text-white/20">
                                        {d.columns?.length || 0} cols
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {overviewTab === "entities" && (
                            <p className="text-xs text-white/30 italic">
                              See Entity Details below for the full list.
                            </p>
                          )}

                          {overviewTab === "columns" && (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-[11px] text-white/25 uppercase tracking-wider font-semibold">
                                  Columns ({totalCols})
                                </div>
                                <TableDownloadButtons
                                  title="Columns"
                                  headers={["Entity", "Column", "Type"]}
                                  rows={entities.flatMap((e: any) =>
                                    (e.columns ?? []).map((c: any) => [
                                      e.name ?? "",
                                      c.technical_name ?? "",
                                      c.semantic_type ?? "",
                                    ]),
                                  )}
                                  filenameBase="columns"
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                {entities.flatMap((e: any) =>
                                  (e.columns ?? []).map((c: any, i: number) => (
                                    <div
                                      key={`${e.name}-${i}`}
                                      className="flex items-center gap-2 text-[11px] px-2 py-1 rounded hover:bg-white/5"
                                    >
                                      <span className="text-white/25">
                                        {e.name}
                                      </span>
                                      <span className="text-white/15">.</span>
                                      <code className="text-white/60">
                                        {c.technical_name}
                                      </code>
                                      {c.semantic_type && (
                                        <span className="ml-auto text-white/30">
                                          {c.semantic_type}
                                        </span>
                                      )}
                                    </div>
                                  )),
                                )}
                              </div>
                            </>
                          )}

                          {overviewTab === "measures" && (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-[11px] text-white/25 uppercase tracking-wider font-semibold">
                                  Measures ({measureCols.length})
                                </div>
                                <TableDownloadButtons
                                  title="Measures"
                                  headers={["Measure", "Aggregation"]}
                                  rows={measureCols.map((m: any) => [
                                    m.business_name || m.technical_name || "",
                                    m.aggregation ?? "",
                                  ])}
                                  filenameBase="measures"
                                />
                              </div>
                              {measureCols.length === 0 ? (
                                <p className="text-xs text-white/30 italic">
                                  No measures detected.
                                </p>
                              ) : (
                                <div className="flex flex-wrap gap-1.5">
                                  {measureCols.map((m: any, i: number) => (
                                    <span
                                      key={i}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 border border-green-500/15 rounded-lg text-[11px] text-green-400/70"
                                    >
                                      {m.business_name || m.technical_name}
                                      {m.aggregation && (
                                        <span className="text-green-400/40 text-[11px] font-mono">
                                          ({m.aggregation})
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {overviewTab === "relationships" && (
                            <>
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-[11px] text-white/25 uppercase tracking-wider font-semibold">
                                  Relationships ({relationships.length})
                                </div>
                                <TableDownloadButtons
                                  title="Relationships"
                                  headers={[
                                    "From Entity",
                                    "From Column",
                                    "To Entity",
                                    "To Column",
                                    "Cardinality",
                                  ]}
                                  rows={relationships.map((r: any) => {
                                    const n = normalizeRelationship(r);
                                    return [
                                      n.fromEntity,
                                      n.fromColumn,
                                      n.toEntity,
                                      n.toColumn,
                                      n.cardinality,
                                    ];
                                  })}
                                  filenameBase="relationships"
                                />
                              </div>
                              {relationships.length === 0 ? (
                                <p className="text-xs text-white/30 italic">
                                  No relationships detected.
                                </p>
                              ) : (
                                <div className="flex flex-col gap-1.5">
                                  {relationships.map((r: any, i: number) => {
                                    const n = normalizeRelationship(r);
                                    return (
                                      <div
                                        key={i}
                                        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-white/50 px-2 py-1"
                                      >
                                        <code className="bg-surface-200 px-1.5 py-0.5 rounded text-purple-300 break-all">
                                          {n.fromEntity}.{n.fromColumn}
                                        </code>
                                        <span className="text-white/20 flex-shrink-0">
                                          →
                                        </span>
                                        <code className="bg-surface-200 px-1.5 py-0.5 rounded text-purple-300 break-all">
                                          {n.toEntity}.{n.toColumn}
                                        </code>
                                        <span className="ml-auto flex-shrink-0 text-white/30 px-1.5 py-0.5 rounded bg-white/5">
                                          {cardinalityLabel(n.cardinality)}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-[11px] text-white/25 text-center py-2">
                          Click a stat above to see its details.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Entity Detail Cards — only for the "Entities" stat ── */}
                {modelViewTab === "overview" && overviewTab === "entities" && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 px-1 mb-3">
                      <Table2 size={14} className="text-brand-500" />
                      <h3 className="text-xs font-bold text-white/60 uppercase tracking-wider">
                        Entity Details
                      </h3>
                      <div className="ml-auto">
                        <TableDownloadButtons
                          title="Entity Details"
                          headers={[
                            "Entity",
                            "Column",
                            "Business Term",
                            "Domain",
                            "Type",
                            "Sensitivity",
                          ]}
                          rows={entities.flatMap((e: any) =>
                            (e.columns ?? []).map((c: any) => [
                              e.name ?? "",
                              c.technical_name ?? "",
                              c.business_name ?? "",
                              c.domain ?? "",
                              c.semantic_type ?? "",
                              c.sensitivity ?? "",
                            ]),
                          )}
                          filenameBase="entity-details"
                        />
                      </div>
                    </div>
                    {addEntityFormUI}
                    <div className="space-y-1 max-h-[600px] overflow-y-auto custom-scrollbar pr-1">
                      {entities.map((entity: any, idx: number) => (
                        <EntityCard
                          key={idx}
                          entity={entity}
                          defaultOpen={entities.length <= 3}
                          sessionId={sessionId ?? undefined}
                          allEntities={entities}
                          onSaved={(wrapper) =>
                            setSemanticView((prev: any) =>
                              prev
                                ? { ...prev, semantic_view: wrapper }
                                : { semantic_view: wrapper },
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Glossary — collapsible */}
                {modelViewTab === "glossary" && (
                  <CollapsibleSection
                    icon={<BookOpen size={14} className="text-brand-500" />}
                    title="Business Glossary"
                    badge={`${totalCols} terms`}
                    defaultOpen={true}
                  >
                    <GlossaryTable entities={entities} />
                  </CollapsibleSection>
                )}

                {/* ER Diagram — rendered Mermaid */}
                {modelViewTab === "erd" && (
                  <CollapsibleSection
                    icon={<GitBranch size={14} className="text-brand-500" />}
                    title="Entity Relationship Diagram"
                    badge={`${entities.length} entities`}
                    defaultOpen={true}
                    extra={
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              semanticView.er_diagram_markdown || "",
                            );
                          }}
                          className="text-[11px] text-white/30 hover:text-white/60 transition-colors flex items-center gap-1"
                          title="Copy Mermaid source"
                        >
                          <Copy size={10} /> Copy
                        </button>
                        <button
                          onClick={() => handleDownloadErDiagramImage("png")}
                          disabled={erDiagramDownloading !== null}
                          className="text-[11px] text-white/30 hover:text-white/60 transition-colors flex items-center gap-1 disabled:opacity-50"
                          title="Download as PNG image"
                        >
                          {erDiagramDownloading === "png" ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <Download size={10} />
                          )}{" "}
                          PNG
                        </button>
                        <button
                          onClick={() => handleDownloadErDiagramImage("svg")}
                          disabled={erDiagramDownloading !== null}
                          className="text-[11px] text-white/30 hover:text-white/60 transition-colors flex items-center gap-1 disabled:opacity-50"
                          title="Download as SVG (scalable vector)"
                        >
                          {erDiagramDownloading === "svg" ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <Download size={10} />
                          )}{" "}
                          SVG
                        </button>
                      </div>
                    }
                  >
                    {semanticView.er_diagram_markdown ? (
                      <MermaidDiagram
                        chart={semanticView.er_diagram_markdown}
                        onSvgReady={(svg) => {
                          erDiagramSvgRef.current = svg;
                        }}
                      />
                    ) : (
                      <p className="text-xs text-white/30 italic">
                        No ER diagram available
                      </p>
                    )}
                  </CollapsibleSection>
                )}

                {/* ER Canvas — interactive, draggable/zoomable */}
                {modelViewTab === "canvas" && (
                  <CollapsibleSection
                    icon={<LayoutGrid size={14} className="text-brand-500" />}
                    title="Interactive ER Canvas"
                    badge={`${entities.length} entities`}
                    defaultOpen={true}
                  >
                    <SemanticERCanvas
                      entities={entities}
                      relationships={relationships}
                      onEditEntity={(name) => setCanvasEditEntityName(name)}
                      onUpdateRelationshipCardinality={
                        handleCanvasRelationshipCardinalityChange
                      }
                      onDeleteRelationship={handleCanvasRelationshipDelete}
                      onCreateRelationship={handleCanvasCreateRelationship}
                    />

                    {/* Modeling intelligence: deterministic (no-LLM) FK
                        suggestions, fan-out/grain warnings, and SCD-type
                        suggestions computed from the current model. */}
                    <div className="mt-3 rounded-lg border border-white/10 bg-surface-200/40 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb size={13} className="text-amber-400" />
                        <p className="text-[11px] font-semibold text-white/70">
                          Modeling Insights
                        </p>
                        {insightsLoading && (
                          <Loader2
                            size={11}
                            className="animate-spin text-white/30"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => void loadModelingInsights()}
                          className="ml-auto text-[10px] text-brand-400 hover:text-brand-300"
                        >
                          Refresh
                        </button>
                      </div>
                      {insightsError && (
                        <p className="text-[10px] text-red-400 mb-2">
                          {insightsError}
                        </p>
                      )}
                      {modelingInsights && (
                        <div className="space-y-3">
                          {modelingInsights.fk_suggestions.length > 0 && (
                            <div>
                              <p className="text-[10px] font-semibold text-white/50 uppercase tracking-wide mb-1">
                                Suggested relationships (
                                {modelingInsights.fk_suggestions.length})
                              </p>
                              <ul className="space-y-1">
                                {modelingInsights.fk_suggestions.map((s, i) => (
                                  <li
                                    key={i}
                                    className="flex items-center gap-2 text-[11px] text-white/70 bg-surface-100 rounded px-2 py-1"
                                  >
                                    <span className="truncate flex-1">
                                      {s.from_entity}.{s.from_column} →{" "}
                                      {s.to_entity}.{s.to_column}{" "}
                                      <span className="text-white/30">
                                        ({Math.round(s.confidence * 100)}%)
                                      </span>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => void acceptFkSuggestion(s)}
                                      className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-brand-600 hover:bg-brand-700 text-white shrink-0"
                                    >
                                      <Check size={9} /> Accept
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {modelingInsights.warnings.length > 0 && (
                            <div>
                              <p className="text-[10px] font-semibold text-white/50 uppercase tracking-wide mb-1">
                                Warnings ({modelingInsights.warnings.length})
                              </p>
                              <ul className="space-y-1">
                                {modelingInsights.warnings.map((w, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-1.5 text-[11px] bg-surface-100 rounded px-2 py-1"
                                  >
                                    <AlertTriangle
                                      size={11}
                                      className={
                                        w.severity === "error"
                                          ? "text-red-400 shrink-0 mt-0.5"
                                          : w.severity === "warning"
                                            ? "text-amber-400 shrink-0 mt-0.5"
                                            : "text-white/30 shrink-0 mt-0.5"
                                      }
                                    />
                                    <span className="text-white/60">
                                      {w.message}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {modelingInsights.scd_suggestions.length > 0 && (
                            <div>
                              <p className="text-[10px] font-semibold text-white/50 uppercase tracking-wide mb-1">
                                SCD suggestions (
                                {modelingInsights.scd_suggestions.length})
                              </p>
                              <ul className="space-y-1">
                                {modelingInsights.scd_suggestions.map(
                                  (s, i) => (
                                    <li
                                      key={i}
                                      className="text-[11px] text-white/60 bg-surface-100 rounded px-2 py-1"
                                    >
                                      <span className="font-medium text-white/80">
                                        {s.entity}
                                      </span>
                                      : {s.suggested_type} — {s.rationale}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                          {modelingInsights.fk_suggestions.length === 0 &&
                            modelingInsights.warnings.length === 0 &&
                            modelingInsights.scd_suggestions.length === 0 && (
                              <p className="text-[11px] text-white/30 italic">
                                No suggestions or warnings — the model looks
                                clean.
                              </p>
                            )}
                        </div>
                      )}
                    </div>

                    {canvasEditEntityName &&
                      (() => {
                        const editEntity = entities.find(
                          (e: any) => e.name === canvasEditEntityName,
                        );
                        if (!editEntity) return null;
                        return (
                          <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                            onClick={() => setCanvasEditEntityName(null)}
                          >
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="w-full max-w-2xl max-h-[85vh] overflow-y-auto custom-scrollbar"
                            >
                              <div className="flex items-center justify-end mb-2">
                                <button
                                  onClick={() => setCanvasEditEntityName(null)}
                                  className="text-white/40 hover:text-white transition p-1"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                              <EntityCard
                                entity={editEntity}
                                defaultOpen
                                autoEdit
                                sessionId={sessionId ?? undefined}
                                allEntities={entities}
                                onSaved={(wrapper) =>
                                  setSemanticView((prev: any) =>
                                    prev
                                      ? { ...prev, semantic_view: wrapper }
                                      : { semantic_view: wrapper },
                                  )
                                }
                              />
                            </div>
                          </div>
                        );
                      })()}
                  </CollapsibleSection>
                )}

                {/* Metric Definitions — reusable business-metric layer */}
                {modelViewTab === "metrics" && (
                  <CollapsibleSection
                    icon={<Sigma size={14} className="text-brand-500" />}
                    title="Metric Definitions"
                    badge={`${metrics.length} metrics`}
                    defaultOpen={true}
                  >
                    <div className="space-y-3">
                      {metrics.length === 0 ? (
                        <p className="text-xs text-white/30 italic">
                          No metrics defined yet — add a reusable business
                          metric below (e.g. Total Revenue = SUM(amount)).
                        </p>
                      ) : (
                        <ul className="divide-y divide-white/5 rounded-lg border border-white/10 overflow-hidden">
                          {metrics.map((m: any) => {
                            const lineageCols = metricLineageColumns(
                              m,
                              entities,
                            );
                            return (
                              <li
                                key={m.name}
                                className="px-3 py-2 flex items-center justify-between gap-3"
                              >
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-white truncate">
                                    {m.name}{" "}
                                    <span className="text-white/30 font-normal">
                                      ({m.entity})
                                    </span>
                                  </p>
                                  <p className="text-[11px] text-white/50 font-mono truncate">
                                    {m.expression}
                                  </p>
                                  {m.description && (
                                    <p className="text-[11px] text-white/30 truncate">
                                      {m.description}
                                    </p>
                                  )}
                                  {lineageCols.length > 0 && (
                                    <p className="text-[10px] text-brand-400/70 truncate flex items-center gap-1 mt-0.5">
                                      <GitBranch
                                        size={9}
                                        className="shrink-0"
                                      />
                                      references: {lineageCols.join(", ")}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => deleteMetric(m.name)}
                                  disabled={deletingMetric === m.name}
                                  className="shrink-0 p-1.5 text-white/30 hover:text-red-400 transition-colors disabled:opacity-40"
                                  title="Delete metric"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      <div className="glass rounded-xl p-3">
                        {!addingMetric ? (
                          <button
                            onClick={() => setAddingMetric(true)}
                            className="flex items-center gap-1.5 text-[11px] text-brand-400 hover:text-brand-300 transition-colors"
                          >
                            <Plus size={12} /> Add metric
                          </button>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="grid grid-cols-2 gap-1.5">
                              <input
                                value={newMetricDraft.name}
                                onChange={(e) =>
                                  setNewMetricDraft((p) => ({
                                    ...p,
                                    name: e.target.value,
                                  }))
                                }
                                placeholder="Metric name (e.g. total_revenue)"
                                className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                              />
                              <select
                                value={newMetricDraft.entity}
                                onChange={(e) =>
                                  setNewMetricDraft((p) => ({
                                    ...p,
                                    entity: e.target.value,
                                  }))
                                }
                                className="px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                              >
                                <option value="">Entity…</option>
                                {entities.map((e: any) => (
                                  <option key={e.name} value={e.name}>
                                    {e.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <input
                              value={newMetricDraft.expression}
                              onChange={(e) =>
                                setNewMetricDraft((p) => ({
                                  ...p,
                                  expression: e.target.value,
                                }))
                              }
                              placeholder="Expression (e.g. SUM(amount))"
                              className="w-full px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 font-mono focus:outline-none focus:border-brand-500"
                            />
                            <input
                              value={newMetricDraft.description}
                              onChange={(e) =>
                                setNewMetricDraft((p) => ({
                                  ...p,
                                  description: e.target.value,
                                }))
                              }
                              placeholder="Description (optional)"
                              className="w-full px-1.5 py-1 bg-surface-300 border border-white/10 rounded text-[11px] text-white/80 focus:outline-none focus:border-brand-500"
                            />
                            {addMetricError && (
                              <p className="text-[11px] text-red-400">
                                {addMetricError}
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={addMetric}
                                disabled={addMetricBusy}
                                className="px-2.5 py-1 bg-brand-500/20 hover:bg-brand-500/30 text-brand-400 rounded text-[11px] transition disabled:opacity-50"
                              >
                                {addMetricBusy ? "Saving…" : "Save"}
                              </button>
                              <button
                                onClick={() => {
                                  setAddingMetric(false);
                                  setAddMetricError(null);
                                }}
                                className="px-2.5 py-1 text-white/40 hover:text-white text-[11px] transition"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleSection>
                )}

                {/* Hierarchies — if detected */}
                {sv?.hierarchies && sv.hierarchies.length > 0 && (
                  <CollapsibleSection
                    icon={<Layers size={14} className="text-purple-400" />}
                    title="Dimensional Hierarchies"
                    badge={`${sv.hierarchies.length} found`}
                    defaultOpen={false}
                  >
                    <div className="space-y-2">
                      {sv.hierarchies.map((h: any, i: number) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 p-2 bg-surface-200/30 rounded-lg"
                        >
                          <span className="text-[11px] font-semibold text-purple-400/80 min-w-[80px]">
                            {h.name}
                          </span>
                          <div className="flex items-center gap-1 flex-wrap">
                            {(h.levels || []).map(
                              (level: string, j: number) => (
                                <span
                                  key={j}
                                  className="flex items-center gap-1"
                                >
                                  {j > 0 && (
                                    <span className="text-white/20 text-[11px]">
                                      →
                                    </span>
                                  )}
                                  <code className="text-[11px] bg-purple-500/10 text-purple-400/70 px-1.5 py-0.5 rounded border border-purple-500/15">
                                    {level}
                                  </code>
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Derived Columns — if suggested */}
                {sv?.derived_columns && sv.derived_columns.length > 0 && (
                  <CollapsibleSection
                    icon={<Sparkles size={14} className="text-amber-400" />}
                    title="Suggested Derived Columns"
                    badge={`${sv.derived_columns.length} suggestions`}
                    defaultOpen={false}
                  >
                    <div className="space-y-2">
                      {sv.derived_columns.map((dc: any, i: number) => (
                        <div
                          key={i}
                          className="p-2 bg-surface-200/30 rounded-lg"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-[11px] font-semibold text-amber-400/80">
                              {dc.name}
                            </code>
                            {dc.semantic_type && (
                              <span className="text-[11px] bg-amber-500/10 text-amber-400/60 px-1.5 py-0.5 rounded">
                                {dc.semantic_type}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-white/40">
                            <span className="text-white/25">Expression:</span>{" "}
                            <code className="text-white/50">
                              {dc.expression}
                            </code>
                          </div>
                          {dc.description && (
                            <p className="text-[11px] text-white/30 mt-0.5">
                              {dc.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* SCD Patterns — if detected */}
                {sv?.scd_patterns &&
                  sv.scd_patterns.length > 0 &&
                  sv.scd_patterns.some(
                    (s: any) => s.type && s.type !== "None",
                  ) && (
                    <CollapsibleSection
                      icon={<Clock size={14} className="text-teal-400" />}
                      title="Slowly Changing Dimensions"
                      badge={`${sv.scd_patterns.filter((s: any) => s.type && s.type !== "None").length} detected`}
                      defaultOpen={false}
                    >
                      <div className="space-y-2">
                        {sv.scd_patterns
                          .filter((s: any) => s.type && s.type !== "None")
                          .map((scd: any, i: number) => (
                            <div
                              key={i}
                              className="flex items-center gap-3 p-2 bg-surface-200/30 rounded-lg"
                            >
                              <span className="text-[11px] font-semibold text-teal-400/80 min-w-[100px]">
                                {scd.entity}
                              </span>
                              <span className="text-[11px] bg-teal-500/15 text-teal-400/80 px-2 py-0.5 rounded border border-teal-500/20 font-semibold">
                                {scd.type}
                              </span>
                              {scd.tracking_columns?.length > 0 && (
                                <div className="flex gap-1">
                                  {scd.tracking_columns.map(
                                    (tc: string, j: number) => (
                                      <code
                                        key={j}
                                        className="text-[11px] text-white/40 bg-white/5 px-1 py-0.5 rounded"
                                      >
                                        {tc}
                                      </code>
                                    ),
                                  )}
                                </div>
                              )}
                              {scd.description && (
                                <span className="text-[11px] text-white/30 truncate">
                                  {scd.description}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    </CollapsibleSection>
                  )}

                {/* Back to Q&A / Continue to review & refine */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={() => setStep(3)}
                    className="text-sm text-white/30 hover:text-white/60 flex items-center gap-1"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    onClick={() => setStep(4.5)}
                    className="px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-medium transition-colors inline-flex items-center gap-2"
                  >
                    Review & Refine Semantic View <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            );
          })()}

        {/* ════════════════════════════════════════════════
           STEP 4.5: Review & Refine
           ════════════════════════════════════════════════ */}
        {step === 4.5 && semanticView && sessionId && (
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in"
            style={{ minHeight: "calc(100vh - 250px)" }}
          >
            {/* Left: Current Semantic View */}
            <div
              className="overflow-y-auto custom-scrollbar space-y-4 pr-2"
              style={{ maxHeight: "calc(100vh - 250px)" }}
            >
              <div className="glass rounded-xl p-4 relative z-20">
                <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <Eye size={16} className="text-brand-500" />
                  Current Semantic View
                </h3>
                <p className="text-xs text-white/40 mb-4">
                  Chat with the AI to refine entities, columns, descriptions,
                  keys, or relationships. Changes will be applied live.
                </p>
                {(() => {
                  const sv = (semanticView.semantic_view as Record<string, any>)
                    ?.semantic_view;
                  const allEntityNames: string[] = (sv?.entities ?? [])
                    .map((e: any) => e.name)
                    .filter(Boolean);
                  const suggestions = entitySearch
                    ? allEntityNames
                        .filter(
                          (n) =>
                            n
                              .toLowerCase()
                              .includes(entitySearch.toLowerCase()) &&
                            !entityFilter.includes(n),
                        )
                        .slice(0, 8)
                    : [];
                  return (
                    <div>
                      <div className="relative">
                        <Search
                          size={12}
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-white/20"
                        />
                        <input
                          value={entitySearch}
                          onChange={(e) => setEntitySearch(e.target.value)}
                          placeholder="Search a table to show only its details…"
                          className="w-full pl-7 pr-3 py-1.5 bg-white/5 border border-white/8 rounded-lg text-[11px] text-white/70 outline-none focus:border-brand-500/30 placeholder:text-white/15"
                        />
                        {suggestions.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full bg-surface-200 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {suggestions.map((n) => (
                              <button
                                key={n}
                                onClick={() => {
                                  setEntityFilter((prev) => [...prev, n]);
                                  setEntitySearch("");
                                }}
                                className="w-full text-left px-3 py-1.5 text-[11px] text-white/70 hover:bg-white/5 transition-colors"
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {entityFilter.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {entityFilter.map((n) => (
                            <span
                              key={n}
                              className="flex items-center gap-1 text-[11px] bg-brand-500/15 text-brand-300 border border-brand-500/20 px-2 py-0.5 rounded-full"
                            >
                              {n}
                              <button
                                onClick={() =>
                                  setEntityFilter((prev) =>
                                    prev.filter((x) => x !== n),
                                  )
                                }
                                className="hover:text-white transition-colors"
                              >
                                <X size={10} />
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={() => setEntityFilter([])}
                            className="text-[11px] text-white/30 hover:text-white/60 underline transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Add entity — target-only tables with no source counterpart */}
              {addEntityFormUI}

              {/* Compact entity cards — reuses EntityCard's built-in "Edit"
                  mode so entities can be refined deterministically (no LLM)
                  as an alternative to the chat-based Refine panel. */}
              {(() => {
                const sv = (semanticView.semantic_view as Record<string, any>)
                  ?.semantic_view;
                const visibleEntities =
                  entityFilter.length > 0
                    ? (sv?.entities ?? []).filter((e: any) =>
                        entityFilter.includes(e.name),
                      )
                    : (sv?.entities ?? []);
                return visibleEntities.map((entity: any, idx: number) => (
                  <EntityCard
                    key={idx}
                    entity={entity}
                    defaultOpen
                    sessionId={sessionId}
                    allEntities={sv?.entities ?? []}
                    onSaved={(wrapper) =>
                      setSemanticView((prev: any) =>
                        prev
                          ? { ...prev, semantic_view: wrapper }
                          : { semantic_view: wrapper },
                      )
                    }
                  />
                ));
              })()}

              {/* Relationship editor */}
              <div className="glass rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Link size={14} className="text-brand-500" />
                    Relationships
                  </h3>
                  {!relEditing && (
                    <button
                      onClick={startEditingRelationships}
                      className="text-[11px] px-2 py-1 rounded-md bg-surface-200 hover:bg-surface-300 text-white/50 hover:text-white transition"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {relError && (
                  <p className="text-[11px] text-red-400 mb-2">{relError}</p>
                )}
                {!relEditing ? (
                  (() => {
                    const allRels: any[] =
                      (semanticView?.semantic_view as Record<string, any>)
                        ?.semantic_view?.relationships ?? [];
                    const visibleRels =
                      entityFilter.length > 0
                        ? allRels.filter((r) => {
                            const n = normalizeRelationship(r);
                            return (
                              entityFilter.includes(n.fromEntity) ||
                              entityFilter.includes(n.toEntity)
                            );
                          })
                        : allRels;
                    return (
                      <div className="space-y-1.5">
                        {visibleRels.length === 0 && (
                          <p className="text-[11px] text-white/30">
                            {allRels.length === 0
                              ? "No relationships defined yet."
                              : "No relationships involve the selected table(s)."}
                          </p>
                        )}
                        {visibleRels.map((r: any, i: number) => {
                          const n = normalizeRelationship(r);
                          return (
                            <div
                              key={i}
                              className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-white/50"
                            >
                              <code className="bg-surface-200 px-1.5 py-0.5 rounded text-brand-300 break-all">
                                {n.fromEntity}.{n.fromColumn}
                              </code>
                              <ArrowRight
                                size={10}
                                className="text-white/20 flex-shrink-0"
                              />
                              <code className="bg-surface-200 px-1.5 py-0.5 rounded text-brand-300 break-all">
                                {n.toEntity}.{n.toColumn}
                              </code>
                              <span className="ml-auto flex-shrink-0 text-white/30 px-1.5 py-0.5 rounded bg-white/5">
                                {cardinalityLabel(n.cardinality)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : (
                  <div className="space-y-2">
                    {relDraft.map((r, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-6 gap-1.5 items-center"
                      >
                        <input
                          value={r.from_entity}
                          onChange={(e) =>
                            setRelDraft((prev) =>
                              prev.map((p, idx) =>
                                idx === i
                                  ? { ...p, from_entity: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          placeholder="From entity"
                          className="col-span-1 px-1.5 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
                        />
                        <input
                          value={r.from_column}
                          onChange={(e) =>
                            setRelDraft((prev) =>
                              prev.map((p, idx) =>
                                idx === i
                                  ? { ...p, from_column: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          placeholder="From column"
                          className="col-span-1 px-1.5 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
                        />
                        <input
                          value={r.to_entity}
                          onChange={(e) =>
                            setRelDraft((prev) =>
                              prev.map((p, idx) =>
                                idx === i
                                  ? { ...p, to_entity: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          placeholder="To entity"
                          className="col-span-1 px-1.5 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
                        />
                        <input
                          value={r.to_column}
                          onChange={(e) =>
                            setRelDraft((prev) =>
                              prev.map((p, idx) =>
                                idx === i
                                  ? { ...p, to_column: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          placeholder="To column"
                          className="col-span-1 px-1.5 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
                        />
                        <select
                          value={r.cardinality}
                          onChange={(e) =>
                            setRelDraft((prev) =>
                              prev.map((p, idx) =>
                                idx === i
                                  ? { ...p, cardinality: e.target.value }
                                  : p,
                              ),
                            )
                          }
                          className="col-span-1 px-1 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
                        >
                          <option value="one_to_one">1:1</option>
                          <option value="one_to_many">1:M</option>
                          <option value="many_to_one">M:1</option>
                          <option value="many_to_many">M:M</option>
                        </select>
                        <button
                          onClick={() =>
                            setRelDraft((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            )
                          }
                          className="col-span-1 flex items-center justify-center text-red-400/60 hover:text-red-400"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setRelDraft((prev) => [
                            ...prev,
                            {
                              from_entity: "",
                              to_entity: "",
                              from_column: "",
                              to_column: "",
                              cardinality: "many_to_one",
                              confidence: 1,
                            },
                          ])
                        }
                        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-surface-200 hover:bg-surface-300 text-white/50 hover:text-white transition"
                      >
                        <Plus size={11} /> Add relationship
                      </button>
                      <button
                        onClick={() => setRelEditing(false)}
                        className="text-[11px] px-2 py-1 rounded-md text-white/40 hover:text-white transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveRelationships}
                        disabled={relSaving}
                        className="ml-auto flex items-center gap-1 text-[11px] px-3 py-1 rounded-md bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white transition"
                      >
                        {relSaving ? (
                          <Loader2 size={11} className="animate-spin" />
                        ) : (
                          <Check size={11} />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Session snapshots */}
              <div className="glass rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <Clock size={14} className="text-brand-500" />
                  Snapshots
                </h3>
                {snapshotError && (
                  <p className="text-[11px] text-red-400 mb-2">
                    {snapshotError}
                  </p>
                )}
                <div className="flex gap-2 mb-2">
                  <input
                    value={snapshotLabel}
                    onChange={(e) => setSnapshotLabel(e.target.value)}
                    placeholder="Label (optional)"
                    className="flex-1 px-2 py-1.5 bg-surface-300 border border-surface-200 rounded-lg text-[11px] text-white placeholder-gray-600"
                  />
                  <button
                    onClick={saveSnapshot}
                    disabled={snapshotSaving}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                  >
                    {snapshotSaving ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <Plus size={11} />
                    )}
                    Save Snapshot
                  </button>
                </div>
                {snapshotsLoading ? (
                  <p className="text-[11px] text-white/30">Loading…</p>
                ) : snapshots.length === 0 ? (
                  <p className="text-[11px] text-white/30">No snapshots yet.</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar">
                    {snapshots.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between gap-2 text-[11px] text-white/50 bg-surface-200/40 rounded-lg px-2 py-1.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-white/70">{s.label}</p>
                          <p className="text-white/25">
                            {new Date(s.created_at).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => restoreSnapshot(s.id)}
                          disabled={snapshotRestoring === s.id}
                          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-surface-200 hover:bg-surface-300 text-brand-400 hover:text-brand-300 transition disabled:opacity-60"
                        >
                          {snapshotRestoring === s.id ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <RefreshCw size={10} />
                          )}
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Compare two snapshots before restoring */}
                {snapshots.length >= 2 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <GitCompare size={12} className="text-white/40" />
                      <p className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">
                        Compare Snapshots
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <select
                        value={diffFromId ?? ""}
                        onChange={(e) => setDiffFromId(e.target.value || null)}
                        className="flex-1 px-2 py-1 bg-surface-300 border border-surface-200 rounded-lg text-[11px] text-white"
                      >
                        <option value="">From…</option>
                        {snapshots.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label || s.id}
                          </option>
                        ))}
                      </select>
                      <ArrowRight
                        size={11}
                        className="text-white/30 shrink-0"
                      />
                      <select
                        value={diffToId ?? ""}
                        onChange={(e) => setDiffToId(e.target.value || null)}
                        className="flex-1 px-2 py-1 bg-surface-300 border border-surface-200 rounded-lg text-[11px] text-white"
                      >
                        <option value="">To…</option>
                        {snapshots.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label || s.id}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => void runSnapshotDiff()}
                        disabled={!diffFromId || !diffToId || diffLoading}
                        className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-surface-200 hover:bg-surface-300 text-brand-400 hover:text-brand-300 transition disabled:opacity-60"
                      >
                        {diffLoading ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          "Diff"
                        )}
                      </button>
                    </div>
                    {diffError && (
                      <p className="text-[10px] text-red-400 mb-1">
                        {diffError}
                      </p>
                    )}
                    {diffResult && (
                      <div className="space-y-1.5 text-[11px] max-h-48 overflow-y-auto custom-scrollbar">
                        {diffResult.diff.entities.added.length === 0 &&
                        diffResult.diff.entities.removed.length === 0 &&
                        diffResult.diff.entities.modified.length === 0 &&
                        diffResult.diff.relationships.added.length === 0 &&
                        diffResult.diff.relationships.removed.length === 0 &&
                        diffResult.diff.relationships.changed.length === 0 &&
                        diffResult.diff.metrics.added.length === 0 &&
                        diffResult.diff.metrics.removed.length === 0 ? (
                          <p className="text-white/30 italic">
                            No differences between these snapshots.
                          </p>
                        ) : (
                          <>
                            {diffResult.diff.entities.added.map((name, i) => (
                              <div
                                key={`ea-${i}`}
                                className="bg-surface-200/40 rounded px-2 py-1"
                              >
                                <span className="text-green-400">added</span>{" "}
                                <span className="text-white/70">
                                  entity {name}
                                </span>
                              </div>
                            ))}
                            {diffResult.diff.entities.removed.map((name, i) => (
                              <div
                                key={`er-${i}`}
                                className="bg-surface-200/40 rounded px-2 py-1"
                              >
                                <span className="text-red-400">removed</span>{" "}
                                <span className="text-white/70">
                                  entity {name}
                                </span>
                              </div>
                            ))}
                            {diffResult.diff.entities.modified.map((e, i) => (
                              <div
                                key={`em-${i}`}
                                className="bg-surface-200/40 rounded px-2 py-1"
                              >
                                <span className="text-amber-400">modified</span>{" "}
                                <span className="text-white/70">
                                  entity {e.name}
                                </span>
                                {(e.columns_added.length > 0 ||
                                  e.columns_removed.length > 0) && (
                                  <span className="text-white/30">
                                    {" "}
                                    (
                                    {e.columns_added.length > 0 &&
                                      `+${e.columns_added.join(", ")}`}
                                    {e.columns_added.length > 0 &&
                                      e.columns_removed.length > 0 &&
                                      "; "}
                                    {e.columns_removed.length > 0 &&
                                      `-${e.columns_removed.join(", ")}`}
                                    )
                                  </span>
                                )}
                              </div>
                            ))}
                            {diffResult.diff.relationships.added.map((r, i) => (
                              <div
                                key={`ra-${i}`}
                                className="bg-surface-200/40 rounded px-2 py-1"
                              >
                                <span className="text-green-400">added</span>{" "}
                                <span className="text-white/70">
                                  relationship {r.from_entity}.{r.from_column} →{" "}
                                  {r.to_entity}.{r.to_column}
                                </span>
                              </div>
                            ))}
                            {diffResult.diff.relationships.removed.map(
                              (r, i) => (
                                <div
                                  key={`rr-${i}`}
                                  className="bg-surface-200/40 rounded px-2 py-1"
                                >
                                  <span className="text-red-400">removed</span>{" "}
                                  <span className="text-white/70">
                                    relationship {r.from_entity}.{r.from_column}{" "}
                                    → {r.to_entity}.{r.to_column}
                                  </span>
                                </div>
                              ),
                            )}
                            {diffResult.diff.relationships.changed.map(
                              (r, i) => (
                                <div
                                  key={`rc-${i}`}
                                  className="bg-surface-200/40 rounded px-2 py-1"
                                >
                                  <span className="text-amber-400">
                                    changed
                                  </span>{" "}
                                  <span className="text-white/70">
                                    relationship {r.from_entity}.{r.from_column}{" "}
                                    → {r.to_entity}.{r.to_column}
                                  </span>
                                  <span className="text-white/30">
                                    {" "}
                                    ({r.from_cardinality ?? "?"} →{" "}
                                    {r.to_cardinality ?? "?"})
                                  </span>
                                </div>
                              ),
                            )}
                            {diffResult.diff.metrics.added.map((name, i) => (
                              <div
                                key={`ma-${i}`}
                                className="bg-surface-200/40 rounded px-2 py-1"
                              >
                                <span className="text-green-400">added</span>{" "}
                                <span className="text-white/70">
                                  metric {name}
                                </span>
                              </div>
                            ))}
                            {diffResult.diff.metrics.removed.map((name, i) => (
                              <div
                                key={`mr-${i}`}
                                className="bg-surface-200/40 rounded px-2 py-1"
                              >
                                <span className="text-red-400">removed</span>{" "}
                                <span className="text-white/70">
                                  metric {name}
                                </span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Semantic-layer export (dbt / LookML / Cube.dev) */}
              <div className="glass rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <Download size={14} className="text-brand-500" />
                  Export Semantic Layer
                </h3>
                {exportError && (
                  <p className="text-[11px] text-red-400 mb-2">{exportError}</p>
                )}
                <div className="flex gap-2">
                  {(
                    ["dbt", "lookml", "cube"] as SemanticLayerExportFormat[]
                  ).map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => void handleExportSemanticLayer(fmt)}
                      disabled={exportingFormat !== null}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                    >
                      {exportingFormat === fmt ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <Download size={11} />
                      )}
                      {fmt === "dbt"
                        ? "dbt YAML"
                        : fmt === "lookml"
                          ? "LookML"
                          : "Cube.dev"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Refinement Chat */}
            <div
              className="flex flex-col glass rounded-xl overflow-hidden"
              style={{ maxHeight: "calc(100vh - 250px)" }}
            >
              {/* Chat header */}
              <div
                className="flex-shrink-0 px-4 py-3 border-b border-surface-200 flex items-center justify-between"
                style={{ background: "rgba(14,14,24,0.7)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Eye size={14} className="text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">
                      {refinePanelMode === "refine"
                        ? "Review & Refine"
                        : "Ask a Question"}
                    </h3>
                    <p className="text-[11px] text-white/30">
                      {refinePanelMode === "refine"
                        ? "Chat to edit the semantic view"
                        : "Ask about the model — read-only, no edits"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-surface-300 rounded-lg p-0.5">
                  <button
                    onClick={() => setRefinePanelMode("refine")}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition ${
                      refinePanelMode === "refine"
                        ? "bg-purple-600 text-white"
                        : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    Refine
                  </button>
                  <button
                    onClick={() => setRefinePanelMode("ask")}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition ${
                      refinePanelMode === "ask"
                        ? "bg-purple-600 text-white"
                        : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    Ask
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 custom-scrollbar">
                {activeChatMessages.length === 0 &&
                  refinePanelMode === "refine" && (
                    <div className="text-center py-8 space-y-3">
                      <Eye size={32} className="mx-auto text-purple-400/40" />
                      <p className="text-sm text-white/40">
                        Refine your semantic view
                      </p>
                      {llmAvailable === false ? (
                        <div className="max-w-sm mx-auto glass rounded-xl p-4 text-left space-y-2">
                          <p className="text-xs text-white/60">
                            No LLM provider is configured for this project —
                            conversational refinement won't work.
                          </p>
                          <p className="text-xs text-white/40">
                            Use the deterministic{" "}
                            <strong className="text-white/70 font-medium">
                              Edit
                            </strong>{" "}
                            button on any entity, or the Relationships panel, on
                            the left instead — no AI required.
                          </p>
                          <DeterministicRefineGuide
                            open={showDeterministicGuide}
                            onToggle={() =>
                              setShowDeterministicGuide((v) => !v)
                            }
                          />
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2 justify-center">
                          {[
                            'Rename the "Orders" entity to "Sales Transactions"',
                            "Mark email and phone columns as PII",
                            "Add a foreign key from Orders.customer_id to Customers.id",
                            'Change the domain of "amount" columns to Financial',
                            "Set the description for the Customers entity",
                          ].map((suggestion) => (
                            <button
                              key={suggestion}
                              onClick={() => sendRefineMessage(suggestion)}
                              className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 border border-white/5 rounded-lg text-[11px] text-white/40 hover:text-white/60 transition-all text-left"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                {activeChatMessages.length === 0 &&
                  refinePanelMode === "ask" && (
                    <div className="text-center py-8 space-y-3">
                      <MessageSquare
                        size={32}
                        className="mx-auto text-purple-400/40"
                      />
                      <p className="text-sm text-white/40">
                        Ask a free-form question about this model
                      </p>
                      {llmAvailable === false ? (
                        <div className="max-w-sm mx-auto glass rounded-xl p-4 text-left space-y-3">
                          <p className="text-xs text-white/60">
                            No LLM provider is configured for this project, so
                            NL Q&amp;A won't work here.
                          </p>
                          {projectId && (
                            <button
                              onClick={() =>
                                openAgentInNewTab(
                                  `/projects/${projectId}/agents/conversational-ai`,
                                )
                              }
                              className="ztd-btn ztd-btn-secondary ztd-btn-sm w-full justify-center"
                            >
                              <MessageSquare size={12} /> Open Conversational AI
                              <ArrowUpRight
                                size={12}
                                className="text-white/40"
                              />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2 justify-center">
                          {[
                            "Which entity is the fact table and why?",
                            "What columns contain PII?",
                            "How are Orders and Customers related?",
                          ].map((suggestion) => (
                            <button
                              key={suggestion}
                              onClick={() => sendQaChatMessage(suggestion)}
                              className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 border border-white/5 rounded-lg text-[11px] text-white/40 hover:text-white/60 transition-all text-left"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                {activeChatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <Eye size={14} className="text-purple-400" />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-purple-600 text-white rounded-br-md"
                          : "bg-surface-200 text-gray-200 rounded-bl-md"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => (
                              <p className="mb-2 last:mb-0">{children}</p>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-semibold text-white">
                                {children}
                              </strong>
                            ),
                            code: ({ children, className }) =>
                              className ? (
                                <pre className="bg-surface-300 rounded-lg p-3 my-2 overflow-x-auto font-mono text-xs">
                                  <code>{children}</code>
                                </pre>
                              ) : (
                                <code className="bg-gray-700 px-1.5 py-0.5 rounded text-xs font-mono text-purple-300">
                                  {children}
                                </code>
                              ),
                            ul: ({ children }) => (
                              <ul className="list-disc list-inside space-y-1 mb-2">
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="list-decimal list-inside space-y-1 mb-2">
                                {children}
                              </ol>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      ) : (
                        msg.content
                      )}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-7 h-7 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0 mt-1">
                        <User size={14} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                ))}
                {activeChatLoading && (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Eye size={14} className="text-purple-400" />
                    </div>
                    <div className="bg-surface-200 rounded-2xl rounded-bl-md px-4 py-3">
                      <Loader2
                        size={16}
                        className="animate-spin text-gray-400"
                      />
                    </div>
                  </div>
                )}
                <div ref={refineBottomRef} />
              </div>

              {/* No-LLM reminder once a chat is already in progress — the
                  empty state above covers the "before typing anything" case. */}
              {llmAvailable === false &&
                refinePanelMode === "ask" &&
                activeChatMessages.length > 0 && (
                  <div className="mx-4 mb-2 glass rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-white/50 min-w-0">
                      <MessageSquare size={14} className="flex-shrink-0" />
                      <span className="text-xs text-white/60">
                        No LLM provider is configured for this project. Hand
                        over to the Conversational AI agent instead, or switch
                        to the Refine tab to edit entities/relationships
                        directly (no AI required).
                      </span>
                    </div>
                    {projectId && (
                      <button
                        onClick={() =>
                          openAgentInNewTab(
                            `/projects/${projectId}/agents/conversational-ai`,
                          )
                        }
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                      >
                        <MessageSquare size={12} /> Open Conversational AI
                        <ArrowUpRight size={12} className="text-white/40" />
                      </button>
                    )}
                  </div>
                )}
              {llmAvailable === false &&
                refinePanelMode === "refine" &&
                activeChatMessages.length > 0 && (
                  <div className="mx-4 mb-2 glass rounded-xl p-3 flex items-start gap-2 flex-wrap">
                    <PenSquare
                      size={14}
                      className="text-white/50 flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-white/60">
                        No LLM provider is configured, so conversational
                        refinement won't work. Use the deterministic{" "}
                        <strong className="text-white/80 font-medium">
                          Edit
                        </strong>{" "}
                        button on any entity or the Relationships panel on the
                        left — no AI required.
                      </span>
                      <DeterministicRefineGuide
                        open={showDeterministicGuide}
                        onToggle={() => setShowDeterministicGuide((v) => !v)}
                      />
                    </div>
                  </div>
                )}

              {/* Navigation buttons */}
              <div className="px-4 pb-2 flex gap-2">
                <button
                  onClick={() => setStep(4)}
                  className="px-4 py-2.5 bg-surface-200 hover:bg-surface-300 text-white/40 hover:text-white rounded-xl text-sm transition flex items-center gap-1.5"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  onClick={() => {
                    setStep(4.7);
                    handleValidate();
                  }}
                  className="flex-1 py-2.5 bg-gradient-to-r from-brand-600 to-cyan-600 hover:from-brand-700 hover:to-cyan-700 text-white rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2"
                >
                  <ClipboardCheck size={16} /> Run Validation
                </button>
              </div>

              {/* Chat input */}
              <div
                className="flex-shrink-0 p-4 border-t border-surface-200"
                style={{ background: "rgba(14,14,24,0.95)" }}
              >
                <div className="flex gap-2 items-end">
                  <textarea
                    ref={refineInputRef}
                    value={activeChatInput}
                    onChange={(e) => setActiveChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendActiveChatMessage(activeChatInput);
                      }
                    }}
                    placeholder={
                      refinePanelMode === "refine"
                        ? "Describe changes to your semantic view..."
                        : "Ask a question about this model..."
                    }
                    rows={2}
                    className="flex-1 px-4 py-3 bg-surface-300 border border-surface-200 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
                    style={{ maxHeight: "120px" }}
                    disabled={activeChatLoading}
                  />
                  <button
                    onClick={() => sendActiveChatMessage(activeChatInput)}
                    disabled={activeChatLoading || !activeChatInput.trim()}
                    className="p-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 rounded-xl transition-colors flex-shrink-0"
                  >
                    <Send
                      size={18}
                      className={
                        activeChatLoading || !activeChatInput.trim()
                          ? "text-gray-500"
                          : "text-white"
                      }
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 4.7: Validation Gate
           ════════════════════════════════════════════════ */}
        {step === 4.7 && sessionId && (
          <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
            <div className="text-center space-y-3">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium"
                style={{
                  background: "rgba(6,182,212,0.08)",
                  borderColor: "rgba(6,182,212,0.2)",
                  color: "#06B6D4",
                }}
              >
                <ClipboardCheck size={14} />
                Validation Gate
              </div>
              <h2 className="text-2xl font-bold text-white">
                Semantic View Quality Check
              </h2>
              <p className="text-white/35 max-w-lg mx-auto text-sm">
                Checks primary keys, documentation, relationships, grain, and
                business purpose before deployment.
              </p>
            </div>

            {validationLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="text-center space-y-4">
                  <div
                    className="w-14 h-14 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"
                    style={{ boxShadow: "0 0 30px rgba(6,182,212,0.2)" }}
                  />
                  <p className="text-lg text-white/80 font-medium">
                    Validating Semantic View
                  </p>
                  <p className="text-sm text-white/30">
                    Running 10-point quality checklist...
                  </p>
                </div>
              </div>
            )}

            {validation && !validationLoading && (
              <>
                {/* Readiness Score */}
                <div
                  className={`glass rounded-xl p-6 border ${
                    validation.status === "ready"
                      ? "border-green-500/30"
                      : validation.status === "needs_review"
                        ? "border-yellow-500/30"
                        : "border-red-500/30"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Readiness Score
                      </h3>
                      <p className="text-sm text-white/40 mt-1">
                        {validation.summary}
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        data-testid="semantic-readiness-score"
                        className={`text-4xl font-bold ${
                          validation.readiness_score >= 80
                            ? "text-green-400"
                            : validation.readiness_score >= 60
                              ? "text-yellow-400"
                              : "text-red-400"
                        }`}
                      >
                        {validation.readiness_score}
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          validation.status === "ready"
                            ? "bg-green-500/20 text-green-400"
                            : validation.status === "needs_review"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {validation.status === "ready"
                          ? "Ready"
                          : validation.status === "needs_review"
                            ? "Needs Review"
                            : "Not Ready"}
                      </span>
                    </div>
                  </div>

                  {/* Score bar */}
                  <div className="h-2 bg-surface-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        validation.readiness_score >= 80
                          ? "bg-green-500"
                          : validation.readiness_score >= 60
                            ? "bg-yellow-500"
                            : "bg-red-500"
                      }`}
                      style={{ width: `${validation.readiness_score}%` }}
                    />
                  </div>
                </div>

                {/* Dimension breakdown */}
                {validation.dimension_scores && (
                  <div className="glass rounded-xl p-5 space-y-3">
                    <h3 className="text-sm font-semibold text-white">
                      Quality Dimensions
                    </h3>
                    <div className="space-y-2.5">
                      {Object.entries(validation.dimension_scores).map(
                        ([key, score]) => (
                          <div key={key} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-white/50 capitalize">
                                {key.replace(/_/g, " ")}
                              </span>
                              <span className="text-white/70 font-medium">
                                {score}%
                              </span>
                            </div>
                            <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  score >= 80
                                    ? "bg-green-500"
                                    : score >= 50
                                      ? "bg-yellow-500"
                                      : "bg-red-500"
                                }`}
                                style={{ width: `${score}%` }}
                              />
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {/* Issues */}
                {validation.issues.length > 0 && (
                  <div className="glass rounded-xl p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                        <AlertTriangle size={16} className="text-yellow-400" />
                        Issues Found ({validation.issues.length})
                      </h3>
                      {validation.issues.some(
                        (i) => i.severity === "critical",
                      ) && (
                        <button
                          onClick={() => fixAllCritical(validation.issues)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                          style={{
                            background: "rgba(239,68,68,0.15)",
                            border: "1px solid rgba(239,68,68,0.3)",
                            color: "#f87171",
                          }}
                        >
                          <Wand2 size={12} />
                          Fix All Critical Issues
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(() => {
                        const svForFix = semanticView
                          ? ((semanticView.semantic_view as Record<string, any>)
                              ?.semantic_view ??
                            (semanticView.semantic_view as Record<string, any>))
                          : null;
                        const columnsByEntityForFix: Record<string, string[]> =
                          {};
                        for (const e of svForFix?.entities ?? []) {
                          columnsByEntityForFix[e.name] = (e.columns ?? []).map(
                            (c: any) => c.technical_name,
                          );
                        }
                        const QUICK_FIX_FIELD: Record<
                          string,
                          "primary_keys" | "grain" | "business_purpose"
                        > = {
                          primary_key: "primary_keys",
                          grain: "grain",
                          business_purpose: "business_purpose",
                        };
                        return validation.issues.map((issue, i) => {
                          const fixField = QUICK_FIX_FIELD[issue.category];
                          const quickFixKey = `issue-${i}`;
                          const isQuickFixOpen = quickFixOpen.has(quickFixKey);
                          return (
                            <div
                              key={i}
                              className={`p-3 rounded-lg border ${
                                issue.severity === "critical"
                                  ? "bg-red-500/5 border-red-500/20"
                                  : issue.severity === "warning"
                                    ? "bg-yellow-500/5 border-yellow-500/20"
                                    : "bg-blue-500/5 border-blue-500/20"
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <span
                                  className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 mt-0.5 ${
                                    issue.severity === "critical"
                                      ? "bg-red-500/20 text-red-400"
                                      : issue.severity === "warning"
                                        ? "bg-yellow-500/20 text-yellow-400"
                                        : "bg-blue-500/20 text-blue-400"
                                  }`}
                                >
                                  {issue.severity}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-white/70">
                                    {issue.message}
                                  </p>
                                  <span className="text-[11px] text-white/30">
                                    {issue.category}
                                  </span>
                                  {issue.affected_items.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {issue.affected_items
                                        .slice(0, 5)
                                        .map((item) => (
                                          <code
                                            key={item}
                                            className="text-[11px] px-1 py-0.5 bg-surface-300 rounded text-white/40"
                                          >
                                            {item}
                                          </code>
                                        ))}
                                      {issue.affected_items.length > 5 && (
                                        <span className="text-[11px] text-white/20">
                                          +{issue.affected_items.length - 5}{" "}
                                          more
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {fixField && isQuickFixOpen && (
                                    <IssueQuickFixPanel
                                      field={fixField}
                                      entityNames={issue.affected_items}
                                      columnsByEntity={columnsByEntityForFix}
                                      values={quickFixValues}
                                      onChangeValue={(entityName, value) =>
                                        setQuickFixValues((prev) => ({
                                          ...prev,
                                          [`${fixField}::${entityName}`]: value,
                                        }))
                                      }
                                      saving={quickFixSaving}
                                      saved={quickFixSaved}
                                      onSave={(entityName) =>
                                        saveQuickFix(
                                          entityName,
                                          fixField,
                                          quickFixValues[
                                            `${fixField}::${entityName}`
                                          ] ?? "",
                                        )
                                      }
                                    />
                                  )}
                                </div>
                                <div className="flex flex-col gap-1 flex-shrink-0">
                                  {fixField && (
                                    <button
                                      onClick={() =>
                                        setQuickFixOpen((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(quickFixKey))
                                            next.delete(quickFixKey);
                                          else next.add(quickFixKey);
                                          return next;
                                        })
                                      }
                                      className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border border-cyan-500/30 text-cyan-400 hover:opacity-90 transition-all"
                                    >
                                      <PenSquare size={9} />
                                      {isQuickFixOpen
                                        ? "Hide Quick Fix"
                                        : "Quick Fix"}
                                    </button>
                                  )}
                                  {(issue.severity === "critical" ||
                                    issue.severity === "warning") && (
                                    <button
                                      onClick={() => fixIssueWithAI(issue)}
                                      className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all hover:opacity-90"
                                      style={{
                                        background:
                                          issue.severity === "critical"
                                            ? "rgba(239,68,68,0.15)"
                                            : "rgba(234,179,8,0.15)",
                                        border:
                                          issue.severity === "critical"
                                            ? "1px solid rgba(239,68,68,0.3)"
                                            : "1px solid rgba(234,179,8,0.3)",
                                        color:
                                          issue.severity === "critical"
                                            ? "#f87171"
                                            : "#facc15",
                                      }}
                                    >
                                      <Wand2 size={9} />
                                      Fix with AI
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {validation.recommendations.length > 0 && (
                  <div className="glass rounded-xl p-5 space-y-3">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <Info size={16} className="text-cyan-400" />
                      Recommendations
                    </h3>
                    <ul className="space-y-2">
                      {validation.recommendations.map((rec, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-xs text-white/50"
                        >
                          <CheckCircle2
                            size={12}
                            className="text-cyan-400 flex-shrink-0 mt-0.5"
                          />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Naming standards */}
                <div className="pt-2 pb-1">
                  <div className="flex items-center justify-center gap-3">
                    <button
                      data-testid="semantic-naming-standards-btn"
                      onClick={handleApplyNamingStandards}
                      disabled={namingStandardsLoading}
                      className="px-4 py-2 bg-surface-200 hover:bg-surface-300 text-white/70 hover:text-white rounded-xl text-xs transition flex items-center gap-2 border border-white/10 disabled:opacity-50"
                    >
                      <Wand2 size={13} />
                      {namingStandardsLoading
                        ? "Applying naming standards…"
                        : "Apply Naming Standards"}
                    </button>
                  </div>
                  {namingStandardsError && (
                    <p className="text-center text-xs text-red-400 mt-2">
                      {namingStandardsError}
                    </p>
                  )}
                  {namingStandardsReport &&
                    !namingStandardsError &&
                    (namingStandardsReport.renamed_entities.length > 0 ||
                    namingStandardsReport.renamed_columns.length > 0 ? (
                      <div className="mt-3 mx-auto max-w-xl text-xs text-white/60 bg-surface-200/50 rounded-lg p-3 border border-white/5">
                        <p className="text-white/80 mb-1">
                          Naming standards applied (
                          {namingStandardsReport.case_style}):{" "}
                          {namingStandardsReport.renamed_entities.length}{" "}
                          entities,{" "}
                          {namingStandardsReport.renamed_columns.length} columns
                          renamed.
                        </p>
                        <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                          {namingStandardsReport.renamed_entities.map((r) => (
                            <li key={`e-${r.old}`}>
                              Entity:{" "}
                              <span className="text-white/40">{r.old}</span> →{" "}
                              <span className="text-cyan-400">{r.new}</span>
                            </li>
                          ))}
                          {namingStandardsReport.renamed_columns.map((r, i) => (
                            <li key={`c-${r.entity}-${r.old}-${i}`}>
                              {r.entity}.{r.old} →{" "}
                              <span className="text-cyan-400">{r.new}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-center text-xs text-white/40 mt-2">
                        Already compliant — nothing to rename.
                      </p>
                    ))}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-center gap-4 pt-2">
                  <button
                    onClick={() => setStep(4.5)}
                    className="px-5 py-2.5 bg-surface-200 hover:bg-surface-300 text-white/40 hover:text-white rounded-xl text-sm transition flex items-center gap-2"
                  >
                    <ArrowLeft size={14} /> Back to Refine
                  </button>
                  <button
                    data-testid="semantic-validate-btn"
                    onClick={handleValidate}
                    className="px-5 py-2.5 bg-surface-200 hover:bg-surface-300 text-cyan-400 hover:text-cyan-300 rounded-xl text-sm transition flex items-center gap-2 border border-cyan-500/20"
                  >
                    <ClipboardCheck size={14} /> Re-validate
                  </button>
                  <button
                    onClick={() => setStep(5)}
                    className="px-6 py-2.5 text-white rounded-xl font-medium text-sm transition-all flex items-center gap-2"
                    style={{
                      background:
                        validation.status === "ready"
                          ? "linear-gradient(135deg, #86BC25, #6B9B1E)"
                          : validation.status === "needs_review"
                            ? "linear-gradient(135deg, #eab308, #ca8a04)"
                            : "rgba(239,68,68,0.4)",
                      boxShadow:
                        validation.status === "ready"
                          ? "0 0 20px rgba(134,188,37,0.2)"
                          : "none",
                    }}
                  >
                    {validation.status === "not_ready"
                      ? "Proceed Anyway"
                      : "Choose Target Platform"}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 5: Target Platform Selection
           ════════════════════════════════════════════════ */}
        {step === 5 && sessionId && !targetLoading && (
          <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
            <div className="text-center">
              <h2 className="text-xl font-bold text-white">
                Choose Target Platform
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Select where to deploy your semantic model
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {TARGETS.map((t) => {
                const Icon = t.icon;
                const isActive = selectedTarget === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTarget(t.id)}
                    className={`flex flex-col items-start gap-2 p-4 rounded-xl border transition-all text-left ${
                      isActive
                        ? `${t.color} ring-1 ring-current`
                        : "bg-surface-300 border-surface-200 hover:border-surface-100 text-gray-400 hover:text-gray-200"
                    }`}
                  >
                    <Icon size={22} />
                    <span className="text-sm font-semibold">{t.name}</span>
                    <div className="flex flex-wrap gap-1">
                      {t.features.map((f) => (
                        <span
                          key={f}
                          className="text-[11px] px-1.5 py-0.5 bg-surface-200/50 rounded-full text-gray-500"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedTarget && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">
                    Target Schema (optional)
                  </label>
                  <input
                    type="text"
                    value={targetSchema}
                    onChange={(e) => setTargetSchema(e.target.value)}
                    placeholder={
                      selectedTarget === "oracle"
                        ? "SEMANTIC_MODEL"
                        : selectedTarget === "snowflake"
                          ? "SEMANTIC"
                          : "public"
                    }
                    className="w-full px-3 py-2 bg-surface-300 border border-surface-200 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(4.7)}
                    className="px-4 py-3 bg-surface-200 hover:bg-surface-300 rounded-lg text-sm text-white/40 hover:text-white transition flex items-center gap-2"
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                  <button
                    onClick={handleGenerateTarget}
                    disabled={targetLoading}
                    className="flex-1 py-3 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <ChevronRight size={16} /> Generate Model
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Target loading */}
        {targetLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <div className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-white/50">
                Generating {TARGETS.find((t) => t.id === selectedTarget)?.name}{" "}
                Model...
              </p>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           STEP 6: Documentation + Deployment
           ════════════════════════════════════════════════ */}
        {step === 6 && targetModel && sessionId && (
          <div className="space-y-6 animate-fade-in">
            {docError && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle size={18} className="text-red-400" />
                  <p className="text-sm font-semibold text-red-300">Error</p>
                </div>
                <p className="text-sm text-red-200/80">{docError}</p>
              </div>
            )}

            {/* Optimization Hints */}
            {targetModel.optimization_hints && (
              <div className="glass rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                  <Sparkles size={16} className="text-yellow-400" />{" "}
                  Optimization Recommendations
                </h3>
                <div className="prose prose-invert prose-sm max-w-none text-xs">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {targetModel.optimization_hints}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* Deployment artifacts */}
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-3 mb-4 p-3 bg-brand-500/5 border border-brand-500/20 rounded-lg">
                <Database size={20} className="text-brand-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">
                    {targetModel.target.charAt(0).toUpperCase() +
                      targetModel.target.slice(1)}{" "}
                    Model Generated
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {Object.keys(displayArtifacts).length} artifacts &middot;{" "}
                    {targetModel.schema
                      ? `Schema: ${targetModel.schema}`
                      : "Default schema"}
                  </p>
                </div>
                <button
                  data-testid="download-all-artifacts-btn"
                  onClick={() => void handleDownloadAllArtifacts()}
                  disabled={downloadingAllArtifacts}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-brand-500/10 border border-brand-500/30 text-brand-400 hover:bg-brand-500/20 transition-colors disabled:opacity-60"
                >
                  {downloadingAllArtifacts ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  Download All
                </button>
              </div>

              {/* Artifacts accordion */}
              <div className="space-y-2">
                {Object.entries(displayArtifacts).map(([key, content]) => {
                  const isOpen = expanded === key;
                  const label =
                    key === "er_diagram"
                      ? "ER Diagram"
                      : key.replace(/_/g, " ");
                  const isDownloading = downloadingArtifact === key;
                  return (
                    <div
                      key={key}
                      data-testid="semantic-target-artifact"
                      data-artifact={key}
                      className="glass rounded-xl overflow-hidden"
                    >
                      <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-200/30 transition">
                        <button
                          onClick={() => setExpanded(isOpen ? null : key)}
                          className="flex-1 flex items-center gap-2 text-left"
                        >
                          <FileCode2 size={14} className="text-brand-500" />
                          <span className="text-sm font-medium text-white capitalize">
                            {label}
                          </span>
                        </button>
                        {key === "er_diagram" && (
                          <>
                            <button
                              onClick={() =>
                                handleDownloadErDiagramImage("png")
                              }
                              disabled={erDiagramDownloading !== null}
                              title="Download as PNG image"
                              className="px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-brand-400 hover:bg-brand-500/10 transition-colors disabled:opacity-60 flex items-center gap-1"
                            >
                              {erDiagramDownloading === "png" ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Download size={12} />
                              )}
                              PNG
                            </button>
                            <button
                              onClick={() =>
                                handleDownloadErDiagramImage("svg")
                              }
                              disabled={erDiagramDownloading !== null}
                              title="Download as SVG (scalable vector)"
                              className="px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-brand-400 hover:bg-brand-500/10 transition-colors disabled:opacity-60 flex items-center gap-1"
                            >
                              {erDiagramDownloading === "svg" ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Download size={12} />
                              )}
                              SVG
                            </button>
                          </>
                        )}
                        {(key === "deployment_guide" ||
                          key === "business_glossary") && (
                          <>
                            <button
                              onClick={() =>
                                void handleExportArtifactDocument(
                                  key,
                                  content,
                                  "pdf",
                                )
                              }
                              disabled={exportingArtifact !== null}
                              title="Download as a formatted PDF"
                              className="px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-brand-400 hover:bg-brand-500/10 transition-colors disabled:opacity-60 flex items-center gap-1"
                            >
                              {exportingArtifact === `${key}:pdf` ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Download size={12} />
                              )}
                              PDF
                            </button>
                            <button
                              onClick={() =>
                                void handleExportArtifactDocument(
                                  key,
                                  content,
                                  "csv",
                                )
                              }
                              disabled={exportingArtifact !== null}
                              title="Download as Excel-compatible CSV (tables only)"
                              className="px-2 py-1 rounded-md text-[11px] font-medium text-gray-400 hover:text-brand-400 hover:bg-brand-500/10 transition-colors disabled:opacity-60 flex items-center gap-1"
                            >
                              {exportingArtifact === `${key}:csv` ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <FileSpreadsheet size={12} />
                              )}
                              Excel
                            </button>
                          </>
                        )}
                        <button
                          data-testid="download-artifact-btn"
                          data-artifact={key}
                          onClick={() =>
                            void handleArtifactDownload(key, content)
                          }
                          disabled={isDownloading}
                          title={`Download ${artifactFilename(key)}`}
                          className="p-1.5 rounded-md text-gray-400 hover:text-brand-400 hover:bg-brand-500/10 transition-colors disabled:opacity-60"
                        >
                          {isDownloading ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                        </button>
                        <button
                          onClick={() => setExpanded(isOpen ? null : key)}
                          className="pl-2"
                        >
                          {isOpen ? (
                            <ChevronUp size={14} className="text-gray-500" />
                          ) : (
                            <ChevronDown size={14} className="text-gray-500" />
                          )}
                        </button>
                      </div>
                      {isOpen && (
                        <div className="px-4 pb-4">
                          {key === "er_diagram" ? (
                            <MermaidDiagram
                              chart={content}
                              onSvgReady={(svg) => {
                                erDiagramSvgRef.current = svg;
                              }}
                            />
                          ) : MARKDOWN_ARTIFACT_KEYS.has(key) ? (
                            <div className="prose prose-invert prose-sm max-w-none text-xs">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <pre
                              data-testid="semantic-target-artifact-content"
                              data-artifact={key}
                              className="text-[11px] font-mono bg-surface-500 rounded-lg p-4 overflow-x-auto max-h-96 custom-scrollbar text-gray-300"
                            >
                              {content}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* DDL validation + cost estimate */}
            <div className="glass rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                  <ClipboardCheck size={16} className="text-brand-500" />
                  Pre-flight Checks
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={runDdlValidation}
                    disabled={ddlValidating}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                  >
                    {ddlValidating ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <ClipboardCheck size={12} />
                    )}
                    Validate DDL
                  </button>
                  <button
                    onClick={runCostEstimate}
                    disabled={costLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                  >
                    {costLoading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <BarChart3 size={12} />
                    )}
                    Estimate Cost
                  </button>
                </div>
              </div>

              {ddlError && <p className="text-xs text-red-400">{ddlError}</p>}
              {ddlValidation && (
                <div
                  className={`p-3 rounded-lg border text-xs ${
                    ddlValidation.valid
                      ? "bg-green-500/10 border-green-500/30 text-green-300"
                      : "bg-red-500/10 border-red-500/30 text-red-300"
                  }`}
                >
                  <p className="font-medium mb-1">
                    {ddlValidation.valid
                      ? `Valid — ${ddlValidation.statement_count} statements parsed cleanly`
                      : `${ddlValidation.errors.length} error(s) found`}
                  </p>
                  {ddlValidation.errors.map((e, i) => (
                    <p key={i} className="text-white/50">
                      #{e.statement_index}: {e.message}
                    </p>
                  ))}
                </div>
              )}

              {costError && <p className="text-xs text-red-400">{costError}</p>}
              {costEstimate && (
                <div className="p-3 rounded-lg border border-white/10 bg-surface-500/40 text-xs space-y-2">
                  <p className="text-white/40">
                    Indicative estimate only — {costEstimate.rates_source}
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">
                        {costEstimate.totals.estimated_gb.toFixed(2)} GB
                      </p>
                      <p className="text-gray-500">Storage</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">
                        ${costEstimate.totals.monthly_storage_usd.toFixed(2)}
                      </p>
                      <p className="text-gray-500">Storage / mo</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">
                        ${costEstimate.totals.full_scan_query_usd.toFixed(2)}
                      </p>
                      <p className="text-gray-500">Full scan</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Lineage capture + catalog export */}
            <div className="glass rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                  <GitBranch size={16} className="text-brand-500" />
                  Lineage & Catalog
                </h3>
                <button
                  onClick={runCaptureLineage}
                  disabled={lineageLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                >
                  {lineageLoading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <GitBranch size={12} />
                  )}
                  Capture Lineage
                </button>
              </div>
              {lineageError && (
                <p className="text-xs text-red-400">{lineageError}</p>
              )}
              {lineageResult && (
                <p className="text-xs text-green-300">
                  Captured {lineageResult.edges} lineage edge(s) across{" "}
                  {lineageResult.nodes} node(s).
                </p>
              )}

              <div className="border-t border-white/5 pt-3 space-y-2">
                <p className="text-xs text-gray-500">
                  Publish this model's glossary + entity/column metadata (never
                  row-level data) to an external data catalog.
                </p>
                <input
                  value={catalogEndpoint}
                  onChange={(e) => setCatalogEndpoint(e.target.value)}
                  placeholder="Catalog endpoint URL"
                  className="w-full px-3 py-2 bg-surface-500 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50"
                />
                <input
                  value={catalogApiKey}
                  onChange={(e) => setCatalogApiKey(e.target.value)}
                  type="password"
                  placeholder="API key (optional)"
                  className="w-full px-3 py-2 bg-surface-500 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50"
                />
                {catalogError && (
                  <p className="text-xs text-red-400">{catalogError}</p>
                )}
                {catalogResult && (
                  <p
                    className={`text-xs ${catalogResult.status === "sent" ? "text-green-300" : "text-red-300"}`}
                  >
                    {catalogResult.status === "sent"
                      ? "Exported successfully."
                      : catalogResult.detail || "Export failed."}
                  </p>
                )}
                <button
                  onClick={runCatalogExport}
                  disabled={catalogExporting || !catalogEndpoint.trim()}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                >
                  {catalogExporting ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <ArrowUpRight size={12} />
                  )}
                  Export to Catalog
                </button>
              </div>
            </div>

            {/* Guardian masking suggestions */}
            <div className="glass rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                  <Shield size={16} className="text-brand-500" />
                  Masking Suggestions
                </h3>
                <button
                  onClick={fetchMaskingPlan}
                  disabled={maskingLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                >
                  {maskingLoading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Shield size={12} />
                  )}
                  Suggest Masking Plan
                </button>
              </div>
              {maskingError && (
                <p className="text-xs text-red-400">{maskingError}</p>
              )}
              {maskingPlan && maskingPlan.length === 0 && (
                <p className="text-xs text-gray-500">
                  No PII/Confidential columns detected — nothing to mask.
                </p>
              )}
              {maskingPlan && maskingPlan.length > 0 && (
                <div className="space-y-2">
                  {Object.entries(
                    maskingPlan.reduce<Record<string, typeof maskingPlan>>(
                      (acc, item) => {
                        (acc[item.entity] ??= []).push(item);
                        return acc;
                      },
                      {},
                    ),
                  ).map(([entityName, items]) => (
                    <div
                      key={entityName}
                      className="rounded-lg bg-surface-500/40 p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white">
                          {entityName}
                        </span>
                        <button
                          onClick={() => applyMaskingForEntity(entityName)}
                          disabled={
                            maskingApplying === entityName ||
                            maskingApplied[entityName]
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white transition"
                        >
                          {maskingApplying === entityName ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : maskingApplied[entityName] ? (
                            <Check size={10} />
                          ) : null}
                          {maskingApplied[entityName]
                            ? "Applied"
                            : "Apply to Guardian"}
                        </button>
                      </div>
                      <ul className="space-y-1">
                        {items.map((item) => (
                          <li
                            key={item.column_name}
                            className="flex items-center gap-2 text-[11px] text-white/50"
                          >
                            <code className="bg-surface-200 px-1.5 py-0.5 rounded text-brand-300">
                              {item.column_name}
                            </code>
                            <span className="text-white/30">
                              {item.sensitivity}
                            </span>
                            <ArrowRight size={9} className="text-white/20" />
                            <span className="text-white/60">
                              {item.suggested_technique_id}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* HITL approval gate */}
            <div className="glass rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                  <Shield size={16} className="text-brand-500" />
                  Deployment Approval
                </h3>
                {approval && (
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                      approval.status === "approved"
                        ? "bg-green-500/20 text-green-400"
                        : approval.status === "rejected"
                          ? "bg-red-500/20 text-red-400"
                          : "bg-yellow-500/20 text-yellow-400"
                    }`}
                  >
                    {approval.status}
                  </span>
                )}
              </div>
              {approvalError && (
                <p className="text-xs text-red-400">{approvalError}</p>
              )}
              {!approval && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">
                    Require a human sign-off before this model can be deployed.
                  </p>
                  {!selectedTarget && (
                    <p className="text-xs text-amber-500">
                      Select a target platform above before requesting approval
                      — the sign-off is scoped to a specific target.
                    </p>
                  )}
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Notes for the reviewer (optional)"
                    rows={2}
                    className="w-full px-3 py-2 bg-surface-500 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 resize-none"
                  />
                  <button
                    onClick={submitApprovalRequest}
                    disabled={approvalLoading || !selectedTarget}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition disabled:opacity-60"
                  >
                    {approvalLoading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Shield size={12} />
                    )}
                    Request Approval
                  </button>
                </div>
              )}
              {approval && approval.status === "pending" && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">
                    Requested by {approval.requested_by} at{" "}
                    {new Date(approval.requested_at).toLocaleString()}.
                  </p>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Decision notes (optional)"
                    rows={2}
                    className="w-full px-3 py-2 bg-surface-500 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => decideApprovalRequest("approved")}
                      disabled={approvalLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-700 text-white transition disabled:opacity-60"
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => decideApprovalRequest("rejected")}
                      disabled={approvalLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition disabled:opacity-60"
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                </div>
              )}
              {approval && approval.status !== "pending" && (
                <p className="text-xs text-gray-500">
                  Decided by {approval.decided_by} at{" "}
                  {approval.decided_at
                    ? new Date(approval.decided_at).toLocaleString()
                    : ""}
                  {approval.status === "rejected" && (
                    <button
                      onClick={() => setApproval(null)}
                      className="ml-2 text-brand-400 hover:text-brand-300"
                    >
                      Request again
                    </button>
                  )}
                </p>
              )}
            </div>

            {/* Deploy to target */}
            <div className="glass rounded-xl p-4">
              <button
                onClick={() => setShowDeploy(!showDeploy)}
                className="w-full flex items-center justify-between"
              >
                <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                  <Rocket size={16} className="text-brand-500" /> Deploy to{" "}
                  {targetModel.target.charAt(0).toUpperCase() +
                    targetModel.target.slice(1)}
                </h3>
                {showDeploy ? (
                  <ChevronUp size={14} className="text-gray-500" />
                ) : (
                  <ChevronDown size={14} className="text-gray-500" />
                )}
              </button>
              {showDeploy && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs text-gray-500">
                    Reuse a database connection any agent already saved for this
                    project, or connect a new one, to deploy the generated DDL —
                    or verify a previous deployment matches this semantic model.
                  </p>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">
                      Schema (optional)
                    </label>
                    <input
                      type="text"
                      value={deploySchemaName}
                      onChange={(e) => setDeploySchemaName(e.target.value)}
                      placeholder={targetModel.schema || "public"}
                      className="w-full px-3 py-2 bg-surface-500 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50"
                    />
                  </div>

                  {deployError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                      {deployError}
                    </div>
                  )}

                  {deployResult && (
                    <div
                      className={`p-4 rounded-lg border ${deployResult.status === "completed" ? "bg-green-500/10 border-green-500/30" : "bg-yellow-500/10 border-yellow-500/30"}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {deployResult.status === "completed" ? (
                          <CheckCircle2 size={18} className="text-green-400" />
                        ) : (
                          <X size={18} className="text-yellow-400" />
                        )}
                        <p className="text-sm font-medium text-white">
                          Deployment {deployResult.status}
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                          <p className="text-lg font-bold text-white">
                            {deployResult.tables?.total_statements || 0}
                          </p>
                          <p className="text-gray-500">Statements</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-400">
                            {deployResult.tables?.successful || 0}
                          </p>
                          <p className="text-gray-500">Successful</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-red-400">
                            {deployResult.tables?.errors || 0}
                          </p>
                          <p className="text-gray-500">Errors</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {reconcileError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                      {reconcileError}
                    </div>
                  )}

                  {reconcileResult && (
                    <div
                      className={`p-4 rounded-lg border space-y-3 ${reconcileResult.summary.in_sync ? "bg-green-500/10 border-green-500/30" : "bg-yellow-500/10 border-yellow-500/30"}`}
                    >
                      <div className="flex items-center gap-2">
                        {reconcileResult.summary.in_sync ? (
                          <CheckCircle2 size={18} className="text-green-400" />
                        ) : (
                          <X size={18} className="text-yellow-400" />
                        )}
                        <p className="text-sm font-medium text-white">
                          {reconcileResult.summary.in_sync
                            ? "Source and target are in sync"
                            : "Differences found between source and target"}
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-400">
                            {reconcileResult.summary.matched}
                          </p>
                          <p className="text-gray-500">Matched</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-yellow-400">
                            {reconcileResult.summary.mismatched}
                          </p>
                          <p className="text-gray-500">Mismatched</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-red-400">
                            {reconcileResult.summary.missing}
                          </p>
                          <p className="text-gray-500">Missing</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {reconcileResult.tables.map((t) => (
                          <div
                            key={t.table}
                            className="rounded-lg bg-surface-500/60 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-white">
                                {t.table}
                              </span>
                              <span
                                className={`text-[11px] px-2 py-0.5 rounded-full ${
                                  t.status === "matched"
                                    ? "bg-green-500/20 text-green-300"
                                    : t.status === "missing"
                                      ? "bg-red-500/20 text-red-300"
                                      : "bg-yellow-500/20 text-yellow-300"
                                }`}
                              >
                                {t.status}
                              </span>
                            </div>
                            {t.columns.filter((c) => c.status !== "matched")
                              .length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {t.columns
                                  .filter((c) => c.status !== "matched")
                                  .map((c) => (
                                    <li
                                      key={c.column}
                                      className="text-[11px] text-gray-400 flex items-center gap-2"
                                    >
                                      <span className="text-gray-300 font-mono">
                                        {c.column}
                                      </span>
                                      <span
                                        className={
                                          c.status === "missing"
                                            ? "text-red-400"
                                            : c.status === "extra"
                                              ? "text-blue-400"
                                              : "text-yellow-400"
                                        }
                                      >
                                        {c.status === "missing" &&
                                          "missing on target"}
                                        {c.status === "extra" &&
                                          "extra on target"}
                                        {c.status === "type_mismatch" &&
                                          `type mismatch: expected ${c.expected_type}, got ${c.actual_type}`}
                                      </span>
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className="flex-1 space-y-1.5">
                      <button
                        onClick={() => {
                          setDeployDialogAction("deploy");
                          setDeployError(null);
                          setShowDeployConnectDialog(true);
                        }}
                        disabled={
                          deploying ||
                          (approval !== null && approval.status !== "approved")
                        }
                        className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {deploying ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />{" "}
                            Deploying...
                          </>
                        ) : (
                          <>
                            <Rocket size={18} /> Connect &amp; Deploy DDL
                          </>
                        )}
                      </button>
                      {approval && approval.status !== "approved" && (
                        <p className="text-[11px] text-yellow-400/80 text-center">
                          Deployment blocked — approval status is "
                          {approval.status}".
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setDeployDialogAction("reconcile");
                        setReconcileError(null);
                        setShowDeployConnectDialog(true);
                      }}
                      disabled={reconciling}
                      className="flex-1 py-3 bg-surface-300 hover:bg-surface-200 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {reconciling ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />{" "}
                          Checking...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={18} /> Verify Deployment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <ConnectDatabaseDialog
              open={showDeployConnectDialog}
              onClose={() => setShowDeployConnectDialog(false)}
              title={
                deployDialogAction === "reconcile"
                  ? "Connect Target Database — Verify Deployment"
                  : "Connect Target Database — Deploy DDL"
              }
              projectId={projectId}
              kind="target"
              agentKey="semantic_intelligence"
              defaultDbType={targetModel.target}
              defaultSchemaName={
                deploySchemaName || targetModel.schema || undefined
              }
              testIdPrefix="semantic-deploy-target"
              onConnectExisting={handleDeployDialogConnectExisting}
              onConnectNew={handleDeployDialogConnectNew}
            />

            {/* Start over */}
            <div className="text-center pb-6">
              <button
                onClick={handleReset}
                className="px-6 py-2.5 bg-surface-200 hover:bg-surface-300 text-white/50 hover:text-white rounded-xl text-sm transition flex items-center gap-2 mx-auto"
              >
                <ArrowLeft size={14} /> Start New Session
              </button>
            </div>
          </div>
        )}
      </div>

      <ConnectDatabaseDialog
        open={showSharedConnectDialog}
        onClose={() => setShowSharedConnectDialog(false)}
        title="Connect Database"
        projectId={projectId}
        agentKey="semantic_intelligence"
        extraField={{
          key: "table_or_query",
          label: "Table or Query (optional)",
          value: dbForm.table_or_query || "",
          onChange: (v) => setDbForm((p) => ({ ...p, table_or_query: v })),
          placeholder: "Leave blank to browse tables",
          testId: "semantic-db-table",
        }}
        testIdPrefix="semantic-db"
        onConnectExisting={handleSharedDialogConnectExisting}
        onConnectNew={handleSharedDialogConnectNew}
      />
    </div>
  );
}

/* ── Shared field component ── */
function DbField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  testId?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">
        {label}
      </label>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-surface-300 border border-surface-200 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
      />
    </div>
  );
}
